import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  Audience,
  AudienceWithCompletionCriteria,
  BatchMode,
  ContentTemplate,
  ContentType,
  CREATE_MEETING_SERVICES,
  DEFAULT_SHEET_MAPPINGS_V2,
  DestinatioType,
  ExperienceTemplateMessage,
  ExternalServiceType,
  getSubTaskTemplates,
  IContentTextTemplate,
  IExperienceTemplate,
  IExperienceTemplateResource,
  InternalStorageInstructionType,
  isContentOrLAyoutTemplateBlock,
  isContentTemplateBlock,
  isEventDrivenTiming,
  isLayoutBlock,
  isSubTaskTemplateBlock,
  ISubTaskQuestionTemplate,
  ITaskTemplate,
  ITimelineItem,
  LayoutBlock,
  LayoutBlockType,
  MessageBlock,
  PARTICIPANT_ACTOR_KEY,
  QuestionType,
  RequirementType,
  SubTaskDataDestination,
  SubTaskDataDestinationGoogleSheet,
  SubTaskDataDestinationSupportingActor,
  SubTaskQuestionTemplateQuestion,
  SubTaskStartHookType,
  SubTaskStatus,
  SubTaskTemplate,
  SubTaskType,
  TaskBlockTemplate,
  TaskStatus,
  TemplateResourceType,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { emptyScaleQuestion } from '@dadohr/shared/domainTypes/questionScale';
import { Integration } from '@dadohr/shared/domainTypes/integrations';
import {
  CustomUserField,
  IOrganization,
  IOrganizationDenormalized
} from '@dadohr/shared/domainTypes/org';
import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  capitalize,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  Typography
} from '@material-ui/core';
import arrayMove from 'array-move';
import { assertNever } from 'assert-never';
import { cloneDeep, compact, mapValues, sortBy } from 'lodash';
import {
  AlignLeft,
  Calendar,
  CalendarPlus,
  Check,
  CheckCircle2,
  Clock,
  Columns,
  Copy,
  File,
  FilePen,
  GraduationCap,
  Gift,
  Home,
  LucideIcon as Icon,
  Image,
  Link,
  ListChecks,
  PauseCircle,
  Pencil,
  Play,
  PlayCircle,
  SearchCheck,
  SeparatorHorizontal,
  SlidersHorizontal,
  Square,
  Star,
  Trash2,
  Type,
  Upload,
  UserCheck,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import { useNewUx } from '../../../../../services/newUx';
import * as UX from '../newux/kit';
import { ACTOR_COLORS_LIGHT, useActorDescriptorsFromTemplate } from '../../Actors';
import { MarkdownEditorWithAI } from '../components/AIContentGenerator/MarkdownEditorWithAI';
import React, {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import shortid from 'shortid';
import { AIMessageDraft } from '../../../../../components/AIMessageDraft';
import { CustomInputLabel } from '../../../../../components/CustomInputLabel';
import { DebugText } from '../../../../../components/DebugText';
import { DeleteButton } from '../../../../../components/DeletionConfirmation';
import { DeactivateItemConfirmDialog } from '../../DeactivateItemButton';
import { FormRow } from '../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../components/InfoBox';
import {
  PlainTextWithReplacementPlaceholders,
  TokenDefinition
} from '../../../../../components/MarkdownText';
import { TextFieldWithReplacementTokens } from '../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import {
  useCurrentUser,
  useIsCurrentUserDadoEmployee
} from '../../../../../services/currentUser';
import {
  clearEveryoneNameIfSole,
  filterBlocksForAudience,
  getAudienceById,
  getAudienceName,
  getEveryoneAudience
} from '../../../../../services/experiences/audience';
import { cloneBgCheckResource } from '../../../../../services/experiences/bgCheckResourceDuplication';
import { toDenormalizedTask } from '../../../../../services/experiences/instance';
import {
  getAnchorInterpolationVariable,
  hasInstanceSpecificInterpolations,
  templateTimingsShareTriggerCondition
} from '../../../../../services/experiences/template';
import { useFeatureEnabled } from '../../../../../services/features';
import { LightMdToJsx } from '../../../../../services/formatter';
import { useIntegrationsContext } from '../../../../../services/integrations/context';
import { channelToKey, channelToString } from '../../../../../services/channels';
import { toDenormalizedOrg } from '../../../../../services/orgs';
import { useIsOrgAIEnabled } from '../../../../../services/orgSettings';
import { InterpolationDefaultOptions } from '../../../../../services/set/interpolation';
import { now, ONE_DAY, ONE_MINUTE } from '../../../../../services/time';
import {
  EXAMPLE_USER,
  useUserRuleConfigWithAutocompletion
} from '../../../../../services/user';
import { AnchorField, useAnchorFields } from '../../Anchors';
import { ChannelIcon } from '../../Channels';
import {
  EMPTY_RULE_GROUP,
  countConditions,
  formatRuleAsPlainText,
  RuleRenderer
} from '../../RuleBuilder';
import type { RuleBuilderConfig } from '../../RuleBuilder';
import { useGoogleCalendarEventInvitiationAnchorOptions } from '../AutomationBuilder/ActionSettings/GoogleCalendar';
import { actionActorToString } from '../AutomationBuilder/shared';
import { AIMultiBlockGeneratorDialog } from '../components/AIMultiBlockGenerator';
import { AIGeneratorLoadingIndicator } from '../components/AIMultiBlockGenerator/LoadingIndicator';
// import { MultiBlockGeneratorButton } from '../components/AIMultiBlockGenerator/MultiBlockGeneratorButton';
import { DragDropItemFlag } from '../components/DragDropList';
// import { useBlockBuilderAI } from './useBlockBuilderAI';
import {
  TaskSettingsAssignment,
  TaskSettingsOverviewSectionAssignment
} from './components/Assignment';
import {
  DEFAULT_AUDIENCE_COLOR,
  makeNewAudienceDraft,
  MessageSettingsAudiencesDetails,
  MessageSettingsAudiencesOverview,
  nextSegmentColorHex,
  TaskSettingsAudiencesDetails,
  TaskSettingsAudiencesOverview,
  TaskSettingsOverviewSectionAudiences
} from './components/Audiences';
import { getMininumRequiredCount } from './components/CompletionCriteria';
import {
  BlockSettingsContentCallout,
  BlockSettingsContentCalloutReadOnly
} from './components/BlockSettings/Content/Callout';
import { useBlockBuilderAI } from './useBlockBuilderAI';

import {
  BlockSettingsContentDivider,
  BlockSettingsContentDividerReadOnly
} from './components/BlockSettings/Content/Divider';
import {
  BlockSettingsContentHeading,
  BlockSettingsContentHeadingReadOnly
} from './components/BlockSettings/Content/Heading';
import {
  BlockSettingsContentImage,
  BlockSettingsContentImageReadOnly
} from './components/BlockSettings/Content/Image';
import {
  BlockSettingsContentText,
  BlockSettingsContentTextReadOnly
} from './components/BlockSettings/Content/Text';
import {
  BlockSettingsLayoutColumns,
  BlockSettingsLayoutColumnsReadOnly
} from './components/BlockSettings/Layout/Columns';
import {
  BlockSettingsBackgroundCheckInvite,
  BlockSettingsBackgroundCheckInviteReadOnly
} from './components/BlockSettings/SubTask/BackgroundCheckInvite';
import {
  BlockSettingsSubTaskCalendlyInvite,
  BlockSettingsSubTaskCalendlyInviteReadOnly
} from './components/BlockSettings/SubTask/CalendlyInvite';
import {
  BlockSettingsSubTaskConfirm,
  BlockSettingsSubTaskConfirmReadOnly
} from './components/BlockSettings/SubTask/Confirm';
import {
  BlockSettingsSubtaskCreateCalendarEvent,
  BlockSettingsSubtaskCreateCalendarEventReadOnly
} from './components/BlockSettings/SubTask/CreateCalendarEvent';
import {
  BlockSettingsSubtaskDataFieldChange,
  BlockSettingsSubtaskDataFieldChangeReadOnly
} from './components/BlockSettings/SubTask/DataFieldChange';
import {
  BlockSettingsCompleteLmsModule,
  BlockSettingsCompleteLmsModuleReadOnly
} from './components/BlockSettings/SubTask/CompleteLmsModule';
import {
  BlockSettingsDocumentRead,
  BlockSettingsDocumentReadReadOnly
} from './components/BlockSettings/SubTask/DocumentRead';
import {
  BlockSettingsDocuSignEnvelopeSign,
  BlockSettingsDocuSignEnvelopeSignReadOnly
} from './components/BlockSettings/SubTask/DocuSignEnvelopeSign';
import {
  BlockSettingsSubTaskMeet,
  BlockSettingsSubTaskMeetReadOnly
} from './components/BlockSettings/SubTask/Meet';
import {
  BlockSettingsSubTaskQuestion,
  BlockSettingsSubTaskQuestionReadOnly
} from './components/BlockSettings/SubTask/Question';
import {
  BlockSettingsSubTaskSubmit,
  BlockSettingsSubTaskSubmitReadOnly
} from './components/BlockSettings/SubTask/Submit';
import { ReactComponent as SubtaskChoice } from './components/BlockSettings/SubTask/subtask-choice.svg';
import {
  BlockSettingsSwagUpChoice,
  BlockSettingsSwagUpChoiceReadOnly
} from './components/BlockSettings/SubTask/SwagUpChoice';
import {
  BlockSettingsSubTaskVisit,
  BlockSettingsSubTaskVisitReadOnly
} from './components/BlockSettings/SubTask/Visit';
import {
  BlockSettingsSubTaskWatch,
  BlockSettingsSubTaskWatchReadOnly
} from './components/BlockSettings/SubTask/Watch';
import { CauseItemDeletionConfirmationBody } from './components/CauseItemDeletionConfirmationBody';
import { DROP_IDS } from './components/dndIds';
import {
  Grid,
  MainContainer,
  PreviewBody,
  PreviewContainer,
  PreviewHeading,
  SettingsBody,
  SettingsFooter,
  SettingsSection,
  SideContainer
} from './components/layout';
import {
  ExperienceMessagePreview,
  TaskMessagePreviewProps,
  TaskMessagePreviewStart
} from './components/Messages';
import {
  TaskSettingsOverviewSectionRemindersV2,
  TaskSettingsReminders,
  TaskViewerReminders
} from './components/Reminders';
import {
  MessageSettingsOverviewSectionSendDate,
  MessageSettingsSendDateV2,
  MessageViewerSendDate
} from './components/SendDate';
import { SideContainerHeader } from './components/SideContainerHeader';
import {
  TaskSettingsOverviewSectionStartAndDuration,
  TaskSettingsStartAndDurationV2,
  TaskViewerStartAndDeadline
} from './components/StartAndDuration';
import {
  MessageSettingsOverviewSectionTaskList,
  MessageSettingsTaskList,
  MessageSettingsTaskListNewUx
} from './components/TaskList';
import {
  TaskSettingsOverviewSectionTriggeredItems,
  TaskSettingsTriggeredItems
} from './components/TriggeredItems';
import {
  BlockSettingsSubPage,
  getTimelineStartTimeFromTaskBuilderContext,
  MESSAGE_BUILDER_VIEW_MODES,
  MessageBuilderViewMode,
  TASK_BUILDER_VIEW_MODES,
  TaskBuilderContext,
  TaskBuilderViewMode,
  useTaskBuilderContext,
  ViewModeAudiencesDetail,
  ViewModeTaskAudiencesDetail
} from './types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  validateBlock
} from './validation';

const TaskSettingsOverview = ({
  value,
  onChange,
  expTpl,
  onDelete,
  setViewMode,
  customFields,
  tokens,
  triggeredItems: _triggeredItems,
  onGoToOtherItem
}: {
  value: ITaskTemplate;
  onChange?: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  onDelete?: (t: ITaskTemplate) => void;
  expTpl: TaskBuilderContext;
  setViewMode?: (
    nextViewMode: Exclude<TaskBuilderViewMode, ViewModeTaskAudiencesDetail>
  ) => void;
  customFields: CustomUserField[];
  tokens: TokenDefinition[];
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (item: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const anchorFields = useAnchorFields(customFields);

  const anchorUserField = getAnchorInterpolationVariable(
    expTpl.anchor,
    anchorFields
  );

  const hasInvalidInstanceInterpolations =
    value.batchMode === BatchMode.PER_COHORT &&
    hasInstanceSpecificInterpolations(value.title, anchorUserField);

  const triggeredItems = useMemo(() => {
    return _triggeredItems;
  }, [_triggeredItems]);

  return (
    <SideContainer>
      <SettingsBody>
        <SettingsSection>
          <FormRow>
            <CustomInputLabel
              label={t('Title')}
              subLabel={capitalize(t('visible to employees'))}
              fullWidth
            >
              {onChange ? (
                <TextFieldWithReplacementTokens
                  value={value.title}
                  fullWidth
                  variant="outlined"
                  onChange={(nextTitle) =>
                    onChange({
                      ...value,
                      title: nextTitle
                    })
                  }
                  error={!value.title || hasInvalidInstanceInterpolations}
                  helperText={
                    !value.title
                      ? t('Please enter a title')
                      : hasInvalidInstanceInterpolations
                      ? t(
                          'You cannot use this variable for tasks sent once per cohort'
                        )
                      : undefined
                  }
                  tokens={
                    value.batchMode === BatchMode.PER_COHORT
                      ? tokens.filter(
                          (t) =>
                            t.group !== 'Participant' ||
                            (anchorUserField &&
                              t.token === `participant.${anchorUserField}`)
                        )
                      : tokens
                  }
                />
              ) : (
                <PlainTextWithReplacementPlaceholders
                  text={value.title}
                  tokens={tokens}
                />
              )}
            </CustomInputLabel>
          </FormRow>
          {(onChange || value.adminName) && (
            <FormRow>
              <CustomInputLabel
                label={t('Admin Name')}
                subLabel={t('Only visible to administrators')}
                fullWidth
              >
                {onChange ? (
                  <TextFieldWithReplacementTokens
                    value={value.adminName || ''}
                    fullWidth
                    variant="outlined"
                    onChange={(nextAdminName) =>
                      onChange({
                        ...value,
                        adminName: nextAdminName
                      })
                    }
                    tokens={tokens}
                  />
                ) : (
                  <PlainTextWithReplacementPlaceholders
                    text={value.adminName || ''}
                    tokens={tokens}
                  />
                )}
              </CustomInputLabel>
            </FormRow>
          )}
        </SettingsSection>
        <TaskSettingsOverviewSectionAssignment
          title={t('Recipient and Sender')}
          assigneeNoun={t('Assignee')}
          actor={value.actor}
          supportingActors={expTpl.supportingActors}
          customSender={value.customSender}
          receivesCopy={value.receivesCopy}
          channels={value.channels}
          batchMode={value.batchMode}
          onEdit={
            setViewMode
              ? () => setViewMode(TASK_BUILDER_VIEW_MODES.assignment)
              : undefined
          }
          divider
        />
        <TaskSettingsOverviewSectionStartAndDuration
          title={t('Start and Deadline')}
          value={value}
          initialUserTimezoneStartTime={getTimelineStartTimeFromTaskBuilderContext(
            expTpl
          )}
          onEdit={
            setViewMode
              ? () => setViewMode(TASK_BUILDER_VIEW_MODES.startandDuration)
              : undefined
          }
          allTasks={expTpl.tasks}
          divider
        />
        <TaskSettingsOverviewSectionAudiences
          title={t('Target Audiences')}
          audiences={value.audiences}
          onEdit={
            setViewMode
              ? () =>
                  setViewMode(TASK_BUILDER_VIEW_MODES.audiencesOverview(null))
              : undefined
          }
          divider
        />
        <TaskSettingsOverviewSectionRemindersV2
          title={t('Reminders')}
          reminders={value.remindersV2}
          onEdit={
            setViewMode
              ? () => setViewMode(TASK_BUILDER_VIEW_MODES.reminders)
              : undefined
          }
          divider={!!triggeredItems.length}
        />
        {!!triggeredItems.length && (
          <TaskSettingsOverviewSectionTriggeredItems
            title={t('Triggered Timeline Items')}
            triggeredItems={triggeredItems}
            onEdit={
              setViewMode
                ? () => setViewMode(TASK_BUILDER_VIEW_MODES.triggeredItems)
                : undefined
            }
            tplContext={expTpl}
            onGoToOtherItem={onGoToOtherItem}
          />
        )}
      </SettingsBody>
      {onDelete && (
        <SettingsFooter>
          <DeleteButton
            variant="outlined"
            color="secondary"
            startIcon={<Trash2 size={16} />}
            onDelete={async () => onDelete(value)}
            dialogTitle={t('Delete Task?')}
            buttonAction={t('Delete Task')}
            dialogBody={
              triggeredItems.length
                ? () => (
                    <CauseItemDeletionConfirmationBody
                      value={value}
                      onDelete={async () => onDelete(value)}
                      triggeredItems={triggeredItems}
                      tplContext={expTpl}
                    />
                  )
                : undefined
            }
          >
            {t('Delete Task')}
          </DeleteButton>
        </SettingsFooter>
      )}
    </SideContainer>
  );
};

const TaskSettings = ({
  value,
  onChange,
  expTpl,
  onDelete,
  viewMode,
  setViewMode,
  customUserFields,
  tokens,
  triggeredItems,
  onGoToOtherItem
}: {
  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  onDelete?: (t: ITaskTemplate) => void;
  expTpl: TaskBuilderContext;
  viewMode: TaskBuilderViewMode;
  setViewMode: (nextViewMode: TaskBuilderViewMode) => void;
  customUserFields: CustomUserField[];
  tokens: TokenDefinition[];
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const onBack = useCallback(
    () => setViewMode(TASK_BUILDER_VIEW_MODES.settingsOverview),
    [setViewMode]
  );
  switch (viewMode.mode) {
    case 'SETTINGS_OVERVIEW':
      return (
        <TaskSettingsOverview
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onDelete={onDelete}
          setViewMode={setViewMode}
          customFields={customUserFields}
          tokens={tokens}
          triggeredItems={triggeredItems}
          onGoToOtherItem={onGoToOtherItem}
        />
      );
    case 'ASSIGNMENT':
      return (
        <TaskSettingsAssignment
          title={t('Recipient and Sender')}
          description={
            'Select who should receive this task and how it should be delivered.'
          }
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          type="TASK"
          tokens={tokens}
        />
      );
    case 'AUDIENCES_OVERVIEW':
      return (
        <TaskSettingsAudiencesOverview
          title={t('Target Audiences')}
          description={t(
            'Show or hide subtasks and content to certain participants based on their profile.'
          )}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          previewAudienceId={viewMode.previewAudienceId}
          onPreview={(audienceId) =>
            setViewMode(TASK_BUILDER_VIEW_MODES.audiencesOverview(audienceId))
          }
          goToDetails={(audience) =>
            setViewMode(
              TASK_BUILDER_VIEW_MODES.audiencesDetail(audience, false)
            )
          }
        />
      );
    case 'AUDIENCES_DETAIL':
      return (
        <TaskSettingsAudiencesDetails
          title={t('Edit Target Audience')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          model={viewMode.editModel}
          onUpdateModel={(nextModel) =>
            setViewMode(
              TASK_BUILDER_VIEW_MODES.audiencesDetail(
                nextModel,
                viewMode.showHiddenBlocks
              )
            )
          }
          showHiddenBlocks={viewMode.showHiddenBlocks}
          onToggleHiddenBlocks={() =>
            setViewMode(
              TASK_BUILDER_VIEW_MODES.audiencesDetail(
                viewMode.editModel,
                !viewMode.showHiddenBlocks
              )
            )
          }
          onBack={() => {
            return setViewMode(
              TASK_BUILDER_VIEW_MODES.audiencesOverview(viewMode.editModel.id)
            );
          }}
          customUserFields={customUserFields}
        />
      );
    case 'START_AND_DURATION':
      return(
        <TaskSettingsStartAndDurationV2
          title={t('Start and Deadline')}
          description={
            'Specify when the task will be sent and how much time the assignee has to complete it.'
          }
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          triggeredItems={triggeredItems}
          onGoToOtherItem={onGoToOtherItem}
        />
      );
    case 'REMINDERS':
      return (
        <TaskSettingsReminders
          title={t('Reminders')}
          description={
            'Set up completion reminders to be sent before and after the task deadline.'
          }
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
        />
      );
    case 'TRIGGERED_ITEMS':
      return (
        <TaskSettingsTriggeredItems
          title={t('Triggered Timeline Items')}
          description={
            'Completion of this task triggers the following timeline items. To disconnect them, edit the start settings for each item.'
          }
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          triggeredItems={triggeredItems}
          onGoToOtherItem={onGoToOtherItem}
          onBack={onBack}
        />
      );

    default:
      return null;
  }
};

const TaskPreview = ({
  task,
  expTpl,
  org,
  participant,
  actor,
  selectedBlock,
  onSelectBlock,
  onDeleteBlocks,
  onDuplicateBlock,
  onPickBlocks,
  validation,
  draggable,
  viewMode,
  dragDropContext,
  anchorFields,
  tokens,
  sendTestTaskButton,
  aiGeneratorState,
  newUx,
  readOnly,
  renderInlineEditor,
  assignAudiences,
  onToggleBlockAudience
}: {
  task: ITaskTemplate;
  expTpl: TaskBuilderContext;
  org: IOrganizationDenormalized;
  participant: Doc<IUser>;
  actor: string;
  newUx?: boolean;
  readOnly?: boolean;
  renderInlineEditor?: (blockId: string) => React.ReactNode;
  assignAudiences?: Audience[];
  onToggleBlockAudience?: (blockId: string, audienceId: string) => void;
  selectedBlock: string | null;
  onSelectBlock?: (nextSelected: string | null) => void;
  onDeleteBlocks?: (blockIds: string[]) => void;
  onDuplicateBlock?: (blockId: string) => void;
  onPickBlocks?: (blocks: string[]) => void;
  draggable: boolean;
  validation: TaskBuilderBlockValidationState;
  viewMode: TaskBuilderViewMode;
  dragDropContext?: React.MutableRefObject<{
    currentlyVisibleBlocks: string[];
  }>;
  tokens: TokenDefinition[];
  anchorFields: AnchorField[];
  sendTestTaskButton: React.ReactNode;
  aiGeneratorState?: 'idle' | 'generating' | 'inserting' | 'error';
}) => {
  const { t } = useUnsafeTranslation();
  const { tz } = expTpl;
  const orgName = org.name;
  const { audiences } = task;
  const totalNumberOfBlocks = task.blocks.length;

  const audience = useMemo(() => {
    if (viewMode.mode === 'AUDIENCES_DETAIL') {
      return viewMode.editModel;
    }
    if (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId) {
      return getAudienceById(audiences, viewMode.previewAudienceId);
    }
    return getEveryoneAudience(audiences);
  }, [viewMode, audiences]);

  const interpolationOptions: InterpolationDefaultOptions = useMemo(
    () => ({
      dateFormat: org.dateFormats.MMDDYYYY
    }),
    [org.dateFormats.MMDDYYYY]
  );

  const denormalizedTask = useMemo(() => {
    const getSubTasks = () => {
      const subTasks = getSubTaskTemplates(task);
      if (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks) {
        return filterBlocksForAudience(subTasks, viewMode.editModel);
      }
      if (
        viewMode.mode === 'AUDIENCES_OVERVIEW' &&
        viewMode.previewAudienceId
      ) {
        return filterBlocksForAudience(subTasks, audience);
      }
      return subTasks;
    };

    const ignoreAudience = !(
      (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks) ||
      (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId)
    );
    return toDenormalizedTask(
      {
        id: 'xxx',
        experienceId: 'xxx',
        templateId: 'xxx',
        status: TaskStatus.RUNNING,
        actor: task.actor,
        overrides: null,
        subTasks: getSubTasks().map((s) => ({
          // Using the template id as regular id, so that it's
          // easier for us to mix and match between the two data types.
          id: s.id,
          templateId: s.id,
          type: s.type,
          status: SubTaskStatus.OPEN,
          completedAt: null,
          requirementType: s.requirementType,
          parentLayoutElement: s.parentLayoutElement
        })),
        audience: audience.id,
        timing: {
          // TODO TIMING
          start: now(),
          end: now(),
          manualOverride: false
        }
      },
      {
        ...task,
        // toDenormalizedTask will pick it's audience on the inside,
        // so we need to make sure that edited models are wevead in here
        audiences: task.audiences.find((a) => a.id === audience.id)
          ? task.audiences.map((a) => (a.id === audience.id ? audience : a)) // edit audience case
          : [...task.audiences, audience] // create audience case
      },
      { [PARTICIPANT_ACTOR_KEY]: participant },
      now(),
      {
        experience: { vars: { title: expTpl.title, tasks: {} } },
        org: { vars: { name: orgName } },
        roles: []
      },
      { answersById: {}, calendlyEventsById: {} },
      [],
      tz,
      interpolationOptions,
      [],
      now(),
      [],
      { ignoreAudience }
    );
  }, [
    participant,
    task,
    tz,
    expTpl,
    orgName,
    viewMode,
    audience,
    interpolationOptions
  ]);

  const blockPicker: TaskMessagePreviewProps['blockPicker'] = useMemo(() => {
    if (onPickBlocks && viewMode.mode === 'AUDIENCES_DETAIL') {
      const audience = viewMode.editModel;
      return {
        picked: audience.blocks,
        onUpdate: onPickBlocks
      };
    }
    return undefined;
  }, [viewMode, onPickBlocks]);

  const flags = useMemo(() => {
    // NEW_UX: the per-block audience flags ("E" / "All") are redundant with the
    // segment tabs on top of the preview and the per-block Assign control, so the
    // new editor keeps the preview clean and omits them.
    if (newUx) {
      return undefined;
    }
    if (audiences.length === 1) {
      if (audiences[0].blocks.length === totalNumberOfBlocks) {
        return undefined;
      }
    }

    if (viewMode.mode === 'AUDIENCES_OVERVIEW' && viewMode.previewAudienceId) {
      return undefined;
    }

    if (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks) {
      return undefined;
    }

    const res: { [blockId: string]: React.ReactNode[] } = {};
    audiences.forEach((uneditedAudience) => {
      const a =
        viewMode.mode === 'AUDIENCES_DETAIL' &&
        uneditedAudience.id === viewMode.editModel.id
          ? viewMode.editModel
          : uneditedAudience;
      const name = getAudienceName(a, audiences.length > 1, t);
      const el = (
        <DragDropItemFlag
          key={a.id}
          label={name[0]}
          color={a.color || DEFAULT_AUDIENCE_COLOR}
          tooltip={t('Used by {{ audienceName }} audience', {
            audienceName: name
          })}
        />
      );
      a.blocks.forEach((bId) => {
        const container = (res[bId] = res[bId] || []);
        container.push(el);
      });
    });
    return mapValues(res, (els) => {
      if (els.length === audiences.length && audiences.length > 1) {
        return [
          <DragDropItemFlag
            label={t('All')}
            color={DEFAULT_AUDIENCE_COLOR}
            tooltip={t('Used by all audiences')}
          />
        ];
      }
      return els;
    });
  }, [viewMode, audiences, totalNumberOfBlocks, t, newUx]);

  // NEW_UX removes the "preview" concept entirely: a focused segment tab is just the
  // editable view of that segment, so none of the preview/audience info banners apply.
  const infoBox = newUx ? null : (
    <>
      {viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId &&
      audience.blocks.length === 0 ? (
        <InfoBox>
          <LightMdToJsx>
            {t(
              "No visible blocks. This task won't be sent to the **{{ audienceName }}** audience.",
              {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      ) : viewMode.mode === 'AUDIENCES_OVERVIEW' &&
        viewMode.previewAudienceId ? (
        // NEW_UX: the selected segment is already shown by the segment tabs on top
        // of the preview sheet, so the "This is a preview of X" banner is redundant.
        newUx ? null : (
          <InfoBox>
            <LightMdToJsx>
              {t('This is a preview of the **{{ audienceName }}** audience.', {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              })}
            </LightMdToJsx>
          </InfoBox>
        )
      ) : !newUx &&
        viewMode.mode === 'AUDIENCES_OVERVIEW' &&
        audiences.length > 1 ? (
        <InfoBox>
          {t('Showing all blocks. Select an audience to preview.')}
        </InfoBox>
      ) : viewMode.mode === 'AUDIENCES_DETAIL' ? (
        <InfoBox>
          <LightMdToJsx>
            {t(
              'New blocks will only apply to the audience **{{ audienceName }}**.',
              {
                audienceName: getAudienceName(audience, audiences.length > 1, t)
              }
            )}
          </LightMdToJsx>
        </InfoBox>
      ) : null}
    </>
  );

  if (dragDropContext) {
    dragDropContext.current = {
      currentlyVisibleBlocks: denormalizedTask.blocks.map((b) => b.id)
    };
  }

  const wontSend =
    (viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId &&
      audience.blocks.length === 0) ||
    false;

  const taskMessagePreviewProps: TaskMessagePreviewProps = {
    task: denormalizedTask,
    expTpl,
    org,
    actor,
    selectedBlock,
    onSelectBlock,
    onDeleteBlocks,
    onDuplicateBlock,
    blockPicker,
    validation,
    draggable,
    infoBox,
    flags,
    anchorFields,
    renderInlineEditor,
    batchMode: task.batchMode,
    tokens,
    newUx,
    readOnly,
    assignAudiences,
    onToggleBlockAudience,
    focusedAudienceId: audience.rule !== 'EVERYONE' ? audience.id : null
  };

  const previewContent = (
    <>
      {aiGeneratorState && (
        <AIGeneratorLoadingIndicator state={aiGeneratorState} />
      )}
      <div onClick={onSelectBlock ? () => onSelectBlock(null) : undefined}>
        <TaskMessagePreviewStart
          {...taskMessagePreviewProps}
          hidePreview={wontSend}
        />
      </div>
    </>
  );

  // The newUx shells (editor + read-only viewer) already supply their own canvas
  // chrome (UX.PreviewCanvas) around this — the classic PreviewContainer/MainContainer
  // wrapper below is the pre-newUx side-by-side layout's own background/borders and
  // would otherwise double up with it.
  if (newUx) {
    return previewContent;
  }

  return (
    <PreviewContainer>
      <PreviewHeading>
        <Typography variant="h6">Preview</Typography>
        {sendTestTaskButton}
      </PreviewHeading>
      <PreviewBody>
        <MainContainer>{previewContent}</MainContainer>
      </PreviewBody>
    </PreviewContainer>
  );
};

/*
@guide Create a new subtask - 2.3 Add the SettingsReadOnly component

Define the component which is used to show your new subtask in all non-edit modes

Lives in the same file as the edit component associated with it.
Typically just a simple textual representation of the edited values for your new subtask
*/
const SubTaskBlockSettingsItemReadOnly = ({
  value,
  resources,
  expTpl,
  validation,
  anchorFields,
  customFields,
  tokens,
  showOnCompleteHookSettings
}: {
  value: SubTaskTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  validation: TaskBuilderBlockValidationStateItem | null;
  customFields: CustomUserField[];
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  showOnCompleteHookSettings: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const getBody = () => {
    switch (value.type) {
      case SubTaskType.CONFIRM:
        return (
          <BlockSettingsSubTaskConfirmReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.VISIT:
        return (
          <BlockSettingsSubTaskVisitReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.WATCH:
        return (
          <BlockSettingsSubTaskWatchReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.MEET:
        return <BlockSettingsSubTaskMeetReadOnly value={value} />;
      case SubTaskType.CREATE_MEETING:
        return (
          <BlockSettingsSubtaskCreateCalendarEventReadOnly
            value={value}
            anchorFields={anchorFields}
            expTpl={expTpl}
            tokens={tokens}
            customFields={customFields}
            resources={resources}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
          />
        );
      case SubTaskType.SUBMIT:
        return (
          <BlockSettingsSubTaskSubmitReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.QUESTION:
        return (
          <BlockSettingsSubTaskQuestionReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.CALENDLY_INVITE:
        return (
          <BlockSettingsSubTaskCalendlyInviteReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.DOCUMENT_READ:
        return (
          <BlockSettingsDocumentReadReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.SWAG_UP_CHOICE:
        return <BlockSettingsSwagUpChoiceReadOnly value={value} />;
      case SubTaskType.DATA_FIELD_CHANGE:
        return (
          <BlockSettingsSubtaskDataFieldChangeReadOnly
            value={value}
            expTpl={expTpl}
            tokens={tokens}
          />
        );
      case SubTaskType.DOCUSIGN_ENVELOPE_SIGN:
        return (
          <BlockSettingsDocuSignEnvelopeSignReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.BACKGROUND_CHECK_INVITE:
        return (
          <BlockSettingsBackgroundCheckInviteReadOnly
            value={value}
            resources={resources}
            expTpl={expTpl}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
          />
        );
      case SubTaskType.COMPLETE_LMS_MODULE:
        return (
          <BlockSettingsCompleteLmsModuleReadOnly
            value={value}
            expTpl={expTpl}
            tokens={tokens}
          />
        );
      default:
        return assertNever(value);
    }
  };
  return (
    <>
      {value.requirementType === RequirementType.OPTIONAL && (
        <FlexContainer fullWidth marginBottom={2} marginTop={-1}>
          <Typography variant="caption">
            <i> {t(`Optional subtask`)}</i>
          </Typography>
        </FlexContainer>
      )}
      {getBody()}
    </>
  );
};

const ContentBlockSettingsItemReadOnly = ({
  value,
  validation
}: {
  value: ContentTemplate | LayoutBlock;
  validation: TaskBuilderBlockValidationStateItem | null;
}) => {
  switch (value.type) {
    case ContentType.TEXT:
      return <BlockSettingsContentTextReadOnly value={value} />;
    case ContentType.CALLOUT:
      return <BlockSettingsContentCalloutReadOnly value={value} />;

    case ContentType.HEADING:
      return <BlockSettingsContentHeadingReadOnly value={value} />;
    case ContentType.IMAGE:
      return <BlockSettingsContentImageReadOnly value={value} />;
    case ContentType.DIVIDER:
      return <BlockSettingsContentDividerReadOnly value={value} />;
    case LayoutBlockType.COLUMNS:
      return <BlockSettingsLayoutColumnsReadOnly value={value} />;
    default:
      return assertNever(value);
  }
};

/*
@guide Create a new subtask - 2.2 Add the Settings component

Define the component which is used to edit your new subtask

As you created a new type already, you will see this component flash up with an error,
as it contains an assertNever type switch which requires us to define something for the new type

Create a new component called BlockSettingsSubTaskYourNewType in
pkgs/app/src/features/Experiences/components/Builders/TaskBuilder/components/BlockSettings/SubTask/YourNewType.tsx

All these components have a fairly unified interface. They take
- a value -> the SubTaskTemplate
- an onChange fn -> returning a new SubTaskTemplate
- the expTpl -> containing information about the template in case the new subtask needs it
- customFields -> the org's custom fields, so that any reference to a custom field can be resolved to a human readable name
- goToSubPage -> required for OnCompleteHooks
- resouces/onChange resources -> required for OnCompleteHooks and maybe more, depending on the needs of your subtask
- validation/setValidation -> validation helpers
*/
export const SubTaskBlockSettingsItem = ({
  value,
  onChange,
  actor,
  channels,
  resources,
  onChangeResources,
  expTpl,
  validation,
  setValidation,
  customFields,
  anchorFields,
  goToSubPage,
  showOnCompleteHookSettings,
  hideOptionalSubTaskSetting,
  tokens
}: {
  value: SubTaskTemplate;
  onChange: (nextValue: SubTaskTemplate) => void;
  actor: string;
  channels: ScheduleChannel[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  anchorFields: AnchorField[];
  customFields: CustomUserField[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  hideOptionalSubTaskSetting: boolean;
  tokens: TokenDefinition[];
}) => {
  const valProps = { validation, setValidation };
  const { t } = useUnsafeTranslation();
  const getBody = () => {
    switch (value.type) {
      case SubTaskType.CONFIRM:
        return (
          <BlockSettingsSubTaskConfirm
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            resources={resources}
            onChangeResources={onChangeResources}
            goToSubPage={goToSubPage}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            customFields={customFields}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.VISIT:
        return (
          <BlockSettingsSubTaskVisit
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            onChangeResources={onChangeResources}
            goToSubPage={goToSubPage}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.WATCH:
        return (
          <BlockSettingsSubTaskWatch
            tokens={tokens}
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            onChangeResources={onChangeResources}
            goToSubPage={goToSubPage}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            {...valProps}
          />
        );
      case SubTaskType.MEET:
        return (
          <BlockSettingsSubTaskMeet
            value={value}
            onChange={onChange}
            customFields={customFields}
          />
        );
      // TODO: Create meeting subtask
      case SubTaskType.CREATE_MEETING:
        return (
          <BlockSettingsSubtaskCreateCalendarEvent
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            anchorFields={anchorFields}
            resources={resources}
            onChangeResources={onChangeResources}
            tokens={tokens}
            goToSubPage={goToSubPage}
            customFields={customFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            {...valProps}
          />
        );
      case SubTaskType.SUBMIT:
        return (
          <BlockSettingsSubTaskSubmit
            value={value}
            onChange={onChange}
            resources={resources}
            onChangeResources={onChangeResources}
            expTpl={expTpl}
            customFields={customFields}
            goToSubPage={goToSubPage}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.QUESTION:
        return (
          <BlockSettingsSubTaskQuestion
            value={value}
            onChange={onChange}
            channels={channels}
            resources={resources}
            onChangeResources={onChangeResources}
            expTpl={expTpl}
            customFields={customFields}
            goToSubPage={goToSubPage}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.CALENDLY_INVITE:
        return (
          <BlockSettingsSubTaskCalendlyInvite
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            onChangeResources={onChangeResources}
            goToSubPage={goToSubPage}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.DOCUMENT_READ:
        return (
          <BlockSettingsDocumentRead
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            onChangeResources={onChangeResources}
            goToSubPage={goToSubPage}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.SWAG_UP_CHOICE:
        return (
          <BlockSettingsSwagUpChoice
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            customFields={customFields}
            {...valProps}
          />
        );
      case SubTaskType.DATA_FIELD_CHANGE:
        return (
          <BlockSettingsSubtaskDataFieldChange
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            goToSubPage={goToSubPage}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.DOCUSIGN_ENVELOPE_SIGN:
        return (
          <BlockSettingsDocuSignEnvelopeSign
            value={value}
            onChange={onChange}
            actor={actor}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            onChangeResources={onChangeResources}
            goToSubPage={goToSubPage}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.BACKGROUND_CHECK_INVITE:
        return (
          <BlockSettingsBackgroundCheckInvite
            value={value}
            onChange={onChange}
            actor={actor}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            onChangeResources={onChangeResources}
            goToSubPage={goToSubPage}
            tokens={tokens}
            {...valProps}
          />
        );
      case SubTaskType.COMPLETE_LMS_MODULE:
        return (
          <BlockSettingsCompleteLmsModule
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            customFields={customFields}
            resources={resources}
            goToSubPage={goToSubPage}
            tokens={tokens}
            {...valProps}
          />
        );
      default:
        return assertNever(value);
    }
  };

  return (
    <>
      {!hideOptionalSubTaskSetting && (
        <FormRow>
          <FormControlLabel
            label={
              <FlexContainer alignItems="center">
                {t(`Optional subtask`)}
              </FlexContainer>
            }
            control={
              <Switch
                checked={value.requirementType === RequirementType.OPTIONAL}
                onChange={(ev) => {
                  const nextValue = {
                    ...value
                  };

                  if (ev.target.checked) {
                    nextValue.requirementType = RequirementType.OPTIONAL;
                  } else {
                    nextValue.requirementType = RequirementType.MANDATORY;
                  }

                  onChange(nextValue);
                }}
              />
            }
          />
        </FormRow>
      )}
      {getBody()}
    </>
  );
};

export const LayoutBlockSettingsItem = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  customFields,
  tokens,
  subtaskPasThroughProps,
  blocks
}: {
  value: LayoutBlock;
  blocks: TaskBlockTemplate[];
  onChange: (nextValue: TaskBlockTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  tokens: TokenDefinition[];
  subtaskPasThroughProps?: {
    anchorFields: AnchorField[];
    customFields: CustomUserField[];
    goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
    showOnCompleteHookSettings: boolean;
    hideOptionalSubTaskSetting: boolean;
    actor: string;
    channels: ScheduleChannel[];
    resources: IExperienceTemplateResource[];
    onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  };
}) => {
  const valProps = { validation, setValidation };
  const getBody = () => {
    switch (value.type) {
      case LayoutBlockType.COLUMNS:
        return (
          <BlockSettingsLayoutColumns
            blocks={blocks}
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            tokens={tokens}
            customFields={customFields}
            subtaskPasThroughProps={subtaskPasThroughProps}
            {...valProps}
          />
        );
    }
  };

  return (
    <>
      {/* {subtaskPasThroughProps && !subtaskPasThroughProps.hideOptionalSubTaskSetting && (
        <FormRow>
          <FormControlLabel
            label={
              <FlexContainer alignItems="center">
                {t(`Optional subtask`)}
              </FlexContainer>
            }
            control={
              <Switch
                checked={value.requirementType === RequirementType.OPTIONAL}
                onChange={(ev) => {
                  const nextValue = {
                    ...value
                  };

                  if (ev.target.checked) {
                    nextValue.requirementType = RequirementType.OPTIONAL;
                  } else {
                    nextValue.requirementType = RequirementType.MANDATORY;
                  }

                  onChange(nextValue);
                }}
              />
            }
          />
        </FormRow>
      )} */}
      {getBody()}
    </>
  );
};
// export const LayouBlockSettingsItem = ({
//   value,
//   onChange,
//   validation,
//   setValidation,
//   expTpl,
//   customFields,
//   tokens,
//   blocks,
//   subtaskPasThroughProps
// }: {
//   value: LayoutBlock;
//   onChange: (nextValue: TaskBlockTemplate) => void;
//   blocks: TaskBlockTemplate[];
//   validation: TaskBuilderBlockValidationStateItem | null;
//   setValidation: React.Dispatch<
//     SetStateAction<TaskBuilderBlockValidationState>
//   >;
//   expTpl: TaskBuilderContext;
//   customFields: CustomUserField[];
//   tokens: TokenDefinition[];
//   subtaskPasThroughProps?: {
//     anchorFields: AnchorField[];
//     customFields: CustomUserField[];
//     goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
//     showOnCompleteHookSettings: boolean;
//     hideOptionalSubTaskSetting: boolean;
//     actor: string;
//     resources: IExperienceTemplateResource[];
//     onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
//   };
// }) => {
//   const valProps = { validation, setValidation };
//   switch (value.type) {
//     case LayoutBlockType.COLUMNS:
//       return (
//         <BlockSettingsLayoutColumns
//           blocks={blocks}
//           value={value}
//           onChange={onChange}
//           expTpl={expTpl}
//           tokens={tokens}
//           customFields={customFields}
//           subtaskPasThroughProps={subtaskPasThroughProps}
//           {...valProps}
//         />
//       );
//   }
// };

export const ContentBlockSettingsItem = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  customFields,
  tokens,
  timelineItemId,
  timelineItemTitle,
  timelineItemType
}: {
  value: ContentTemplate;
  onChange: (nextValue: ContentTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  tokens: TokenDefinition[];
  timelineItemId?: string;
  timelineItemType?: 'task' | 'message';
  timelineItemTitle?: string;
}) => {
  const valProps = { validation, setValidation };
  switch (value.type) {
    case ContentType.TEXT:
      return (
        <BlockSettingsContentText
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          tokens={tokens}
          timelineItemId={timelineItemId}
          timelineItemTitle={timelineItemTitle}
          timelineItemType={timelineItemType}
          {...valProps}
        />
      );
    case ContentType.CALLOUT:
      return (
        <BlockSettingsContentCallout
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          tokens={tokens}
          {...valProps}
        />
      );

    case ContentType.HEADING:
      return (
        <BlockSettingsContentHeading
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          tokens={tokens}
          {...valProps}
        />
      );
    case ContentType.IMAGE:
      return (
        <BlockSettingsContentImage
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          customFields={customFields}
          {...valProps}
        />
      );
    case ContentType.DIVIDER:
      return <BlockSettingsContentDivider value={value} expTpl={expTpl} />;
    default:
      return assertNever(value);
  }
};

export const SideSettingsReadOnlyNoSelection = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  color: p.theme.custom.colors.gray.w500
}));

export const BlockSettingsReadOnly = ({
  b,
  resources,
  expTpl,
  validation,
  customFields,
  anchorFields,
  tokens,
  onBack,
  showOnCompleteHookSettings
}: {
  b: TaskBlockTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  validation: TaskBuilderBlockValidationStateItem | null;
  customFields: CustomUserField[];
  anchorFields: AnchorField[];
  tokens: TokenDefinition[];
  onBack: () => void;
  showOnCompleteHookSettings: boolean;
}) => {
  const def = useBlockDefinition(b, resources, NOOP);
  return (
    <SideContainer>
      <SideContainerHeader def={def} onBack={onBack} />
      {isContentOrLAyoutTemplateBlock(b) ? (
        <ContentBlockSettingsItemReadOnly value={b} validation={validation} />
      ) : (
        <SubTaskBlockSettingsItemReadOnly
          value={b}
          resources={resources}
          expTpl={expTpl}
          anchorFields={anchorFields}
          validation={validation}
          customFields={customFields}
          tokens={tokens}
          showOnCompleteHookSettings={showOnCompleteHookSettings}
        />
      )}
    </SideContainer>
  );
};

export const BlockSettings = ({
  b,
  onChangeBlock,
  actor,
  channels,
  resources,
  onChangeResources,
  expTpl,
  validation,
  setValidation,
  onBack,
  anchorFields,
  customFields,
  showOnCompleteHookSettings = true,
  hideOptionalSubTaskSetting,
  tokens,
  blocks,
  timelineItemId,
  timelineItemTitle,
  timelineItemType,
  newUx = false
}: {
  b: TaskBlockTemplate;
  blocks: TaskBlockTemplate[];
  onChangeBlock: (nextB: TaskBlockTemplate) => void;
  actor: string;
  channels: ScheduleChannel[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  expTpl: TaskBuilderContext;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  anchorFields: AnchorField[];
  onBack: () => void;
  customFields: CustomUserField[];
  showOnCompleteHookSettings?: boolean;
  hideOptionalSubTaskSetting: boolean;
  tokens: TokenDefinition[];
  timelineItemId?: string;
  timelineItemType?: 'task' | 'message';
  timelineItemTitle?: string;
  newUx?: boolean;
}) => {
  const def = useBlockDefinition(b, resources, onChangeResources);
  const [subPage, setSubPage] = useState<BlockSettingsSubPage | null>(null);
  const onBackFromSubPage = () => setSubPage(null);
  return (
    <SideContainer>
      {subPage ? (
        <>
          <SideContainerHeader
            def={subPage.header.def}
            onBack={onBackFromSubPage}
            newUx={newUx}
          />
          {React.createElement(subPage.body, {
            onBack: onBackFromSubPage,
            resources,
            onChangeResources
          })}
        </>
      ) : (
        <>
          <SideContainerHeader def={def} onBack={onBack} newUx={newUx} />
          {isContentTemplateBlock(b) ? (
            <ContentBlockSettingsItem
              key={b.id}
              value={b}
              onChange={onChangeBlock}
              validation={validation}
              setValidation={setValidation}
              expTpl={expTpl}
              customFields={customFields}
              tokens={tokens}
              timelineItemId={timelineItemId}
              timelineItemTitle={timelineItemTitle}
              timelineItemType={timelineItemType}
            />
          ) : isSubTaskTemplateBlock(b) ? (
            <SubTaskBlockSettingsItem
              key={b.id}
              value={b}
              onChange={onChangeBlock}
              actor={actor}
              channels={channels}
              resources={resources}
              onChangeResources={onChangeResources}
              expTpl={expTpl}
              validation={validation}
              setValidation={setValidation}
              customFields={customFields}
              anchorFields={anchorFields}
              goToSubPage={setSubPage}
              showOnCompleteHookSettings={showOnCompleteHookSettings}
              hideOptionalSubTaskSetting={hideOptionalSubTaskSetting}
              tokens={tokens}
            />
          ) : (
            <LayoutBlockSettingsItem
              key={b.id}
              value={b}
              onChange={onChangeBlock}
              validation={validation}
              setValidation={setValidation}
              expTpl={expTpl}
              customFields={customFields}
              tokens={tokens}
              subtaskPasThroughProps={{
                customFields,
                anchorFields,
                goToSubPage: setSubPage,
                showOnCompleteHookSettings,
                hideOptionalSubTaskSetting,
                actor,
                channels,
                onChangeResources,
                resources
              }}
              blocks={blocks}
            />
          )}
        </>
      )}

      <DebugText>Block ID: {b.id}</DebugText>
    </SideContainer>
  );
};

export type BlockDefinition<T extends TaskBlockTemplate> = {
  key: string;
  type: T['type'];
  icon: Icon;
  label: string;
  description?: string;
  // Greyed-out, non-draggable. Useful for "this requires an integration to be set up"
  // states — the entry stays visible so admins discover the capability.
  disabled?: boolean;
  disabledTooltip?: string;
  // Not offered in the block picker (feature-flagged), but still registered so
  // useBlockDefinition resolves existing blocks of this kind instead of throwing.
  hidden?: boolean;
  emptyBlock: () => T; // will return a task block template
};

export const useContentBlocks = (
  opts: {
    extendedSelection?: boolean;
  } = {}
): BlockDefinition<ContentTemplate | LayoutBlock>[] => {
  const { t } = useUnsafeTranslation();
  const extendedSelection = opts.extendedSelection || false;
  return useMemo(
    () =>
      compact([
        {
          key: 'TEXT',
          type: ContentType.TEXT,
          icon: AlignLeft,
          label: t('Text'),
          emptyBlock: () => ({
            id: shortid(),
            parentLayoutElement: null,

            type: ContentType.TEXT,
            data: {
              t: {
                t: '',
                v: 1
              }
            }
          })
        },
        extendedSelection && {
          key: 'CALLOUT',
          type: ContentType.CALLOUT,
          icon: Square,
          label: t('Call-out'),
          emptyBlock: () => ({
            id: shortid(),
            parentLayoutElement: null,
            type: ContentType.CALLOUT,
            data: {
              color: 'blue',
              t: {
                t: '',
                v: 1
              }
            }
          })
        },
        {
          key: 'HEADING',
          type: ContentType.HEADING,
          icon: Type,
          label: t('Heading'),
          emptyBlock: () => ({
            id: shortid(),
            parentLayoutElement: null,
            type: ContentType.HEADING,
            data: {
              t: ''
            }
          })
        },
        {
          key: 'IMAGE',
          type: ContentType.IMAGE,
          icon: Image,
          label: t('Image'),
          emptyBlock: () => ({
            id: shortid(),
            parentLayoutElement: null,
            type: ContentType.IMAGE,
            data: {
              src: '',
              alt: '',
              align: 'center',
              width: 100,
              type: 'url'
            }
          })
        },
        extendedSelection && {
          key: 'COLUMNS',
          type: LayoutBlockType.COLUMNS,
          icon: Columns,
          label: t('Columns'),
          emptyBlock: () => ({
            id: shortid(),
            parentLayoutElement: null,
            type: LayoutBlockType.COLUMNS,
            data: {
              columns: [
                {
                  blockId: shortid(),
                  columnWeight: 50
                },
                {
                  blockId: shortid(),
                  columnWeight: 50
                }
              ]
            }
          })
        },
        {
          key: 'DIVIDER',
          type: ContentType.DIVIDER,
          icon: SeparatorHorizontal,
          label: t('Divider'),
          emptyBlock: () => ({
            id: shortid(),
            parentLayoutElement: null,
            type: ContentType.DIVIDER,
            data: {
              variant: 'light'
            }
          })
        }
      ]),
    [t, extendedSelection]
  );
};

const getSubTaskQuestionTemplateQuestionBlock = (
  type: QuestionType
): SubTaskQuestionTemplateQuestion => {
  switch (type) {
    case QuestionType.TEXT:
      return {
        type: QuestionType.TEXT,
        label: '',
        placeholder: '',
        rows: 1,
        sensitive: false
      };
    case QuestionType.SELECT:
      return { type: QuestionType.SELECT, label: '', options: [] };
    case QuestionType.MULTI_SELECT:
      return { type: QuestionType.MULTI_SELECT, label: '', options: [] };
    case QuestionType.ADDRESS:
      return {
        type: QuestionType.ADDRESS,
        label: '',
        excludedFields: [],
        sensitive: false
      };
    case QuestionType.SUPPORTING_ACTOR:
      return {
        type: QuestionType.SUPPORTING_ACTOR,
        label: '',
        supportingActorToAssign: ''
      };
    case QuestionType.LINEAR_SCALE:
    case QuestionType.RATING:
      return emptyScaleQuestion(type);
  }
};

const emptyQuestionBlock = (
  type: QuestionType,
  resources: IExperienceTemplateResource[],
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void,
  activeIntegrations: Set<Integration>
): ISubTaskQuestionTemplate => {
  const question: SubTaskQuestionTemplateQuestion = getSubTaskQuestionTemplateQuestionBlock(
    type
  );

  if (type === QuestionType.SUPPORTING_ACTOR) {
    const storage: SubTaskDataDestinationSupportingActor = {
      type: InternalStorageInstructionType.SUPPORTING_ACTOR,
      destinationType: DestinatioType.SUBTASK_ANSWER,
      supportingActorToAssign: ''
    };
    return {
      id: shortid(),
      parentLayoutElement: null,
      type: SubTaskType.QUESTION,
      hooks: {},
      requirementType: RequirementType.MANDATORY,
      data: {
        question,
        storage,
        storages: [storage]
      }
    };
  }

  if (activeIntegrations.has(Integration.BAMBOOHR)) {
    return {
      id: shortid(),
      parentLayoutElement: null,
      type: SubTaskType.QUESTION,
      hooks: {},
      requirementType: RequirementType.MANDATORY,
      data: {
        question,
        storage: {
          type: ExternalServiceType.BAMBOO_HR,
          destinationType: DestinatioType.SUBTASK_ANSWER,
          field: null
        },
        storages: [
          {
            type: ExternalServiceType.BAMBOO_HR,
            destinationType: DestinatioType.SUBTASK_ANSWER,
            field: null
          }
        ]
      }
    };
  }

  if (activeIntegrations.has(Integration.GOOGLE_SHEETS)) {
    let resource = resources.find(
      (r) => r.type === TemplateResourceType.GOOGLE_SHEET
    );
    if (!resource) {
      resource = {
        type: TemplateResourceType.GOOGLE_SHEET,
        resourceId: shortid(),
        docId: '',
        sheetId: '',
        docTitle: '',
        sheetTitle: '',
        docStatus: 'PENDING',
        sheetStatus: 'PENDING',
        mappings: DEFAULT_SHEET_MAPPINGS_V2,
        v: 2,
        additionalSaveParticipantToResourceMoments: []
      };
      onChangeResources([...resources, resource]);
    }
    const storage: SubTaskDataDestinationGoogleSheet = {
      type: ExternalServiceType.GOOGLE_SHEET,
      destinationType: DestinatioType.SUBTASK_ANSWER,
      resourceId: resource.resourceId,
      columnName: ''
    };

    return {
      id: shortid(),
      parentLayoutElement: null,
      type: SubTaskType.QUESTION,
      hooks: {},
      requirementType: RequirementType.MANDATORY,
      data: {
        question,
        storage,
        storages: [storage]
      }
    };
  }

  return {
    id: shortid(),
    parentLayoutElement: null,
    type: SubTaskType.QUESTION,
    hooks: {},
    requirementType: RequirementType.MANDATORY,
    data: {
      question,
      storage: {
        type: InternalStorageInstructionType.USER_PROFILE,
        destinationType: DestinatioType.SUBTASK_ANSWER,
        field: ''
      },
      storages: [
        {
          type: InternalStorageInstructionType.USER_PROFILE,
          destinationType: DestinatioType.SUBTASK_ANSWER,
          field: ''
        }
      ]
    }
  };
};

/*
@guide Create a new subtask - 2.1.1 Add defaults

Add defaults for your new type. This BlockDefiniton is responsible for making
your new item appear in the BlockSelector component when creating/editing a task
in the admin app UI.
During development make it only visible to Dado Employees, so that we can partially
deploy the feature for testing/sign-off.
We still keep the block definition around, but hide in the selector (see Create a new subtask 2.1.2)
*/
export const useSubTaskBlocks = (
  resources: IExperienceTemplateResource[],
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void,
  excludedBlocksByKey: string[] = EMPTY_ARR, // a bit brittle, as these keys are not typed at the moment!
  includeIntegrations: Integration[] | null = null
): BlockDefinition<SubTaskTemplate>[] => {
  const { t } = useUnsafeTranslation();
  const { active: activeIntegrations } = useIntegrationsContext();
  const isCheckrFeatureEnabled = useFeatureEnabled('CHECKR_V1');
  const isScaleQuestionsEnabled = useFeatureEnabled('SCALE_QUESTIONS_V1');

  const activeIntegrationsWithoutUnwanted = useMemo(() => {
    if (includeIntegrations === null) {
      return activeIntegrations;
    }

    return new Set(
      Array.from(activeIntegrations).filter((integration) =>
        includeIntegrations.includes(integration)
      )
    );
  }, [activeIntegrations, includeIntegrations]);

  return useMemo(() => {
    const blocks: BlockDefinition<SubTaskTemplate>[] = compact([
      {
        key: 'CONFIRM',
        type: SubTaskType.CONFIRM,
        icon: Check,
        label: t('Confirm Action'),
        description: t('Completed when the participant presses the button.'),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.CONFIRM,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: { label: '' }
        })
      },
      {
        key: 'VISIT',
        type: SubTaskType.VISIT,
        icon: Link,
        label: t('subTask.visit.label'),
        description: t('Completed when the participant visits the link.'),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.VISIT,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            label: '',
            url: ''
          }
        })
      },
      {
        key: 'DOCUMENT_READ',
        type: SubTaskType.DOCUMENT_READ,
        icon: File,
        label: t('Read Document'),
        description: t(
          'Completed when the participant reaches the end of the document.'
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.DOCUMENT_READ,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            title: '',
            originalFileName: '',
            docStoragePath: '',
            docUrl: '',
            thumbnailStoragePath: '',
            thumbnailUrl: '',
            contentType: 'application/pdf',
            pages: 0,
            sizeInBytes: 0
          },
          completeOnDownload: false
        })
      },
      {
        key: 'QUESTION_text',
        type: SubTaskType.QUESTION,
        icon: Pencil,
        label: t('subTask.question.label'),
        placeholder: t('subTask.question.placeholder'),
        description: t('Completed when the participant submit their answer.'),
        emptyBlock: () =>
          emptyQuestionBlock(
            QuestionType.TEXT,
            resources,
            onChangeResources,
            activeIntegrationsWithoutUnwanted
          )
      },
      {
        key: 'QUESTION_select',
        type: SubTaskType.QUESTION,
        icon: SubtaskChoice as any,
        label: t('Select Option'),
        description: t(
          'Completed when the participant selects from predefined choices.'
        ),
        emptyBlock: () =>
          emptyQuestionBlock(
            QuestionType.SELECT,
            resources,
            onChangeResources,
            activeIntegrationsWithoutUnwanted
          )
      },
      {
        key: 'QUESTION_linearScale',
        type: SubTaskType.QUESTION,
        icon: SlidersHorizontal,
        label: t('Linear Scale'),
        description: t(
          'Completed when the participant picks a value on a numbered scale.'
        ),
        hidden: !isScaleQuestionsEnabled,
        emptyBlock: () =>
          emptyQuestionBlock(
            QuestionType.LINEAR_SCALE,
            resources,
            onChangeResources,
            activeIntegrationsWithoutUnwanted
          )
      },
      {
        key: 'QUESTION_rating',
        type: SubTaskType.QUESTION,
        icon: Star,
        label: t('Rating'),
        description: t(
          'Completed when the participant gives a star rating.'
        ),
        hidden: !isScaleQuestionsEnabled,
        emptyBlock: () =>
          emptyQuestionBlock(
            QuestionType.RATING,
            resources,
            onChangeResources,
            activeIntegrationsWithoutUnwanted
          )
      },
      {
        key: 'QUESTION_address',
        type: SubTaskType.QUESTION,
        icon: Home,
        label: t('Provide Address'),
        description: t('Completed when the participant provides an address.'),
        emptyBlock: () =>
          emptyQuestionBlock(
            QuestionType.ADDRESS,
            resources,
            onChangeResources,
            activeIntegrationsWithoutUnwanted
          )
      },
      {
        key: 'QUESTION_supportingActor',
        type: SubTaskType.QUESTION,
        icon: UserPlus,
        label: t('Assign Supporting Actor '),
        description: t(
          'Completed when the recipient picks someone for the specific role.'
        ),
        emptyBlock: () =>
          emptyQuestionBlock(
            QuestionType.SUPPORTING_ACTOR,
            resources,
            onChangeResources,
            activeIntegrationsWithoutUnwanted
          )
      },
      {
        key: 'MEET',
        type: SubTaskType.MEET,
        icon: Users,
        label: t('Meet someone'),
        description: t(
          'Completed when the participant confirms having met that person.'
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.MEET,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            role: 'MANAGER'
          }
        })
      },
      {
        key: 'CREATE_MEETING',
        type: SubTaskType.CREATE_MEETING,
        icon: CalendarPlus,
        label: t('Create Calendar Event'),
        description: t(
          `Opens the user's calendar tool to create a meeting. Completes when the assignee chooses a tool.`
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.CREATE_MEETING,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            pplToAdd: [],
            title: '',
            description: '',
            anchor: { type: 'AFTER_EXPERIENCE_START_DATE', lowerBoundary: 0 },
            at: { hour: 0, minute: 0, duration: ONE_MINUTE * 30 },
            repeat: { enabled: false, frequency: 'DAILY' },
            createLinksFor: Object.values(CREATE_MEETING_SERVICES)
              .filter((x) => activeIntegrations.has(x.relatedIntegration))
              .map((x) => x.type)
          }
        })
      },
      {
        key: 'SUBMIT',
        type: SubTaskType.SUBMIT,
        icon: Upload,
        label: t('subTask.submit.label'),
        description: t(
          'Completed when the participant successfully uploads a file.'
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.SUBMIT,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            label: '',
            to: { type: 'EMAIL', email: '' },
            retentionTimeInDays: 45,
            fileNameFormat: '{{ participant.fullName }} | {{ label }}'
          }
        })
      },
      {
        key: 'WATCH',
        type: SubTaskType.WATCH,
        icon: Play,
        label: t('subTask.watch.label'),
        description: t(
          'Completed when the participant watches the video to the end.'
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.WATCH,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            label: '',
            url: ''
          }
        })
      },
      activeIntegrations.has(Integration.CALENDLY) && {
        key: 'CALENDLY_INVITE',
        type: SubTaskType.CALENDLY_INVITE,
        icon: Calendar,
        label: t('subTask.calendlyInvite.label'),
        description: t(
          'Completed when the participant books a Calendly event.'
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.CALENDLY_INVITE,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            label: '',
            event: null
          }
        })
      },

      activeIntegrations.has(Integration.SWAG_UP) && {
        key: 'SWAG_UP_CHOICE',
        type: SubTaskType.SWAG_UP_CHOICE,
        icon: Gift,
        label: t('subTask.swagUpChoice.label'),
        description: t('Completed when the participant chooses their swag.'),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.SWAG_UP_CHOICE,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            choices: [],
            opts: {
              hidePhotos: false
            },
            swagUpContactEmails: []
          }
        })
      },
      {
        key: 'DATA_FIELD_CHANGE',
        type: SubTaskType.DATA_FIELD_CHANGE,
        icon: CheckCircle2,
        label: t('subTask.dataFieldChange.label'),
        description: t(
          'Completes automatically based on the value of Dado field(s).'
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.DATA_FIELD_CHANGE,
          hooks: {},
          requirementType: RequirementType.MANDATORY,
          data: {
            instructions: { t: '', v: 1 },
            rules: EMPTY_RULE_GROUP
          }
        })
      },
      {
        key: 'DOCUSIGN_ENVELOPE_SIGN',
        type: SubTaskType.DOCUSIGN_ENVELOPE_SIGN,
        icon: FilePen,
        label: t('subTask.docusignEnvelopeSign.label'),
        description: t(
          'Use Docusign to send documents for signature, via a Dado sub-task.'
        ),
        // Stays visible (so admins discover it) but locked until the Docusign
        // integration is connected on the Integrations page.
        disabled: !activeIntegrations.has(Integration.DOCUSIGN),
        disabledTooltip: t('subTask.docusignEnvelopeSign.disabledTooltip'),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.DOCUSIGN_ENVELOPE_SIGN,
          hooks: {
            onStart: [{ type: SubTaskStartHookType.DOCUSIGN_CREATE_ENVELOPE }]
          },
          requirementType: RequirementType.MANDATORY,
          data: {
            resourceId: '',
            description: {
              t: t(
                'You will shortly receive an email notification from Docusign with a link to sign your document. Please follow the instructions in that email.'
              ),
              v: 1
            }
          }
        })
      },
      isCheckrFeatureEnabled && {
        key: 'BACKGROUND_CHECK_INVITE',
        type: SubTaskType.BACKGROUND_CHECK_INVITE,
        icon: SearchCheck,
        label: t('subTask.backgroundCheck.label'),
        description: t('subTask.backgroundCheck.description'),
        // Stays visible (so admins discover it) but locked until the Checkr
        // integration is connected on the Integrations page.
        disabled: !activeIntegrations.has(Integration.CHECKR),
        disabledTooltip: t('subTask.backgroundCheck.disabledTooltip'),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.BACKGROUND_CHECK_INVITE,
          hooks: {
            onStart: [
              { type: SubTaskStartHookType.BACKGROUND_CHECK_CREATE_INVITATION }
            ]
          },
          requirementType: RequirementType.MANDATORY,
          data: {
            resourceId: '',
            linkText: t('Complete your check here'),
            description: { t: '', v: 1 }
          }
        })
      },
      {
        // Visible-but-disabled (Background-Check pattern) so admins discover it
        // even before Docebo is connected. TODO: final copy/design.
        key: 'COMPLETE_LMS_MODULE',
        type: SubTaskType.COMPLETE_LMS_MODULE,
        icon: GraduationCap,
        label: t('Complete LMS module'),
        description: t(
          'Enroll the participant in a Docebo learning unit and track its completion.'
        ),
        disabled: !activeIntegrations.has(Integration.DOCEBO),
        disabledTooltip: t(
          'Connect Docebo on the Integrations page to use this sub-task.'
        ),
        emptyBlock: () => ({
          id: shortid(),
          parentLayoutElement: null,
          type: SubTaskType.COMPLETE_LMS_MODULE,
          hooks: {
            onStart: [{ type: SubTaskStartHookType.DOCEBO_ENROLL_IN_UNIT }]
          },
          requirementType: RequirementType.MANDATORY,
          data: {
            description: {
              t: t('Complete the assigned course in Docebo.'),
              v: 1
            },
            unit: { type: 'COURSE', id: '', name: '' },
            supportContact: { email: '' }
          }
        })
      }
    ]);
    return blocks.filter((b) => !excludedBlocksByKey.includes(b.key));
  }, [
    t,
    activeIntegrations,
    resources,
    onChangeResources,
    excludedBlocksByKey,
    activeIntegrationsWithoutUnwanted,
    isCheckrFeatureEnabled,
    isScaleQuestionsEnabled
  ]);
};

const useBlockDefinition = (
  blockTpl: TaskBlockTemplate,
  resources: IExperienceTemplateResource[],
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void
) => {
  const contentBlocks = useContentBlocks({ extendedSelection: true });
  const subTaskBlocks = useSubTaskBlocks(resources, onChangeResources);
  return useMemo(() => {
    const key =
      blockTpl.type === 'QUESTION'
        ? `${blockTpl.type}_${
            blockTpl.data.question.type === 'multiSelect' // multiSelect reuses the same block def as select
              ? 'select'
              : blockTpl.data.question.type
          }`
        : blockTpl.type;
    const blockDef =
      contentBlocks.find((b) => b.key === key) ||
      subTaskBlocks.find((b) => b.key === key);
    if (!blockDef) {
      throw new Error(`UNKNOWN_BLOCK_TYPE - ${blockTpl.type}`);
    }
    return blockDef;
  }, [contentBlocks, subTaskBlocks, blockTpl]);
};

const BlockListContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(),
  marginBottom: p.theme.spacing(2)
}));

const BlockListTitle = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: p.theme.spacing(2)
}));

const BlockListItem = styled('div')<{
  dragging: boolean;
  disabled?: boolean;
}>((p) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  padding: '8px 12px',
  background: p.theme.custom.colors.background.light,
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  boxShadow: p.dragging ? '0 0 8px 1px rgba(0,0,0,.15)' : 'none',

  cursor: p.disabled ? 'inherit' : 'grab',

  '> *': {
    opacity: p.disabled ? 0.3 : 1
  },

  gap: p.theme.spacing(),

  ':hover': {
    borderColor: p.disabled ? undefined : p.theme.custom.colors.gray.w400,
    boxShadow: p.disabled
      ? 'none'
      : `0 0 0 4px ${p.theme.custom.colors.gray.w200}`
  }
}));

const BlockListDragClone = styled('div')((p) => ({
  transform: 'none !important',

  '> div': {
    boxShadow: 'none'
  }
}));

/*
@guide Create a new subtask - 2.1.2 Hide the new subtask during development

During development it might be beneficial to show the new subtask only to us, but not
real users. We can merge code, play with it in dev and prod - all while not interrupting
real users until we're happy to release the feature.
Just add your new SubTaskType to the list of TEMPORARILY_UNSELECTABLE_BLOCK_TYPES - the BlockList
component will then filter them out for all non-Dado employees.
*/
const TEMPORARILIY_UNSELECTABLE_BLOCK_TYPES: (
  | ContentType
  | SubTaskType
  | LayoutBlockType
)[] = [];
const PERMANENTLY_UNSELECTABLE_BLOCK_TYPES: (
  | ContentType
  | SubTaskType
  | LayoutBlockType
)[] = [
  SubTaskType.MEET // Do not remove this - its an old SubTaskType that we don't want to use anymore.
];
const BlockList = ({
  heading,
  blocks,
  disabled,
  copyContent
}: {
  heading: string;
  blocks: BlockDefinition<TaskBlockTemplate>[];
  disabled?: boolean;
  copyContent?: React.ReactNode;
}) => {
  // Hack for copying a draggable:
  // https://github.com/atlassian/react-beautiful-dnd/issues/216#issuecomment-811566541
  const isDadoEmployee = useIsCurrentUserDadoEmployee();
  const selectableBlocks = useMemo(
    () =>
      blocks.filter(
        (b) =>
          !b.hidden &&
          !PERMANENTLY_UNSELECTABLE_BLOCK_TYPES.includes(b.type) &&
          (isDadoEmployee ||
            !TEMPORARILIY_UNSELECTABLE_BLOCK_TYPES.includes(b.type)) // NOSONAR
      ),
    [isDadoEmployee, blocks]
  );
  return (
    <BlockListContainer>
      <BlockListTitle>
        <Typography variant="h4">{heading}</Typography>
        {copyContent && <div>{copyContent}</div>}
      </BlockListTitle>
      {selectableBlocks.map((b, i) => {
        const blockDisabled = disabled || !!b.disabled;
        return (
          <Draggable
            key={b.key}
            draggableId={b.key}
            index={i}
            isDragDisabled={blockDisabled}
          >
            {(provided, snapshot) => {
              const inner = (
                <BlockListItem
                  dragging={snapshot.isDragging}
                  disabled={blockDisabled}
                >
                  <b.icon size={16} width={16} height={16} />
                  <div>{b.label}</div>
                </BlockListItem>
              );
              const item =
                b.disabled && b.disabledTooltip ? (
                  <Tooltip title={b.disabledTooltip} placement="right">
                    {inner}
                  </Tooltip>
                ) : (
                  inner
                );
              return (
                <>
                  <div
                    key={b.type}
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                      ...provided.draggableProps.style,
                      transform: snapshot.isDragging
                        ? provided.draggableProps.style?.transform
                        : 'translate(0px, 0px)'
                    }}
                  >
                    {item}
                  </div>
                  {snapshot.isDragging && (
                    <BlockListDragClone>{item}</BlockListDragClone>
                  )}
                </>
              );
            }}
          </Draggable>
        );
      })}
    </BlockListContainer>
  );
};

export const BlockSelector = ({
  contentBlocks,
  subTaskBlocks,
  disabled,
  copyContent
}: {
  contentBlocks: BlockDefinition<ContentTemplate | LayoutBlock>[];
  subTaskBlocks: BlockDefinition<SubTaskTemplate>[];
  disabled?: boolean;
  copyContent?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  // Temporary disabling of Columns for MS orgs
  const { active } = useIntegrationsContext();
  const activeContentBlocks = useMemo(() => {
    if (active.has(Integration.MICROSOFT)) {
      return contentBlocks.filter((b) => b.type !== LayoutBlockType.COLUMNS);
    }
    return contentBlocks;
  }, [active, contentBlocks]);
  return (
    <SideContainer>
      {!!contentBlocks.length && (
        <Droppable
          droppableId={DROP_IDS.blockSelectorContent}
          isDropDisabled={true}
        >
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <BlockList
                heading={t('Content')}
                blocks={activeContentBlocks}
                disabled={disabled}
                copyContent={copyContent}
              />
            </div>
          )}
        </Droppable>
      )}
      {!!subTaskBlocks.length && (
        <Droppable
          droppableId={DROP_IDS.blockSelectorSubTasks}
          isDropDisabled={true}
        >
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              <BlockList
                heading={t('Sub-Tasks')}
                blocks={subTaskBlocks}
                disabled={disabled}
              />
            </div>
          )}
        </Droppable>
      )}
    </SideContainer>
  );
};

const findTrueInsertionIndex = (
  currentVisibleBlockIds: string[],
  allBlockIds: string[],
  requestedInsertionIndex: number
) => {
  if (currentVisibleBlockIds.length === allBlockIds.length) {
    return requestedInsertionIndex;
  }
  if (!allBlockIds.length) {
    return 0;
  }

  if (requestedInsertionIndex === currentVisibleBlockIds.length) {
    // insert behind the previous item
    const prevItem = currentVisibleBlockIds[requestedInsertionIndex - 1];
    return allBlockIds.indexOf(prevItem) + 1;
  }

  // If the first item is shown, put it at the top
  if (
    requestedInsertionIndex === 0 &&
    currentVisibleBlockIds[0] === allBlockIds[0]
  ) {
    return 0;
  }

  const currentItem = currentVisibleBlockIds[requestedInsertionIndex];
  return allBlockIds.indexOf(currentItem);
};

/**
 * NEW_UX task editor shell (Layout A: icon rail + top bar + inspector + preview canvas).
 * Reskin only — it re-houses the SAME leaf editors (Assignment/Audiences/StartAndDuration/
 * Reminders/BlockSettings/BlockSelector/TaskPreview) and reuses TaskBuilder's own handlers,
 * so no feature is added or removed. Rendered by TaskBuilder when useNewUx().enabled.
 *
 * Field→panel mapping (reskin-time reorganization; same fields, no splits):
 *   Content  = Title + Admin Name + block picker (or the selected block's settings)
 *   Delivery = Recipient/Sender/Channels/CC/BatchMode (the Assignment editor)
 *   Audience = Target Audiences / segments (overview + detail)
 *   Timing   = Start & Deadline / Reminders / Triggered items (internal sub-nav)
 */
type NewUxShellCtx = {
  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  onDelete?: (t: ITaskTemplate) => void;
  onDuplicate?: () => void;
  expTpl: TaskBuilderContext;
  resources: IExperienceTemplateResource[];
  onChangeResources: (r: IExperienceTemplateResource[]) => void;
  validation: TaskBuilderBlockValidationState;
  setValidation: React.Dispatch<SetStateAction<TaskBuilderBlockValidationState>>;
  sendTestTaskButton: React.ReactNode;
  tokens: TokenDefinition[];
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (i: ITimelineItem) => void;
  viewMode: TaskBuilderViewMode;
  setViewMode: (v: TaskBuilderViewMode) => void;
  customUserFields: CustomUserField[];
  contentBlocks: ReturnType<typeof useContentBlocks>;
  subTaskBlocks: ReturnType<typeof useSubTaskBlocks>;
  selectedBlock: TaskBlockTemplate | undefined;
  selectBlock: (id: string | null) => void;
  onChangeBlock: (b: TaskBlockTemplate) => void;
  onDeleteBlocks: (ids: string[]) => void;
  onDuplicateBlock: (id: string) => void;
  onAddBlocks: (
    blocks: TaskBlockTemplate[],
    index: number,
    restrictAudiences?: string[]
  ) => void;
  onPickBlocks?: (ids: string[]) => void;
  denormalizedOrg: IOrganizationDenormalized;
  exampleParticipant: Doc<IUser>;
  anchorFields: ReturnType<typeof useAnchorFields>;
  restrictAllBlockOperations: boolean;
  restrictBlockResorting: boolean;
  dragDropContext: React.MutableRefObject<{ currentlyVisibleBlocks: string[] }>;
  isAIEnabled: boolean;
  aiGeneratorState: 'idle' | 'generating' | 'inserting' | 'error';
  /** Primary Save/Publish action, supplied by the hosting dialog (rendered in the top bar). */
  newUxPrimaryAction?: React.ReactNode;
  /** Toggle deactivate/reactivate from the top-bar overflow menu (edit mode only). */
  newUxOnToggleDeactivate?: () => void;
  /** Close the hosting dialog (the top-bar ✕). */
  onCloseDialog?: () => void;
  notesPanel?: React.ReactNode;
};

type NewUxRail = 'content' | 'delivery' | 'audience' | 'timing' | 'notes';

const NewUxColumn = styled('div')<{ collapsed?: boolean }>`
  width: ${(p) => (p.collapsed ? '0px' : '500px')};
  flex: 0 0 ${(p) => (p.collapsed ? '0px' : '500px')};
  border-right: ${(p) =>
    p.collapsed ? 'none' : `1px solid ${UX.tokens.color.borderSubtle}`};
  opacity: ${(p) => (p.collapsed ? 0 : 1)};
  overflow: hidden;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  transition: width 0.18s ease, opacity 0.15s ease;
`;

const NewUxContentScroll = styled('div')`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${UX.tokens.space(5)};
`;

const TaskEditorNewUxShell = ({ ctx }: { ctx: NewUxShellCtx }) => {
  const { t } = useUnsafeTranslation();
  const [rail, setRail] = useState<NewUxRail>('content');
  // Clicking the active rail icon collapses/expands the inspector (prototype).
  const [railCollapsed, setRailCollapsed] = useState(false);
  // Deactivate/Reactivate is confirmed before it persists (parity with classic).
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const {
    value,
    onChange,
    expTpl,
    tokens,
    viewMode,
    setViewMode,
    customUserFields,
    contentBlocks,
    subTaskBlocks,
    selectedBlock,
    selectBlock,
    triggeredItems,
    onGoToOtherItem
  } = ctx;

  const anchorUserField = getAnchorInterpolationVariable(
    expTpl.anchor,
    ctx.anchorFields
  );
  const hasInvalidInstanceInterpolations =
    value.batchMode === BatchMode.PER_COHORT &&
    hasInstanceSpecificInterpolations(value.title, anchorUserField);
  const titleTokens =
    value.batchMode === BatchMode.PER_COHORT
      ? tokens.filter(
          (tk) =>
            tk.group !== 'Participant' ||
            (anchorUserField && tk.token === `participant.${anchorUserField}`)
        )
      : tokens;

  // Open (and expand) a rail without ever collapsing — used by affordances like the
  // preview's "+ Add segment" tab, which should only ever open the panel.
  const openRail = (next: NewUxRail) => {
    setRailCollapsed(false);
    setRail(next);
    selectBlock(null);
    // Audience uses the classic viewMode overview/detail flow; other panels don't.
    if (next === 'audience') {
      if (viewMode.mode !== 'AUDIENCES_OVERVIEW' && viewMode.mode !== 'AUDIENCES_DETAIL') {
        setViewMode(TASK_BUILDER_VIEW_MODES.audiencesOverview(null));
      }
    } else if (viewMode.mode !== 'SETTINGS_OVERVIEW') {
      setViewMode(TASK_BUILDER_VIEW_MODES.settingsOverview);
    }
  };

  const goRail = (next: NewUxRail) => {
    // Only the rail icons toggle: re-clicking the active (open) rail collapses it.
    // But if a block's settings are showing (selectedBlock set), re-clicking the same
    // rail should back out to that rail's default state instead of collapsing it.
    if (next === rail && !railCollapsed && !selectedBlock) {
      setRailCollapsed(true);
      return;
    }
    openRail(next);
  };

  const [blockFilter, setBlockFilter] = useState<'all' | 'content' | 'subtasks'>('all');
  const isDadoEmployee = useIsCurrentUserDadoEmployee();
  const { active: activeContentIntegrations } = useIntegrationsContext();
  const selectableContentBlocks = useMemo(
    () =>
      contentBlocks.filter(
        (b) =>
          !(
            activeContentIntegrations.has(Integration.MICROSOFT) &&
            b.type === LayoutBlockType.COLUMNS
          )
      ),
    [contentBlocks, activeContentIntegrations]
  );
  const selectableSubTaskBlocks = useMemo(
    () =>
      subTaskBlocks.filter(
        (b) =>
          !PERMANENTLY_UNSELECTABLE_BLOCK_TYPES.includes(b.type) &&
          (isDadoEmployee ||
            !TEMPORARILIY_UNSELECTABLE_BLOCK_TYPES.includes(b.type)) // NOSONAR
      ),
    [subTaskBlocks, isDadoEmployee]
  );
  const pickerBlocks = [
    ...(blockFilter === 'subtasks'
      ? []
      : selectableContentBlocks.map((b) => ({ ...b, kind: 'content' as const }))),
    ...(blockFilter === 'content'
      ? []
      : selectableSubTaskBlocks
          .filter((b) => !b.hidden)
          .map((b) => ({ ...b, kind: 'subtask' as const })))
  ];

  // Click-to-add from the picker (mirrors the drag-add logic in onDragEnd); casts keep
  // the shared guard/shape types happy without importing the denormalized block types.
  const addBlock = (item: {
    emptyBlock: () => TaskBlockTemplate | null;
    disabled?: boolean;
  }) => {
    if (ctx.restrictAllBlockOperations || item.disabled) return;
    const restrictAudiences =
      viewMode.mode === 'AUDIENCES_DETAIL' ? [] : undefined;
    const nb = item.emptyBlock();
    if (!nb) return;
    const insertionI = value.blocks.length;
    if (isLayoutBlock(nb as unknown as Parameters<typeof isLayoutBlock>[0])) {
      const text1 = contentBlocks
        .find((x) => x.key === 'TEXT')!
        .emptyBlock() as IContentTextTemplate;
      const text2 = contentBlocks
        .find((x) => x.key === 'TEXT')!
        .emptyBlock() as IContentTextTemplate;
      const layout = nb as unknown as {
        id: string;
        data: { columns: { blockId: string }[] };
      };
      layout.data.columns[0].blockId = text1.id;
      layout.data.columns[1].blockId = text2.id;
      text1.parentLayoutElement = layout.id;
      text2.parentLayoutElement = layout.id;
      ctx.onAddBlocks([nb, text1, text2], insertionI, restrictAudiences);
    } else {
      ctx.onAddBlocks([nb], insertionI, restrictAudiences);
      // Divider has no settings, so opening its sidebar would just show an empty panel.
      if (nb.type !== ContentType.DIVIDER) {
        selectBlock(nb.id);
      }
    }
  };

  // Inline canvas editing: for the selected TEXT block, render our markdown editor in
  // place (reusing the real editor + template model). Other block types keep panel config.
  const renderInlineEditor = (blockId: string): React.ReactNode => {
    if (!selectedBlock || selectedBlock.id !== blockId) return null;
    const tb = value.blocks.find((b) => b.id === blockId);
    if (!tb || (tb.type !== ContentType.TEXT && tb.type !== ContentType.CALLOUT)) {
      return null;
    }
    return (
      // Stop mousedown/click from reaching the drag handle so the editor is interactive
      // (blocks live inside a react-beautiful-dnd Draggable).
      <div
        // BlockItem wraps block children in `pointerEvents:none` (so a click anywhere
        // on a read-only block selects it); re-enable it for the live editor, and stop
        // mousedown/click from reaching the drag handle / block-select handler. No
        // horizontal padding here — the editor spans the full block width (toolbar
        // full-bleed, text padded inside via the NEW_UX editor styles).
        style={{ pointerEvents: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <MarkdownEditorWithAI
          newUx
          placeholder="Write something…"
          templateId={expTpl.templateId}
          templateName={expTpl.title}
          timelineItemId={value.id}
          timelineItemType="task"
          timelineItemTitle={value.title}
          fullWidth
          autoFocus
          tokens={tokens}
          value={tb.data.t.t}
          onChange={(v: string, wasAIGenerated?: boolean) =>
            ctx.onChangeBlock({
              ...tb,
              ...(wasAIGenerated ? { aiFeatureUsed: true } : {}),
              data: { ...tb.data, t: { ...tb.data.t, t: v } }
            } as TaskBlockTemplate)
          }
          textAlign={tb.data.t.textAlign || 'left'}
          onChangeTextAlign={(nextAlign) =>
            ctx.onChangeBlock({
              ...tb,
              data: { ...tb.data, t: { ...tb.data.t, textAlign: nextAlign } }
            } as TaskBlockTemplate)
          }
          calloutColor={tb.type === ContentType.CALLOUT ? tb.data.color : undefined}
          onChangeCalloutColor={
            tb.type === ContentType.CALLOUT
              ? (nextColor) =>
                  ctx.onChangeBlock({
                    ...tb,
                    data: { ...tb.data, color: nextColor }
                  } as TaskBlockTemplate)
              : undefined
          }
        />
      </div>
    );
  };

  // TEXT and CALLOUT blocks are edited inline in the canvas (renderInlineEditor);
  // every other selected block opens its settings panel (see renderInspector), so
  // Content just renders the composer + block picker.
  const renderContent = () => {
    return (
      <NewUxContentScroll>
        <UX.PanelHeader
          icon={<Type size={17} />}
          title={t('Content')}
          hint={t('Compose the task — title, admin name, and content blocks.')}
        />
        <UX.FieldLabel>{t('Title')}</UX.FieldLabel>
        <TextFieldWithReplacementTokens
          value={value.title}
          fullWidth
          variant="outlined"
          onChange={(nextTitle) => onChange({ ...value, title: nextTitle })}
          error={!value.title || hasInvalidInstanceInterpolations}
          helperText={
            !value.title
              ? t('Please enter a title')
              : hasInvalidInstanceInterpolations
              ? t('You cannot use this variable for tasks sent once per cohort')
              : undefined
          }
          tokens={titleTokens}
          compact
        />
        <UX.FieldHint>{capitalize(t('visible to employees'))}</UX.FieldHint>
        <div style={{ height: 16 }} />
        <UX.FieldLabel>{t('Admin Name')}</UX.FieldLabel>
        <TextFieldWithReplacementTokens
          value={value.adminName || ''}
          fullWidth
          variant="outlined"
          onChange={(nextAdminName) => onChange({ ...value, adminName: nextAdminName })}
          tokens={tokens}
          compact
        />
        <UX.FieldHint>{t('Only visible to administrators')}</UX.FieldHint>

        <UX.SectionLabel>{t('Add block')}</UX.SectionLabel>
        <UX.SegmentedToggleGroup style={{ marginBottom: 12 }}>
          <UX.SegmentedToggleButton active={blockFilter === 'all'} label={t('All')} onClick={() => setBlockFilter('all')} />
          <UX.SegmentedToggleButton active={blockFilter === 'content'} label={t('Content')} onClick={() => setBlockFilter('content')} />
          <UX.SegmentedToggleButton active={blockFilter === 'subtasks'} label={t('Subtasks')} onClick={() => setBlockFilter('subtasks')} />
        </UX.SegmentedToggleGroup>
        <UX.BlockPickerGrid>
          {pickerBlocks.map((b) => {
            const Icon = b.icon as React.ComponentType<{ size?: number }> | undefined;
            const button = (
              <UX.BlockButton
                key={`${b.kind}:${b.key}`}
                icon={Icon ? <Icon size={16} /> : undefined}
                label={b.label}
                disabled={b.disabled}
                onClick={() => addBlock(b)}
              />
            );
            return b.disabled && b.disabledTooltip ? (
              <Tooltip key={`${b.kind}:${b.key}`} title={b.disabledTooltip} placement="top">
                <div>{button}</div>
              </Tooltip>
            ) : (
              button
            );
          })}
        </UX.BlockPickerGrid>
      </NewUxContentScroll>
    );
  };

  const renderInspector = () => {
    // A selected block opens its settings over ANY rail (Content, Delivery, Audience,
    // Timing) — clicking a block in the preview always reveals its config — except
    // TEXT/CALLOUT, which are edited inline in the canvas instead (renderInlineEditor).
    if (
      selectedBlock &&
      selectedBlock.type !== ContentType.TEXT &&
      selectedBlock.type !== ContentType.CALLOUT
    ) {
      return (
        <BlockSettings
          b={selectedBlock}
          blocks={value.blocks}
          onChangeBlock={ctx.onChangeBlock}
          actor={value.actor}
          channels={value.channels}
          resources={ctx.resources}
          onChangeResources={ctx.onChangeResources}
          expTpl={expTpl}
          validation={ctx.validation[selectedBlock.id] || null}
          setValidation={ctx.setValidation}
          onBack={() => selectBlock(null)}
          anchorFields={ctx.anchorFields}
          customFields={customUserFields}
          hideOptionalSubTaskSetting={false}
          tokens={tokens}
          timelineItemId={value.id}
          timelineItemType="task"
          timelineItemTitle={value.title}
          newUx
        />
      );
    }
    if (rail === 'content') return renderContent();
    if (rail === 'delivery') {
      return (
        <TaskSettingsAssignment
          title={t('Delivery')}
          description={t('Select who should receive this task and how it should be delivered.')}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={() => undefined}
          type="TASK"
          tokens={tokens}
          newUx
          newUxSection="delivery"
        />
      );
    }
    if (rail === 'audience') {
      return (
        <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
          <UX.PanelHeader
            icon={<UserCheck size={17} />}
            title={t('Audience & Completion')}
            hint={t('Who receives this and which segments it targets.')}
          />
          <TaskSettingsAssignment
            title={t('Audience')}
            description={''}
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            onBack={() => undefined}
            type="TASK"
            tokens={tokens}
            newUx
            newUxSection="audience"
          />
          <div style={{ height: 24 }} />
          <div
            style={{
              height: 1,
              background: 'var(--border-subtle, #E5E7EB)',
              margin: '4px 0 16px'
            }}
          />
          <TaskSettingsAudiencesOverview
            title={t('Target Audiences')}
            description={t('Show or hide subtasks and content to certain participants based on their profile.')}
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            onBack={() => undefined}
            newUx
            embedded
            previewAudienceId={
              viewMode.mode === 'AUDIENCES_OVERVIEW' ? viewMode.previewAudienceId : null
            }
            onPreview={(audienceId) =>
              setViewMode(TASK_BUILDER_VIEW_MODES.audiencesOverview(audienceId))
            }
            goToDetails={(audience) =>
              setViewMode(TASK_BUILDER_VIEW_MODES.audiencesDetail(audience, false))
            }
            expandedAudienceId={
              viewMode.mode === 'AUDIENCES_DETAIL'
                ? viewMode.editModel.id
                : null
            }
            onToggleExpand={(id) => {
              if (id) {
                const aud = value.audiences.find((a) => a.id === id);
                if (aud) {
                  setViewMode(
                    TASK_BUILDER_VIEW_MODES.audiencesDetail(aud, false)
                  );
                }
              } else {
                setViewMode(TASK_BUILDER_VIEW_MODES.audiencesOverview(null));
              }
            }}
            inlineDetail={
              viewMode.mode === 'AUDIENCES_DETAIL' ? (
                <TaskSettingsAudiencesDetails
                  title={t('Edit Target Audience')}
                  description={''}
                  value={value}
                  onChange={onChange}
                  expTpl={expTpl}
                  model={viewMode.editModel}
                  onUpdateModel={(nextModel) =>
                    setViewMode(
                      TASK_BUILDER_VIEW_MODES.audiencesDetail(
                        nextModel,
                        viewMode.showHiddenBlocks
                      )
                    )
                  }
                  showHiddenBlocks={viewMode.showHiddenBlocks}
                  onToggleHiddenBlocks={() =>
                    setViewMode(
                      TASK_BUILDER_VIEW_MODES.audiencesDetail(
                        viewMode.editModel,
                        !viewMode.showHiddenBlocks
                      )
                    )
                  }
                  onBack={() =>
                    setViewMode(
                      TASK_BUILDER_VIEW_MODES.audiencesOverview(
                        viewMode.editModel.id
                      )
                    )
                  }
                  customUserFields={customUserFields}
                  newUx
                  inline
                />
              ) : undefined
            }
          />
        </div>
      );
    }

    return (
      <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
        <UX.PanelHeader
          icon={<Clock size={17} />}
          title={t('Timing & Triggers')}
          hint={t('When this item fires, its deadline, and completion reminders.')}
        />
        <TaskSettingsStartAndDurationV2
          title={t('Start and Deadline')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={() => undefined}
          triggeredItems={triggeredItems}
          onGoToOtherItem={onGoToOtherItem}
          newUx
        />
        <div
          style={{
            height: 1,
            background: 'var(--border-subtle, #E5E7EB)',
            margin: '4px 0 16px'
          }}
        />
        <TaskSettingsReminders
          title={t('Reminders')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={() => undefined}
          newUx
        />
      </div>
    );
  };

  const actorDescriptors = useActorDescriptorsFromTemplate(expTpl.supportingActors);
  const assigneeActor = actorDescriptors[value.actor];
  const assigneeLabel = assigneeActor?.label || t('Assignee');
  const isLegacyAssigneeColor =
    !!assigneeActor &&
    !ACTOR_COLORS_LIGHT.some((c) => c.hex.toLowerCase() === assigneeActor.color.toLowerCase());

  return (
    <UX.EditorShell>
      <UX.SectionRailContainer>
        <UX.RailButton active={rail === 'content'} title={t('Content')} onClick={() => goRail('content')}>
          <Type size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'delivery'} title={t('Delivery')} onClick={() => goRail('delivery')}>
          <SlidersHorizontal size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'audience'} title={t('Audience & Completion')} onClick={() => goRail('audience')}>
          <UserCheck size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'timing'} title={t('Timing & triggers')} onClick={() => goRail('timing')}>
          <Clock size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'notes'} title={t('Notes')} onClick={() => goRail('notes')}>
          <FilePen size={20} />
        </UX.RailButton>
      </UX.SectionRailContainer>
      <UX.RightColumn>
        <UX.TopBar>
          <div>
            <UX.TopBarCrumb>{expTpl.title}</UX.TopBarCrumb>
            <UX.TopBarTitle>
              {value.title || t('Task')}
              {ctx.newUxOnToggleDeactivate && (
                <UX.ActivePill active={!value.deactivated} />
              )}
            </UX.TopBarTitle>
          </div>
          <UX.TopBarSpacer />
          <UX.TopBarRight>
            <UX.ActorChip
              color={assigneeActor?.color || UX.tokens.color.n300}
              dimmed={isLegacyAssigneeColor}
            >
              {assigneeLabel}
            </UX.ActorChip>
            {ctx.sendTestTaskButton}
            {ctx.newUxPrimaryAction}
            <UX.OverflowMenu
              items={[
                ...(ctx.onDuplicate
                  ? [
                      {
                        label: t('Duplicate'),
                        icon: <Copy size={15} />,
                        onClick: ctx.onDuplicate
                      }
                    ]
                  : []),
                ...(ctx.newUxOnToggleDeactivate
                  ? [
                      {
                        label: value.deactivated
                          ? t('Reactivate')
                          : t('Deactivate'),
                        icon: value.deactivated ? (
                          <PlayCircle size={15} />
                        ) : (
                          <PauseCircle size={15} />
                        ),
                        onClick: () => setDeactivateConfirmOpen(true)
                      }
                    ]
                  : []),
                ...(ctx.onDelete
                  ? [
                      {
                        label: t('Delete'),
                        icon: <Trash2 size={15} />,
                        onClick: () => ctx.onDelete?.(value),
                        danger: true
                      }
                    ]
                  : [])
              ]}
            />
            {ctx.onCloseDialog && (
              <IconButton size="small" aria-label={t('Close')} onClick={ctx.onCloseDialog}>
                <X size={18} />
              </IconButton>
            )}
          </UX.TopBarRight>
        </UX.TopBar>
        <UX.EditorBody>
          {rail === 'notes' ? (
            <NewUxViewerFullWidth>
              <NewUxFullWidthPanel>
                <NewUxFullWidthPanelHead>
                  <UX.PanelHeader icon={<FilePen size={17} />} title={t('Notes')} />
                </NewUxFullWidthPanelHead>
                <NewUxFullWidthPanelBody>{ctx.notesPanel}</NewUxFullWidthPanelBody>
              </NewUxFullWidthPanel>
            </NewUxViewerFullWidth>
          ) : (
            <>
          <NewUxColumn collapsed={railCollapsed}>{renderInspector()}</NewUxColumn>
          <UX.PreviewCanvas onBackgroundClick={() => selectBlock(null)}>
            <div style={{ width: '100%', maxWidth: 640, margin: '0 auto' }}>
              <UX.AudienceTabs
                allLabelEveryone={t('All – sends to everyone')}
                allLabelBlocks={t('All blocks')}
                everyone={{
                  id: getEveryoneAudience(value.audiences).id,
                  label: getAudienceName(getEveryoneAudience(value.audiences), true, t),
                  empty:
                    getEveryoneAudience(value.audiences).blocks.length === 0
                }}
                segments={value.audiences
                  .filter((a) => a.rule !== 'EVERYONE')
                  .map((a) => ({
                    id: a.id,
                    label: a.name || t('Segment'),
                    color: a.color || UX.tokens.color.blue500,
                    empty: a.blocks.length === 0
                  }))}
                activeId={
                  viewMode.mode === 'AUDIENCES_DETAIL'
                    ? viewMode.editModel.id
                    : viewMode.mode === 'AUDIENCES_OVERVIEW'
                    ? viewMode.previewAudienceId
                    : null
                }
                onSelect={(id) => {
                  // Switching the focused audience clears any block selection so the
                  // inspector doesn't linger on a block from the previous segment.
                  selectBlock(null);
                  if (id) {
                    // Focusing a segment from the preview jumps the inspector to the
                    // Audience rail so the nav icon + left panel follow the selection.
                    setRailCollapsed(false);
                    setRail('audience');
                    setViewMode(TASK_BUILDER_VIEW_MODES.audiencesOverview(id));
                  } else {
                    setViewMode(TASK_BUILDER_VIEW_MODES.settingsOverview);
                  }
                }}
                onAdd={() => {
                  // Open the Audience rail with the new-segment form already open.
                  setRailCollapsed(false);
                  setRail('audience');
                  selectBlock(null);
                  setViewMode(
                    TASK_BUILDER_VIEW_MODES.audiencesDetail(
                      {
                        ...makeNewAudienceDraft(
                          value.audiences,
                          nextSegmentColorHex(value.audiences)
                        ),
                        completionCriteria: []
                      },
                      false
                    )
                  );
                }}
                onDelete={(id) => {
                  onChange({
                    ...value,
                    audiences: clearEveryoneNameIfSole(
                      value.audiences.filter((a) => a.id !== id)
                    )
                  });
                  setViewMode(TASK_BUILDER_VIEW_MODES.settingsOverview);
                }}
              />
              <TaskPreview
                task={value}
                expTpl={expTpl}
                org={ctx.denormalizedOrg}
                participant={ctx.exampleParticipant}
                actor={value.actor}
                selectedBlock={selectedBlock?.id ?? null}
                onSelectBlock={ctx.restrictAllBlockOperations ? undefined : selectBlock}
                onDeleteBlocks={ctx.restrictAllBlockOperations ? undefined : ctx.onDeleteBlocks}
                onDuplicateBlock={ctx.onDuplicateBlock}
                onPickBlocks={ctx.onPickBlocks}
                validation={ctx.validation}
                draggable={!ctx.restrictBlockResorting}
                viewMode={viewMode}
                dragDropContext={ctx.dragDropContext}
                tokens={tokens}
                anchorFields={ctx.anchorFields}
                sendTestTaskButton={null}
                aiGeneratorState={ctx.isAIEnabled ? ctx.aiGeneratorState : undefined}
                newUx
                renderInlineEditor={renderInlineEditor}
                assignAudiences={value.audiences}
                onToggleBlockAudience={(blockId, audienceId) =>
                  onChange({
                    ...value,
                    audiences: value.audiences.map((a) =>
                      a.id === audienceId
                        ? {
                            ...a,
                            blocks: a.blocks.includes(blockId)
                              ? a.blocks.filter((x) => x !== blockId)
                              : [...a.blocks, blockId]
                          }
                        : a
                    )
                  })
                }
              />
            </div>
          </UX.PreviewCanvas>
            </>
          )}
        </UX.EditorBody>
      </UX.RightColumn>
      <DeactivateItemConfirmDialog
        open={deactivateConfirmOpen}
        deactivated={value.deactivated}
        onClose={() => setDeactivateConfirmOpen(false)}
        onConfirm={() => ctx.newUxOnToggleDeactivate?.()}
      />
    </UX.EditorShell>
  );
};

export const TaskBuilder = ({
  value,
  onChange,
  onDelete,
  onDuplicate,
  expTpl,
  resources,
  onChangeResources,
  validation,
  setValidation,
  sendTestTaskButton,
  tokens,
  triggeredItems,
  onGoToOtherItem,
  newUxPrimaryAction,
  notesPanel,
  newUxOnToggleDeactivate,
  onCloseDialog
}: {
  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  onDelete?: (t: ITaskTemplate) => void;
  onDuplicate?: () => void;
  expTpl: TaskBuilderContext;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  validation: TaskBuilderBlockValidationState;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  sendTestTaskButton: React.ReactNode;
  tokens: TokenDefinition[];
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  newUxPrimaryAction?: React.ReactNode;
  /** Toggle deactivate/reactivate from the NEW_UX overflow menu (edit mode only). */
  newUxOnToggleDeactivate?: () => void;
  onCloseDialog?: () => void;
  notesPanel?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const { actor, blocks } = value;
  const { org } = useCurrentUser();
  const { customUserFields, id: orgId } = org;
  const orgOverrides = expTpl.orgOverrides;
  const denormalizedOrg = useMemo(() => toDenormalizedOrg(org, orgOverrides), [
    org,
    orgOverrides
  ]);
  const anchorFields = useAnchorFields(org.customUserFields);
  const [viewMode, setViewMode] = useState<TaskBuilderViewMode>(
    TASK_BUILDER_VIEW_MODES.settingsOverview
  );

  const exampleUser = useMemo(() => EXAMPLE_USER(orgId, customUserFields), [
    orgId,
    customUserFields
  ]);

  const exampleParticipant = useMemo(
    () => ({
      id: 'EXAMPLE',
      collection: 'EXAMPLE_USER',
      data:
        actor === PARTICIPANT_ACTOR_KEY
          ? exampleUser
          : EXAMPLE_USER(exampleUser.orgId, customUserFields)
    }),
    [actor, exampleUser, customUserFields]
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId),
    [blocks, selectedBlockId]
  );

  const anchorOptions = useGoogleCalendarEventInvitiationAnchorOptions(
    expTpl.anchor,
    anchorFields
  );

  const newUxEnabled = useNewUx().enabled;

  useEffect(() => {
    const selectedBlockIdIsNotVisibleAnymore =
      selectedBlockId &&
      viewMode.mode === 'AUDIENCES_DETAIL' &&
      !viewMode.editModel.blocks.includes(selectedBlockId);
    // NEW_UX removes the "preview" concept: a focused audience stays fully
    // editable, so we no longer clear the block selection for it. Classic keeps
    // the old preview-is-read-only behaviour.
    const isPreviewMode =
      !newUxEnabled &&
      viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId;
    if (selectedBlockIdIsNotVisibleAnymore || isPreviewMode) {
      setSelectedBlockId(null);
    }
  }, [selectedBlockId, viewMode, newUxEnabled]);

  const contentBlocks = useContentBlocks({ extendedSelection: true });
  const subTaskBlocks = useSubTaskBlocks(resources, onChangeResources);

  const dragDropContext = useRef<{
    currentlyVisibleBlocks: string[];
  }>({
    currentlyVisibleBlocks: []
  });

  const {
    onDeleteBlocks,
    onChangeBlock,
    onChangeBlockOrder,
    onAddBlocks,
    onDuplicateBlock
  } = useMemo(() => {
    const tryToAddNewBlocksToViewModel = (nextBlocks: TaskBlockTemplate[]) => {
      if (viewMode.mode === 'AUDIENCES_DETAIL') {
        setViewMode(
          TASK_BUILDER_VIEW_MODES.audiencesDetail(
            nextBlocks.reduce(
              (m, nextBlock) => {
                return {
                  ...m,
                  blocks: [...m.blocks, nextBlock.id],
                  completionCriteria:
                    isSubTaskTemplateBlock(nextBlock) &&
                    nextBlock.requirementType === RequirementType.MANDATORY
                      ? m.completionCriteria.map((ccc) => {
                          if (ccc.type === 'SUBSET') {
                            // When adding a new subtask, assume it needs to be done, when a completion criterium is on.
                            // If we don't want this behavior, don't update mininumRequired
                            return {
                              ...ccc,
                              minimumRequired: ccc.minimumRequired + 1
                            };
                          }
                          return ccc;
                        })
                      : m.completionCriteria
                };
              },
              { ...viewMode.editModel }
            ),
            viewMode.showHiddenBlocks
          )
        );
      }
    };
    const tryToRemoveBlocksFromViewModel = (removedBlockIds: string[]) => {
      if (viewMode.mode === 'AUDIENCES_DETAIL') {
        const prevBlocks = viewMode.editModel.blocks;
        const nextBlocks = prevBlocks.filter(
          (b) => !removedBlockIds.includes(b)
        );
        const diff = prevBlocks.length - nextBlocks.length;
        setViewMode(
          TASK_BUILDER_VIEW_MODES.audiencesDetail(
            {
              ...viewMode.editModel,
              blocks: nextBlocks,
              completionCriteria: compact(
                viewMode.editModel.completionCriteria.map((ccc) => {
                  if (ccc.type === 'SUBSET') {
                    return {
                      ...ccc,
                      minimumRequired: Math.max(
                        ccc.minimumRequired - diff,
                        nextBlocks.length ? 1 : 0
                      )
                    };
                  }
                  return ccc;
                })
              )
            },
            viewMode.showHiddenBlocks
          )
        );
      }
    };
    const addBlocks = (
      v: ITaskTemplate,
      nextBlocks: TaskBlockTemplate[],
      insertIndex: number,
      onlyAddToTheseAudiences?: string[]
    ) => {
      const _nextBlocks = [...v.blocks];

      const blocksToAdd: TaskBlockTemplate[] = nextBlocks.map((nextBlock) => {
        if (nextBlock.type === SubTaskType.CREATE_MEETING) {
          if (anchorOptions.find((opt) => opt.type === 'AFTER_MILESTONE')) {
            return {
              ...nextBlock,
              data: {
                ...nextBlock.data,
                anchor: {
                  type: 'AFTER_MILESTONE',
                  userField: '',
                  lowerBoundary: 0
                }
              }
            };
          } else if (
            anchorOptions.find(
              (opt) => opt.type === 'AFTER_EXPERIENCE_START_DATE'
            )
          ) {
            return {
              ...nextBlock,
              data: {
                ...nextBlock.data,
                anchor: {
                  type: 'AFTER_EXPERIENCE_START_DATE',
                  lowerBoundary: 0
                }
              }
            };
          }
        }
        return nextBlock;
      });

      blocksToAdd.forEach((blockToAdd) => {
        _nextBlocks.splice(insertIndex, 0, blockToAdd);
      });

      tryToAddNewBlocksToViewModel(blocksToAdd);
      return blocksToAdd.reduce(
        (m, blockToAdd) => {
          return {
            ...m,
            blocks: _nextBlocks,
            audiences: m.audiences.map((a) => {
              if (
                onlyAddToTheseAudiences &&
                !onlyAddToTheseAudiences.includes(a.id)
              ) {
                return a;
              }
              console.log('block update');
              // `completionCriteria` is guaranteed in the FE-built shape but
              // AI-created drafts from before the writer was patched ship
              // audiences without it; coerce so adding the first block here
              // doesn't crash TaskEditDialog (see issue 2026-05-13).
              const existingCriteria = a.completionCriteria ?? [];
              return {
                ...a,
                blocks: [...a.blocks, blockToAdd.id],
                completionCriteria: isSubTaskTemplateBlock(blockToAdd)
                  ? existingCriteria.map((ccc) => {
                      if (
                        ccc.type === 'SUBSET' &&
                        isSubTaskTemplateBlock(blockToAdd) &&
                        blockToAdd.requirementType === RequirementType.MANDATORY
                      ) {
                        // When adding a new subtask, assume it needs to be done, when a completion criterium is on.
                        // If we don't want this behavior, don't update mininumRequired
                        return {
                          ...ccc,
                          minimumRequired: ccc.minimumRequired + 1
                        };
                      }
                      return ccc;
                    })
                  : existingCriteria
              };
            }),
            customCompletionCriteria: isSubTaskTemplateBlock(blockToAdd)
              ? m.customCompletionCriteria.map((ccc) => {
                  if (ccc.type === 'SUBSET') {
                    // When adding a new subtask, assume it needs to be done, when a completion criterium is on.
                    // If we don't want this behavior, don't update mininumRequired
                    return {
                      ...ccc,
                      minimumRequired: ccc.minimumRequired + 1
                    };
                  }
                  return ccc;
                })
              : m.customCompletionCriteria
          };
        },
        { ...v }
      );
    };

    // const getBlocksForLayoutelement = (
    //   layoutBlock: TaskBlockTemplate,
    //   children: TaskBlockTemplate[],
    //   task: ITaskTemplate
    // ): TaskBlockTemplate[] => {
    //   // v.blocks.map((b) =>{
    //   //   if(b.id)
    //   // } (b.id === nextBlock.id ? nextBlock : b));
    //   return [];
    // };
    return {
      onDeleteBlocks: (blockIds: string[]) => {
        tryToRemoveBlocksFromViewModel(blockIds);
        onChange((v) => ({
          ...v,
          blocks: v.blocks.filter((b) => !blockIds.includes(b.id)),
          audiences: blockIds.reduce((as, blockId) => {
            return as.map((a) => {
              const prevBlocks = a.blocks;
              const nextBlocks = prevBlocks.filter((b) => b !== blockId);
              const removedBlock = v.blocks.find((b) => b.id === blockId);
              const diff =
                removedBlock &&
                isSubTaskTemplateBlock(removedBlock) &&
                removedBlock.requirementType === RequirementType.MANDATORY
                  ? 1
                  : 0;
              return {
                ...a,
                blocks: nextBlocks,
                completionCriteria: compact(
                  a.completionCriteria.map((ccc) => {
                    if (ccc.type === 'SUBSET') {
                      return {
                        ...ccc,
                        minimumRequired: Math.max(
                          ccc.minimumRequired - diff,
                          nextBlocks.length ? 1 : 0
                        )
                      };
                    }
                    return ccc;
                  })
                )
              };
            });
          }, v.audiences),
          customCompletionCriteria: compact(
            v.customCompletionCriteria.map((ccc) => {
              if (ccc.type === 'SUBSET') {
                return {
                  ...ccc
                };
              }
              return ccc;
            })
          )
        }));
      },
      onChangeBlockOrder: (sortedBlockIds: string[]) =>
        onChange((v) => ({
          ...v,
          blocks: sortBy(v.blocks, (b) => sortedBlockIds.indexOf(b.id))
        })),

      onChangeBlock: (nextBlock: TaskBlockTemplate) =>
        onChange((v) => {
          const prevBlock = v.blocks.find((b) => b.id === nextBlock.id);
          const viewModelChanges: {
            audienceId: string;
            minimumRequired: number;
          }[] = [];
          const audiences = v.audiences.map((a) => {
            return {
              ...a,
              completionCriteria:
                isSubTaskTemplateBlock(nextBlock) &&
                a.blocks.includes(nextBlock.id)
                  ? a.completionCriteria.map((ccc) => {
                      if (
                        ccc.type === 'SUBSET' &&
                        isSubTaskTemplateBlock(nextBlock) &&
                        prevBlock &&
                        isSubTaskTemplateBlock(prevBlock) &&
                        prevBlock.requirementType !== nextBlock.requirementType
                      ) {
                        let minimumRequired = ccc.minimumRequired;
                        if (
                          nextBlock.requirementType ===
                          RequirementType.MANDATORY
                        ) {
                          minimumRequired++;
                          viewModelChanges.push({
                            audienceId: a.id,
                            minimumRequired: 1
                          });
                        } else if (
                          nextBlock.requirementType === RequirementType.OPTIONAL
                        ) {
                          minimumRequired--;
                          viewModelChanges.push({
                            audienceId: a.id,
                            minimumRequired: -1
                          });
                        }
                        return {
                          ...ccc,
                          minimumRequired: Math.max(0, minimumRequired)
                        };
                      }
                      return ccc;
                    })
                  : a.completionCriteria
            };
          });
          if (viewMode.mode === 'AUDIENCES_DETAIL') {
            viewModelChanges.forEach((ch) => {
              if (viewMode.editModel.id === ch.audienceId) {
                setViewMode(
                  TASK_BUILDER_VIEW_MODES.audiencesDetail(
                    {
                      ...viewMode.editModel,
                      completionCriteria: viewMode.editModel.completionCriteria.map(
                        (x) => ({
                          ...x,
                          minimumRequired: Math.max(
                            0,
                            x.minimumRequired + ch.minimumRequired
                          )
                        })
                      )
                    },
                    viewMode.showHiddenBlocks
                  )
                );
              }
            });
          }
          return {
            ...v,
            blocks: v.blocks.map((b) =>
              b.id === nextBlock.id ? nextBlock : b
            ),
            audiences
          };
        }),
      onAddBlocks: (
        nextBlocks: TaskBlockTemplate[],
        insertIndex: number,
        onlyAddToTheseAudiences?: string[]
      ) =>
        onChange((v) => {
          console.log(
            'ADDING BLOCKS',
            v,
            nextBlocks,
            insertIndex,
            onlyAddToTheseAudiences
          );
          const x = addBlocks(
            v,
            nextBlocks,
            insertIndex,
            onlyAddToTheseAudiences
          );

          console.log('Block result', x);

          return x;
        }),
      onDuplicateBlock: (blockId: string) => {
        onChange((v) => {
          const i = v.blocks.findIndex((b) => b.id === blockId);
          if (i === -1) {
            return v;
          }

          const createDuplicateBlock = (
            blockToDuplicate: TaskBlockTemplate
          ) => {
            const duplicateBlock = cloneDeep(blockToDuplicate);

            const mutablyCleanDataDestinationFields = (
              s: SubTaskDataDestination
            ) => {
              switch (s.type) {
                case ExternalServiceType.GOOGLE_SHEET:
                  s.columnName = '';

                  return;
                case ExternalServiceType.MS_EXCEL:
                  s.columnName = '';
                  return;
                case ExternalServiceType.BAMBOO_HR:
                  s.field = null;
                  return;
                case InternalStorageInstructionType.USER_PROFILE:
                  s.field = '';
                  return;
                case InternalStorageInstructionType.SUPPORTING_ACTOR:
                  //s.supportingActorToAssign = '';
                  return;
              }
            };
            if (isSubTaskTemplateBlock(duplicateBlock)) {
              duplicateBlock.hooks.onComplete?.forEach((h) => {
                h.storages.forEach(mutablyCleanDataDestinationFields);
              });
              if (duplicateBlock.type === SubTaskType.QUESTION) {
                duplicateBlock.data.storages.forEach(
                  mutablyCleanDataDestinationFields
                );
              }
              if (duplicateBlock.type === SubTaskType.BACKGROUND_CHECK_INVITE) {
                // BG-check resources are owned 1:1 by their subtask, so the duplicate
                // gets its own cloned resource — otherwise picking a package
                // on one block silently rewrites the other's (main-378).
                const clonedResource = cloneBgCheckResource(
                  duplicateBlock.data.resourceId,
                  resources
                );
                if (clonedResource) {
                  // Register the clone in the template's resources[] — the
                  // backend resolves the package from there by resourceId.
                  onChangeResources([...resources, clonedResource]);
                  // Point the duplicate block at its own resource instead of
                  // sharing the original's.
                  duplicateBlock.data = {
                    ...duplicateBlock.data,
                    resourceId: clonedResource.resourceId
                  };
                }
              }
            }

            duplicateBlock.id = shortid();
            const audiences =
              viewMode.mode === 'AUDIENCES_DETAIL'
                ? [] // only added to the view model within addBlock
                : v.audiences
                    .filter((a) => a.blocks.includes(blockId))
                    .map((a) => a.id);

            return { duplicate: duplicateBlock, audiences };
          };

          const { duplicate, audiences } = createDuplicateBlock(v.blocks[i]);
          //selectBlockIdAndValidate(duplicate.id);
          return addBlocks(v, [duplicate], i + 1, audiences);
        });
      }

      // onLayoutBlockChange : (nextBlock: LayoutBlock, children:TaskBlockTemplate[] )=>{
      //   onChange((v) => ({
      //     ...v,
      //     blocks: v.blocks.map((b) => (b.id === nextBlock.id ? nextBlock : b)),
      //     audiences: v.audiences.map((a) => {
      //       return {
      //         ...a,
      //         completionCriteria:
      //           isSubTaskTemplateBlock(nextBlock) &&
      //           a.blocks.includes(nextBlock.id)
      //             ? a.completionCriteria.map((ccc) => {
      //                 if (
      //                   ccc.type === 'SUBSET' &&
      //                   isSubTaskTemplateBlock(nextBlock)
      //                 ) {
      //                   const nextSubTasks = [...ccc.subTasks];
      //                   let minimumRequired = ccc.minimumRequired;
      //                   if (
      //                     nextBlock.requirementType ===
      //                       RequirementType.MANDATORY &&
      //                     !nextSubTasks.includes(nextBlock.id)
      //                   ) {
      //                     nextSubTasks.push(nextBlock.id);
      //                     minimumRequired++;
      //                   } else if (
      //                     nextBlock.requirementType ===
      //                       RequirementType.OPTIONAL &&
      //                     nextSubTasks.includes(nextBlock.id)
      //                   ) {
      //                     nextSubTasks.splice(
      //                       nextSubTasks.indexOf(nextBlock.id),
      //                       1
      //                     );
      //                     minimumRequired--;
      //                   }
      //                   return {
      //                     ...ccc,
      //                     subTasks: nextSubTasks,
      //                     minimumRequired
      //                   };
      //                 }
      //                 return ccc;
      //               })
      //             : a.completionCriteria
      //       };
      //     })
      //   }))
      // }
    };
  }, [onChange, viewMode, anchorOptions, resources, onChangeResources]);

  // AI Multi-Block Generator hook
  const {
    aiDialogOpen,
    setAiDialogOpen,
    aiGeneratorState,
    aiGeneratorError,
    handleAIGenerate
  } = useBlockBuilderAI(value.blocks.length, onAddBlocks, viewMode);

  const isAIEnabled = useIsOrgAIEnabled(orgId);
  const onPickBlocks = useCallback(
    (blocks: string[]) => {
      if (viewMode.mode !== 'AUDIENCES_DETAIL') {
        return undefined;
      }
      const editModel = { ...viewMode.editModel, blocks };
      setViewMode(
        TASK_BUILDER_VIEW_MODES.audiencesDetail(
          editModel,
          viewMode.showHiddenBlocks
        )
      );
      // NEW_UX: showing/hiding a block is a live change, not something that waits for the
      // segment's Save button (Save only persists name/rule/completion). Apply it to an
      // already-saved segment right away.
      if (newUxEnabled) {
        onChange((v) =>
          v.audiences.some((a) => a.id === editModel.id)
            ? {
                ...v,
                audiences: v.audiences.map((a) =>
                  a.id === editModel.id ? { ...a, blocks } : a
                )
              }
            : v
        );
      }
    },
    [viewMode, newUxEnabled, onChange]
  );

  const selectBlockIdAndValidate = useCallback(
    (blockId: string | null) => {
      if (blockId === null && selectedBlock) {
        setValidation((s) =>
          selectedBlock ? validateBlock(s, selectedBlock, t, expTpl) : s
        );
      }
      setSelectedBlockId(blockId);
    },
    [setValidation, selectedBlock, t, expTpl]
  );

  const deselectBlock = useCallback(() => selectBlockIdAndValidate(null), [
    selectBlockIdAndValidate
  ]);

  // NEW_UX drops the read-only "preview" state — a focused segment tab is editable, so
  // block operations are never restricted there. Classic keeps preview read-only.
  const restrictAllBlockOperations = !!(
    !newUxEnabled &&
    viewMode.mode === 'AUDIENCES_OVERVIEW' &&
    viewMode.previewAudienceId
  );
  const restrictBlockResorting =
    restrictAllBlockOperations ||
    (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks);

  return (
    <DragDropContext
      onDragEnd={(r) => {
        if (r.destination?.droppableId === DROP_IDS.blockOrder) {
          if (r.source.droppableId === DROP_IDS.blockOrder) {
            onChangeBlockOrder(
              arrayMove(
                value.blocks.map((b) => b.id),
                r.source.index,
                r.destination.index
              )
            );
          }

          const insertionI = findTrueInsertionIndex(
            dragDropContext.current.currentlyVisibleBlocks,
            value.blocks.map((b) => b.id),
            r.destination.index
          );

          // While we're previewing an audience, a newly added block
          // is only applied to this particular audience.
          // However, we want a save step in between.
          // So we add it to NO audience, and just update the viewMode.editModel
          const restrictAudiences =
            viewMode.mode === 'AUDIENCES_DETAIL' ? [] : undefined;

          if (r.source.droppableId === DROP_IDS.blockSelectorContent) {
            const newContentBlock =
              contentBlocks
                .find((b) => b.key === r.draggableId)
                ?.emptyBlock() || null;
            if (newContentBlock && isContentTemplateBlock(newContentBlock)) {
              onAddBlocks([newContentBlock], insertionI, restrictAudiences);
              setSelectedBlockId(newContentBlock.id);
            } else if (newContentBlock && isLayoutBlock(newContentBlock)) {
              const text1 = contentBlocks
                .find((b) => b.key === 'TEXT')!
                .emptyBlock() as IContentTextTemplate;
              const text2 = contentBlocks
                .find((b) => b.key === 'TEXT')!
                .emptyBlock() as IContentTextTemplate;
              newContentBlock.data.columns[0].blockId = text1.id;
              newContentBlock.data.columns[1].blockId = text2.id;
              text1.parentLayoutElement = newContentBlock.id;
              text2.parentLayoutElement = newContentBlock.id;
              onAddBlocks(
                [newContentBlock, text1, text2],
                insertionI,
                restrictAudiences
              );
            }
          }
          if (r.source.droppableId === DROP_IDS.blockSelectorSubTasks) {
            const newSubTaskBlock =
              subTaskBlocks
                .find((b) => b.key === r.draggableId)
                ?.emptyBlock() || null;
            if (newSubTaskBlock) {
              onAddBlocks([newSubTaskBlock], insertionI, restrictAudiences);
              setSelectedBlockId(newSubTaskBlock.id);
            }
          }
        }
      }}
    >
      {newUxEnabled ? (
        <TaskEditorNewUxShell
          ctx={{
            value,
            onChange,
            onDelete,
            onDuplicate,
            expTpl,
            resources,
            onChangeResources,
            validation,
            setValidation,
            sendTestTaskButton,
            tokens,
            triggeredItems,
            onGoToOtherItem,
            viewMode,
            setViewMode,
            customUserFields,
            contentBlocks,
            subTaskBlocks,
            selectedBlock,
            selectBlock: selectBlockIdAndValidate,
            onChangeBlock,
            onDeleteBlocks,
            onDuplicateBlock,
            onAddBlocks,
            onPickBlocks,
            denormalizedOrg,
            exampleParticipant,
            anchorFields,
            restrictAllBlockOperations,
            restrictBlockResorting,
            dragDropContext,
            isAIEnabled,
            aiGeneratorState,
            newUxPrimaryAction,
            newUxOnToggleDeactivate,
            onCloseDialog,
            notesPanel
          }}
        />
      ) : (
      <Grid>
        <TaskSettings
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onDelete={onDelete}
          viewMode={viewMode}
          setViewMode={setViewMode}
          customUserFields={customUserFields}
          tokens={tokens}
          triggeredItems={triggeredItems}
          onGoToOtherItem={onGoToOtherItem}
        />
        <TaskPreview
          task={value}
          expTpl={expTpl}
          org={denormalizedOrg}
          participant={exampleParticipant}
          actor={actor}
          selectedBlock={selectedBlockId}
          onSelectBlock={
            restrictAllBlockOperations ? undefined : selectBlockIdAndValidate
          }
          onDeleteBlocks={
            restrictAllBlockOperations ? undefined : onDeleteBlocks
          }
          onDuplicateBlock={onDuplicateBlock}
          onPickBlocks={onPickBlocks}
          validation={validation}
          draggable={!restrictBlockResorting}
          viewMode={viewMode}
          dragDropContext={dragDropContext}
          tokens={tokens}
          anchorFields={anchorFields}
          sendTestTaskButton={sendTestTaskButton}
          aiGeneratorState={isAIEnabled ? aiGeneratorState : undefined}
        />
        {selectedBlock ? (
          <BlockSettings
            b={selectedBlock}
            blocks={value.blocks}
            onChangeBlock={onChangeBlock}
            actor={value.actor}
            channels={value.channels}
            resources={resources}
            onChangeResources={onChangeResources}
            expTpl={expTpl}
            validation={validation[selectedBlock.id] || null}
            setValidation={setValidation}
            onBack={deselectBlock}
            anchorFields={anchorFields}
            customFields={org.customUserFields}
            hideOptionalSubTaskSetting={false}
            tokens={tokens}
            timelineItemId={value.id}
            timelineItemType="task"
            timelineItemTitle={value.title}
          />
        ) : (
          <BlockSelector
            contentBlocks={contentBlocks}
            subTaskBlocks={subTaskBlocks}
            disabled={restrictAllBlockOperations}
          />
        )}
      </Grid>
      )}
      {isAIEnabled && (
        <AIMultiBlockGeneratorDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          onGenerate={handleAIGenerate}
          state={aiGeneratorState}
          error={aiGeneratorError}
        />
      )}
    </DragDropContext>
  );
};

export const TaskBuilderReadOnly = ({
  value,
  expTpl,
  resources,
  validation,
  anchorFields,
  sendTestTaskButton,
  showOnCompleteHookSettings,
  tokens,
  triggeredItems,
  onGoToOtherItem
}: {
  value: ITaskTemplate;
  expTpl: TaskBuilderContext;
  resources: IExperienceTemplateResource[];
  validation: TaskBuilderBlockValidationState;
  anchorFields: AnchorField[];
  sendTestTaskButton: React.ReactNode;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { actor, blocks } = value;
  const { org } = useCurrentUser();
  const { customUserFields, id: orgId } = org;
  const orgOverrides = expTpl.orgOverrides;
  const denormalizedOrg = useMemo(() => toDenormalizedOrg(org, orgOverrides), [
    org,
    orgOverrides
  ]);
  const [viewMode] = useState<TaskBuilderViewMode>(
    TASK_BUILDER_VIEW_MODES.settingsOverview
  ); // we'll later allow to drill down into audiences and preview them

  const exampleUser = useMemo(() => EXAMPLE_USER(orgId, customUserFields), [
    orgId,
    customUserFields
  ]);

  const exampleParticipant = useMemo(
    () => ({
      id: 'EXAMPLE',
      collection: 'EXAMPLE_USER',
      data:
        actor === PARTICIPANT_ACTOR_KEY
          ? exampleUser
          : EXAMPLE_USER(exampleUser.orgId, customUserFields)
    }),
    [actor, exampleUser, customUserFields]
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );
  const deselectBlock = useCallback(() => setSelectedBlockId(null), [
    setSelectedBlockId
  ]);

  // DragDropContext required because some inner components rely on it
  // It's all disabled though
  return (
    <DragDropContext onDragEnd={NOOP}>
      <Grid>
        <TaskSettingsOverview
          value={value}
          expTpl={expTpl}
          customFields={customUserFields}
          tokens={tokens}
          triggeredItems={triggeredItems}
          onGoToOtherItem={onGoToOtherItem}
        />
        <TaskPreview
          task={value}
          expTpl={expTpl}
          org={denormalizedOrg}
          participant={exampleParticipant}
          actor={actor}
          selectedBlock={selectedBlockId}
          onSelectBlock={setSelectedBlockId}
          validation={validation}
          draggable={false}
          viewMode={viewMode}
          tokens={tokens}
          anchorFields={anchorFields}
          sendTestTaskButton={sendTestTaskButton}
        />
        {selectedBlock ? (
          <BlockSettingsReadOnly
            b={selectedBlock}
            resources={resources}
            expTpl={expTpl}
            anchorFields={anchorFields}
            validation={
              selectedBlock ? validation[selectedBlock.id] || null : null
            }
            tokens={tokens}
            onBack={deselectBlock}
            customFields={customUserFields}
            showOnCompleteHookSettings={showOnCompleteHookSettings}
          />
        ) : (
          <SideSettingsReadOnlyNoSelection>
            {t('Select a block to view details')}
          </SideSettingsReadOnlyNoSelection>
        )}
      </Grid>
    </DragDropContext>
  );
};

/* ============================ NEW_UX task VIEWER ============================ */

type NewUxViewerRail =
  | 'content'
  | 'delivery'
  | 'audience'
  | 'timing'
  | 'recipients'
  | 'notes';

/** Full-bleed content area (Recipients table / Notes log) — replaces the
 * inspector+preview split on those rails, matching the mockups. Mirrors the
 * classic dialog's TabPanel Container (the virtualized recipients table needs
 * height:100%, not an overflow wrapper); each child manages its own scroll. */
export const NewUxViewerFullWidth = styled('div')`
  display: flex;
  flex: 1 1 auto;
  height: 100%;
  width: 100%;
  min-width: 0;
`;

/** Column wrapper for a full-width rail (Recipients/Notes): a PanelHeader-style
 * title row on top, then the panel's own content filling the rest. */
export const NewUxFullWidthPanel = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-width: 0;
`;
export const NewUxFullWidthPanelHead = styled('div')`
  padding: 20px 20px 0;
  flex: none;
`;
export const NewUxFullWidthPanelBody = styled('div')`
  flex: 1 1 auto;
  min-height: 0;
`;

/** A read-only "label + value" row in a viewer inspector panel. */
export const ViewerField: React.FC<{
  label: string;
  labelHint?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, labelHint, children }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <UX.FieldLabel style={{ marginBottom: 0 }}>{label}</UX.FieldLabel>
      {labelHint && (
        <span
          style={{
            fontSize: UX.tokens.font.variant.h6.fontSize,
            fontWeight: UX.tokens.font.variant.caption.fontWeight,
            color: UX.tokens.color.grey500
          }}
        >
          {labelHint}
        </span>
      )}
    </div>
    <div
      style={{
        color: UX.tokens.color.grey700,
        fontSize: 16,
        fontWeight: UX.tokens.font.weightScale.regular
      }}
    >
      {children}
    </div>
  </div>
);

/** The "empty value" glyph shared across viewer fields. */
const DASH = <span style={{ color: UX.tokens.color.n500 }}>—</span>;

/** Channel-chip icon — mirrors the add/edit Delivery panel (Assignment.tsx). */
const renderViewerChannelIcon = (c: ScheduleChannel): React.ReactNode => (
  <ChannelIcon channel={c} size={16} />
);

/** Read-only "Channels" field: the same chips + icons as the add/edit Delivery
 * panel, but non-interactive. Shared by the task + message viewers. */
const ViewerChannels: React.FC<{ channels: ScheduleChannel[] }> = ({
  channels
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <UX.FieldLabel>{t('Channels')}</UX.FieldLabel>
      {channels.length === 0 ? (
        <div style={{ marginBottom: 16 }}>{DASH}</div>
      ) : (
        <UX.ChipRow style={{ marginBottom: 16, pointerEvents: 'none' }}>
          {channels.map((c) => {
            const key = channelToKey(c);
            const label = channelToString(c, t);
            if (!key || !label) return null;
            return (
              <UX.ChannelChip
                key={key}
                icon={renderViewerChannelIcon(c)}
                label={label}
                selected
                onToggle={NOOP}
              />
            );
          })}
        </UX.ChipRow>
      )}
    </>
  );
};

// A segment's rule text swaps to a "N filter rules" count past this length —
// approximates "would wrap to more than 3 lines" for this card's text column
// (~440px at the caption font's ~14px/60 chars-per-line). Approximate by
// design (real wrapping depends on word lengths, not char count) — nudge
// this if the swap-over point looks off against the actual 3-line wrap.
const SEGMENT_RULE_COLLAPSE_CHAR_THRESHOLD = 180;

/** Read-only "Segments" list: add/edit-style cards (color swatch + name) with
 * the Everyone / Everyone-Else catch-all last. Clicking a segment previews it.
 * Shared by the task, message, and automation viewers. `taskValue` (task only)
 * unlocks the per-segment subtask completion-criteria caption; `ruleConfig`
 * turns each segment's rule tree into readable text (e.g. "Department is any
 * of Engineering"). */
export const ViewerSegmentList: React.FC<{
  audiences: (Audience | AudienceWithCompletionCriteria)[];
  previewAudienceId: string | null;
  onPreview: (id: string) => void;
  itemTypeLabel: string;
  ruleConfig: RuleBuilderConfig<UserField>;
  taskValue?: ITaskTemplate;
}> = ({
  audiences,
  previewAudienceId,
  onPreview,
  itemTypeLabel,
  ruleConfig,
  taskValue
}) => {
  const { t } = useUnsafeTranslation();
  const segments = audiences.filter((a) => a.rule !== 'EVERYONE');
  const rows = [
    ...segments.map((audience) => ({ audience, isEveryone: false })),
    ...audiences
      .filter((a) => a.rule === 'EVERYONE')
      .map((audience) => ({ audience, isEveryone: true }))
  ];
  return (
    <>
      <UX.FieldLabel>{t('Segments')}</UX.FieldLabel>
      {rows.map(({ audience: a, isEveryone }) => {
        const selected = previewAudienceId === a.id;
        const willNotReceive = a.blocks.length === 0;
        // Everyone swatch mirrors its preview tab: green while it's the only
        // audience, black once segments exist ("Everyone Else"); segments keep
        // their own color. Same logic as the add/edit overview rows.
        const swatchColor = isEveryone
          ? segments.length > 0
            ? UX.tokens.color.black
            : UX.tokens.color.green
          : a.color || UX.tokens.color.blue500;
        const name = isEveryone
          ? getAudienceName(a, segments.length > 0, t)
          : a.name || t('Segment');
        const ruleText =
          a.rule === 'EVERYONE'
            ? ''
            : formatRuleAsPlainText(a.rule, ruleConfig, t);
        const isLongRule = ruleText.length > SEGMENT_RULE_COLLAPSE_CHAR_THRESHOLD;
        const conditionCount = a.rule === 'EVERYONE' ? 0 : countConditions(a.rule);

        let completionCaption: React.ReactNode = null;
        if (!willNotReceive && taskValue && 'completionCriteria' in a) {
          const audienceSubTasks = filterBlocksForAudience(
            getSubTaskTemplates(taskValue),
            a
          );
          const total = audienceSubTasks.filter(
            (s) => s.requirementType === RequirementType.MANDATORY
          ).length;
          const hasOptional = audienceSubTasks.length > total;
          const required =
            getMininumRequiredCount(a.completionCriteria) || total;
          completionCaption =
            audienceSubTasks.length === 0
              ? null
              : total === 0
              ? t('Only optional subtasks')
              : t('Complete any {{ n }} of {{ total }} required subtasks', {
                  n: required,
                  total
                });
        }

        return (
          <div
            key={a.id}
            role="button"
            onClick={() => onPreview(a.id)}
            style={{
              marginBottom: 8,
              borderRadius: 6,
              background: willNotReceive ? UX.tokens.color.grey100 : '#fff',
              cursor: 'pointer',
              boxShadow: selected
                ? 'inset 0 0 0 1.5px #0061C4'
                : 'inset 0 0 0 1px #DDE0E3'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px'
              }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  flex: 'none',
                  background: swatchColor
                }}
              />
              <span
                style={{
                  flex: 1,
                  fontSize: UX.tokens.font.variant.caption.fontSize,
                  fontWeight: 400,
                  color: '#494F55'
                }}
              >
                {name}
              </span>
              {willNotReceive && (
                <span
                  style={{
                    fontSize: UX.tokens.font.variant.caption.fontSize,
                    fontWeight: UX.tokens.font.variant.caption.fontWeight,
                    color: UX.tokens.color.grey500,
                    fontStyle: 'italic',
                    flex: 'none'
                  }}
                >
                  {t('Will not receive this {{ itemType }}', { itemType: itemTypeLabel })}
                </span>
              )}
            </div>
            {(!isEveryone || segments.length > 0 || completionCaption) && (
              <div
                style={{
                  padding: '0 12px 8px 46px',
                  fontSize: UX.tokens.font.variant.caption.fontSize,
                  fontWeight: UX.tokens.font.variant.caption.fontWeight,
                  color: UX.tokens.color.grey500
                }}
              >
                {(!isEveryone || segments.length > 0) && (
                  <div>
                    {isEveryone ? (
                      t("Everyone who doesn't match above")
                    ) : isLongRule ? (
                      t('{{ count }} filter rules', { count: conditionCount })
                    ) : (
                      <RuleRenderer
                        value={a.rule === 'EVERYONE' ? null : a.rule}
                        config={ruleConfig}
                        variant="oneline"
                      />
                    )}
                  </div>
                )}
                {completionCaption && <div>{completionCaption}</div>}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

/**
 * NEW_UX read-only task viewer (Layout A shell, view mode). Mirrors
 * TaskEditorNewUxShell but renders read-only summaries and adds the Recipients
 * and Notes rails (supplied as nodes by the hosting dialog so this component
 * stays decoupled from pages/Details). Reskin only — it re-houses the SAME
 * read-only content the classic tabbed dialog shows (settings summary +
 * preview + People + Notes), reorganized into the icon rail. Rendered by
 * TaskTemplateReadOnlyDialog when useNewUx().enabled.
 *
 * Field→panel mapping mirrors the editor split:
 *   Content    = Title + Admin Name
 *   Delivery   = Channels + Send frequency + Sender name + Reply-to
 *   Audience   = Assignee + Copies (CC) + Segments (clickable → preview)
 *   Timing     = Start & Deadline + Reminders + Triggered items
 *   Recipients = People execution report (node prop)
 *   Notes      = Timeline-item notes (node prop)
 */
export const TaskViewerNewUxShell = ({
  value,
  expTpl,
  resources,
  validation,
  anchorFields,
  showOnCompleteHookSettings,
  tokens,
  triggeredItems,
  onGoToOtherItem,
  recipientsPanel,
  notesPanel,
  editButton,
  onCloseDialog
}: {
  value: ITaskTemplate;
  expTpl: TaskBuilderContext;
  resources: IExperienceTemplateResource[];
  validation: TaskBuilderBlockValidationState;
  anchorFields: AnchorField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
  triggeredItems: ITimelineItem[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  /** People / execution-report tab, supplied by the hosting dialog. */
  recipientsPanel?: React.ReactNode;
  /** Timeline-item notes tab, supplied by the hosting dialog. */
  notesPanel?: React.ReactNode;
  /** "Edit task" primary action (permission-gated), supplied by the dialog. */
  editButton?: React.ReactNode;
  /** Close the hosting dialog (top-bar ✕). */
  onCloseDialog?: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { actor, blocks } = value;
  const { org } = useCurrentUser();
  const { customUserFields, id: orgId } = org;
  const segmentRuleConfig = useUserRuleConfigWithAutocompletion(
    orgId,
    customUserFields,
    expTpl.tz,
    { orgId, experienceTemplateId: expTpl.templateId }
  );

  const [rail, setRail] = useState<NewUxViewerRail>('content');
  const [railCollapsed, setRailCollapsed] = useState(false);
  // Which segment the preview is filtered to (null = everyone / unfiltered).
  const [previewAudienceId, setPreviewAudienceId] = useState<string | null>(
    null
  );

  const orgOverrides = expTpl.orgOverrides;
  const denormalizedOrg = useMemo(() => toDenormalizedOrg(org, orgOverrides), [
    org,
    orgOverrides
  ]);
  const exampleUser = useMemo(() => EXAMPLE_USER(orgId, customUserFields), [
    orgId,
    customUserFields
  ]);
  const exampleParticipant = useMemo(
    () => ({
      id: 'EXAMPLE',
      collection: 'EXAMPLE_USER',
      data:
        actor === PARTICIPANT_ACTOR_KEY
          ? exampleUser
          : EXAMPLE_USER(exampleUser.orgId, customUserFields)
    }),
    [actor, exampleUser, customUserFields]
  );

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );

  const viewMode: TaskBuilderViewMode = previewAudienceId
    ? TASK_BUILDER_VIEW_MODES.audiencesOverview(previewAudienceId)
    : TASK_BUILDER_VIEW_MODES.settingsOverview;

  const actorDescriptors = useActorDescriptorsFromTemplate(
    expTpl.supportingActors
  );
  const assigneeActor = actorDescriptors[value.actor];
  const assigneeLabel = assigneeActor?.label || t('Assignee');
  const isLegacyAssigneeColor =
    !!assigneeActor &&
    !ACTOR_COLORS_LIGHT.some(
      (c) => c.hex.toLowerCase() === assigneeActor.color.toLowerCase()
    );

  // CC ("Send a copy to") labels — same computation as the classic overview.
  const ccLabels = useMemo(() => {
    const rc = value.receivesCopy;
    if (!rc) return [] as string[];
    const roleLabels = (rc.roles as Array<
      string | { key: string; emailField: string }
    >).map((r) =>
      actionActorToString(
        {
          type: 'actor',
          key: typeof r === 'string' ? r : r.key,
          emailField: typeof r === 'string' ? 'email' : r.emailField,
          optional: false
        },
        expTpl.supportingActors,
        t
      )
    );
    return [...roleLabels, ...rc.emails];
  }, [value.receivesCopy, expTpl.supportingActors, t]);

  const senderName = value.customSender?.name || expTpl.notificationSender.name;
  const replyTo =
    value.customSender?.replyTo || expTpl.notificationSender.replyTo;

  const dash = DASH;

  const segments = value.audiences.filter((a) => a.rule !== 'EVERYONE');

  const openRail = (next: NewUxViewerRail) => {
    setRailCollapsed(false);
    setSelectedBlockId(null);
    setRail(next);
  };
  const goRail = (next: NewUxViewerRail) => {
    if (next === rail && !railCollapsed) {
      setRailCollapsed(true);
      return;
    }
    openRail(next);
  };

  const renderContent = () => (
    <NewUxContentScroll>
      <UX.PanelHeader
        icon={<Type size={17} />}
        title={t('Content')}
      />
      <ViewerField
        label={t('Title')}
        labelHint={capitalize(t('visible to employees'))}
      >
        <PlainTextWithReplacementPlaceholders text={value.title} tokens={tokens} />
      </ViewerField>
      <ViewerField
        label={t('Admin name')}
        labelHint={t('Only visible to administrators')}
      >
        {value.adminName ? (
          <PlainTextWithReplacementPlaceholders
            text={value.adminName}
            tokens={tokens}
          />
        ) : (
          dash
        )}
      </ViewerField>
    </NewUxContentScroll>
  );

  const renderDelivery = () => (
    <NewUxContentScroll>
      <UX.PanelHeader
        icon={<SlidersHorizontal size={17} />}
        title={t('Delivery')}
      />
      <ViewerChannels channels={value.channels} />
      {actor !== PARTICIPANT_ACTOR_KEY && (
        <ViewerField label={t('Send this task')}>
          {value.batchMode === BatchMode.PER_COHORT
            ? t('Once Per Cohort')
            : t('Once Per Participant')}
        </ViewerField>
      )}
      <ViewerField label={t('Sender Name')}>{senderName || dash}</ViewerField>
      <ViewerField label={t('Reply-to Email')}>{replyTo || dash}</ViewerField>
    </NewUxContentScroll>
  );

  const renderAudience = () => (
    <NewUxContentScroll>
      <UX.PanelHeader
        icon={<UserCheck size={17} />}
        title={t('Audience & Completion')}
      />
      <ViewerField label={t('Assignee')}>
        <UX.ActorChip
          color={assigneeActor?.color || UX.tokens.color.n300}
          dimmed={isLegacyAssigneeColor}
        >
          {assigneeLabel}
        </UX.ActorChip>
      </ViewerField>
      {ccLabels.length > 0 && (
        <ViewerField label={t('Send a copy to')}>
          {ccLabels.join(', ')}
        </ViewerField>
      )}
      <ViewerSegmentList
        audiences={value.audiences}
        previewAudienceId={previewAudienceId}
        onPreview={(id) =>
          setPreviewAudienceId((cur) => (cur === id ? null : id))
        }
        itemTypeLabel={t('task')}
        ruleConfig={segmentRuleConfig}
        taskValue={value}
      />
    </NewUxContentScroll>
  );

  const renderTiming = () => (
    <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
      <UX.PanelHeader
        icon={<Clock size={17} />}
        title={t('Timing & Triggers')}
      />
      <TaskViewerStartAndDeadline
        value={value}
        initialUserTimezoneStartTime={getTimelineStartTimeFromTaskBuilderContext(
          expTpl
        )}
        allTasks={expTpl.tasks}
        onGoToOtherItem={onGoToOtherItem}
        ruleConfig={segmentRuleConfig}
        anchor={expTpl.anchor}
        tz={expTpl.tz}
      />
      <TaskViewerReminders reminders={value.remindersV2} />
      {!!triggeredItems.length && (
        <div style={{ marginTop: 16 }}>
          <TaskSettingsOverviewSectionTriggeredItems
            title={t('Triggered Timeline Items')}
            triggeredItems={triggeredItems}
            tplContext={expTpl}
            onGoToOtherItem={onGoToOtherItem}
            hideIcon
          />
        </div>
      )}
    </div>
  );

  const renderInspector = () => {
    // A selected non-TEXT block opens its read-only settings over any rail.
    if (selectedBlock) {
      return (
        <BlockSettingsReadOnly
          b={selectedBlock}
          resources={resources}
          expTpl={expTpl}
          anchorFields={anchorFields}
          validation={validation[selectedBlock.id] || null}
          tokens={tokens}
          onBack={() => setSelectedBlockId(null)}
          customFields={customUserFields}
          showOnCompleteHookSettings={showOnCompleteHookSettings}
        />
      );
    }
    if (rail === 'delivery') return renderDelivery();
    if (rail === 'audience') return renderAudience();
    if (rail === 'timing') return renderTiming();
    return renderContent();
  };

  const isFullWidthRail = rail === 'recipients' || rail === 'notes';

  return (
    <DragDropContext onDragEnd={NOOP}>
      <UX.EditorShell>
        <UX.SectionRailContainer>
          <UX.RailButton
            active={rail === 'content'}
            title={t('Content')}
            onClick={() => goRail('content')}
          >
            <Type size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'delivery'}
            title={t('Delivery')}
            onClick={() => goRail('delivery')}
          >
            <SlidersHorizontal size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'audience'}
            title={t('Audience & Completion')}
            onClick={() => goRail('audience')}
          >
            <UserCheck size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'timing'}
            title={t('Timing & triggers')}
            onClick={() => goRail('timing')}
          >
            <Clock size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'recipients'}
            title={t('Schedule')}
            onClick={() => goRail('recipients')}
          >
            <Calendar size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'notes'}
            title={t('Notes')}
            onClick={() => goRail('notes')}
          >
            <FilePen size={20} />
          </UX.RailButton>
        </UX.SectionRailContainer>
        <UX.RightColumn>
          <UX.TopBar>
            <div>
              <UX.TopBarCrumb>{expTpl.title}</UX.TopBarCrumb>
              <UX.TopBarTitle>{value.title || t('Task')}</UX.TopBarTitle>
            </div>
            <UX.TopBarSpacer />
            <UX.TopBarRight>
              <UX.ActorChip
                color={assigneeActor?.color || UX.tokens.color.n300}
                dimmed={isLegacyAssigneeColor}
              >
                {assigneeLabel}
              </UX.ActorChip>
              {editButton}
              {onCloseDialog && (
                <IconButton
                  size="small"
                  aria-label={t('Close')}
                  onClick={onCloseDialog}
                >
                  <X size={18} />
                </IconButton>
              )}
            </UX.TopBarRight>
          </UX.TopBar>
          <UX.EditorBody>
            {isFullWidthRail ? (
              <NewUxViewerFullWidth>
                <NewUxFullWidthPanel>
                  <NewUxFullWidthPanelHead>
                    <UX.PanelHeader
                      icon={
                        rail === 'recipients' ? (
                          <Calendar size={17} />
                        ) : (
                          <FilePen size={17} />
                        )
                      }
                      title={rail === 'recipients' ? t('Schedule') : t('Notes')}
                    />
                  </NewUxFullWidthPanelHead>
                  <NewUxFullWidthPanelBody>
                    {rail === 'recipients' ? recipientsPanel : notesPanel}
                  </NewUxFullWidthPanelBody>
                </NewUxFullWidthPanel>
              </NewUxViewerFullWidth>
            ) : (
              <>
                <NewUxColumn collapsed={railCollapsed}>
                  {renderInspector()}
                </NewUxColumn>
                <UX.PreviewCanvas onBackgroundClick={() => setSelectedBlockId(null)}>
                  <div style={{ width: '100%', maxWidth: 640, margin: '0 auto' }}>
                    <UX.AudienceTabs
                      allLabelEveryone={t('All – sends to everyone')}
                allLabelBlocks={t('All blocks')}
                      everyone={{
                        id: getEveryoneAudience(value.audiences).id,
                        label: getAudienceName(getEveryoneAudience(value.audiences), true, t),
                        empty:
                          getEveryoneAudience(value.audiences).blocks.length ===
                          0
                      }}
                      segments={segments.map((a) => ({
                        id: a.id,
                        label: a.name || t('Segment'),
                        color: a.color || UX.tokens.color.blue500,
                        empty: a.blocks.length === 0
                      }))}
                      activeId={previewAudienceId}
                      onSelect={(id) => {
                        setSelectedBlockId(null);
                        setPreviewAudienceId(id);
                        if (id) {
                          setRailCollapsed(false);
                          setRail('audience');
                        }
                      }}
                    />
                    <TaskPreview
                      task={value}
                      expTpl={expTpl}
                      org={denormalizedOrg}
                      participant={exampleParticipant}
                      actor={actor}
                      selectedBlock={selectedBlockId}
                      onSelectBlock={setSelectedBlockId}
                      validation={validation}
                      draggable={false}
                      viewMode={viewMode}
                      tokens={tokens}
                      anchorFields={anchorFields}
                      sendTestTaskButton={null}
                      newUx
                      readOnly
                    />
                  </div>
                </UX.PreviewCanvas>
              </>
            )}
          </UX.EditorBody>
        </UX.RightColumn>
      </UX.EditorShell>
    </DragDropContext>
  );
};

/**
 * NEW_UX read-only message viewer (Layout A shell, view mode). Mirrors
 * MessageViewerNewUxShell's sibling TaskViewerNewUxShell but for messages —
 * re-houses the SAME read-only content the classic tabbed message dialog shows
 * (settings summary + preview + People + Notes) into the icon rail. Reskin only.
 * Rendered by the message dialog (Messages/index.tsx) in read mode when
 * useNewUx().enabled.
 *
 * Field→panel mapping mirrors the message editor split:
 *   Content    = Subject + Admin Name
 *   Delivery   = Channels + Send frequency + Sender name + Reply-to
 *   Audience   = Recipient + Copies (CC) + Segments (clickable → preview)
 *   Timing     = Send date (+ catch-up)
 *   Recipients = People execution report (node prop)
 *   Notes      = Timeline-item notes (node prop)
 */
export const MessageViewerNewUxShell = ({
  value,
  expTpl,
  validation,
  tokens,
  connectedTasks,
  recipientsPanel,
  notesPanel,
  editButton,
  onCloseDialog,
  onGoToOtherItem
}: {
  value: ExperienceTemplateMessage;
  expTpl: TaskBuilderContext;
  validation: TaskBuilderBlockValidationState;
  tokens: TokenDefinition[];
  connectedTasks: ITaskTemplate[];
  /** People / execution-report tab, supplied by the hosting dialog. */
  recipientsPanel?: React.ReactNode;
  /** Timeline-item notes tab, supplied by the hosting dialog. */
  notesPanel?: React.ReactNode;
  /** "Edit message" primary action (permission-gated), supplied by the dialog. */
  editButton?: React.ReactNode;
  /** Close the hosting dialog (top-bar ✕). */
  onCloseDialog?: () => void;
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const segmentRuleConfig = useUserRuleConfigWithAutocompletion(
    org.id,
    org.customUserFields,
    expTpl.tz,
    { orgId: org.id, experienceTemplateId: expTpl.templateId }
  );

  const [rail, setRail] = useState<NewUxViewerRail>('content');
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [previewAudienceId, setPreviewAudienceId] = useState<string | null>(
    null
  );
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const selectedBlock = useMemo(
    () => value.blocks.find((b) => b.id === selectedBlockId) || null,
    [value.blocks, selectedBlockId]
  );

  const viewMode: MessageBuilderViewMode = previewAudienceId
    ? MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(previewAudienceId)
    : MESSAGE_BUILDER_VIEW_MODES.settingsOverview;

  const actorDescriptors = useActorDescriptorsFromTemplate(
    expTpl.supportingActors
  );
  const assigneeActor = actorDescriptors[value.actor];
  const assigneeLabel = assigneeActor?.label || t('Recipient');
  const isLegacyAssigneeColor =
    !!assigneeActor &&
    !ACTOR_COLORS_LIGHT.some(
      (c) => c.hex.toLowerCase() === assigneeActor.color.toLowerCase()
    );

  const ccLabels = useMemo(() => {
    const rc = value.receivesCopy;
    if (!rc) return [] as string[];
    const roleLabels = (rc.roles as Array<
      string | { key: string; emailField: string }
    >).map((r) =>
      actionActorToString(
        {
          type: 'actor',
          key: typeof r === 'string' ? r : r.key,
          emailField: typeof r === 'string' ? 'email' : r.emailField,
          optional: false
        },
        expTpl.supportingActors,
        t
      )
    );
    return [...roleLabels, ...rc.emails];
  }, [value.receivesCopy, expTpl.supportingActors, t]);

  const senderName = value.customSender?.name || expTpl.notificationSender.name;
  const replyTo =
    value.customSender?.replyTo || expTpl.notificationSender.replyTo;
  const dash = DASH;
  const segments = value.audiences.filter((a) => a.rule !== 'EVERYONE');

  const openRail = (next: NewUxViewerRail) => {
    setRailCollapsed(false);
    setSelectedBlockId(null);
    setRail(next);
  };
  const goRail = (next: NewUxViewerRail) => {
    if (next === rail && !railCollapsed) {
      setRailCollapsed(true);
      return;
    }
    openRail(next);
  };

  const renderContent = () => (
    <NewUxContentScroll>
      <UX.PanelHeader
        icon={<Type size={17} />}
        title={t('Content')}
      />
      <ViewerField
        label={t('Subject line')}
        labelHint={capitalize(t('visible to employees'))}
      >
        <PlainTextWithReplacementPlaceholders
          text={value.subject}
          tokens={tokens}
        />
      </ViewerField>
      <ViewerField
        label={t('Admin name')}
        labelHint={t('Only visible to administrators')}
      >
        {value.adminName ? (
          <PlainTextWithReplacementPlaceholders
            text={value.adminName}
            tokens={tokens}
          />
        ) : (
          dash
        )}
      </ViewerField>
    </NewUxContentScroll>
  );

  const renderDelivery = () => (
    <NewUxContentScroll>
      <UX.PanelHeader
        icon={<SlidersHorizontal size={17} />}
        title={t('Delivery')}
      />
      <ViewerChannels channels={value.channels} />
      {value.actor !== PARTICIPANT_ACTOR_KEY && (
        <ViewerField label={t('Send this message')}>
          {value.batchMode === BatchMode.PER_COHORT
            ? t('Once Per Cohort')
            : t('Once Per Participant')}
        </ViewerField>
      )}
      <ViewerField label={t('Sender Name')}>{senderName || dash}</ViewerField>
      <ViewerField label={t('Reply-to Email')}>{replyTo || dash}</ViewerField>
    </NewUxContentScroll>
  );

  const renderAudience = () => (
    <NewUxContentScroll>
      <UX.PanelHeader
        icon={<UserCheck size={17} />}
        title={t('Audience & Completion')}
      />
      <ViewerField label={t('Recipient')}>
        <UX.ActorChip
          color={assigneeActor?.color || UX.tokens.color.n300}
          dimmed={isLegacyAssigneeColor}
        >
          {assigneeLabel}
        </UX.ActorChip>
      </ViewerField>
      {ccLabels.length > 0 && (
        <ViewerField label={t('Send a copy to')}>
          {ccLabels.join(', ')}
        </ViewerField>
      )}
      <ViewerSegmentList
        audiences={value.audiences}
        itemTypeLabel={t('message')}
        previewAudienceId={previewAudienceId}
        onPreview={(id) =>
          setPreviewAudienceId((cur) => (cur === id ? null : id))
        }
        ruleConfig={segmentRuleConfig}
      />
    </NewUxContentScroll>
  );

  const renderTiming = () => (
    <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
      <UX.PanelHeader icon={<Calendar size={17} />} title={t('Timing & Triggers')} />
      <MessageViewerSendDate
        value={value}
        allTasks={expTpl.tasks}
        initialUserTimezoneStartTime={getTimelineStartTimeFromTaskBuilderContext(
          expTpl
        )}
        onGoToOtherItem={onGoToOtherItem}
        ruleConfig={segmentRuleConfig}
        anchor={expTpl.anchor}
        tz={expTpl.tz}
      />
    </div>
  );

  const renderInspector = () => {
    if (selectedBlock) {
      return (
        <MessageBlockSettingsReadOnly
          b={selectedBlock}
          validation={validation[selectedBlock.id] || null}
          onBack={() => setSelectedBlockId(null)}
        />
      );
    }
    if (rail === 'delivery') return renderDelivery();
    if (rail === 'audience') return renderAudience();
    if (rail === 'timing') return renderTiming();
    return renderContent();
  };

  const isFullWidthRail = rail === 'recipients' || rail === 'notes';

  return (
    <DragDropContext onDragEnd={NOOP}>
      <UX.EditorShell>
        <UX.SectionRailContainer>
          <UX.RailButton
            active={rail === 'content'}
            title={t('Content')}
            onClick={() => goRail('content')}
          >
            <Type size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'delivery'}
            title={t('Delivery')}
            onClick={() => goRail('delivery')}
          >
            <SlidersHorizontal size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'audience'}
            title={t('Audience & Completion')}
            onClick={() => goRail('audience')}
          >
            <UserCheck size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'timing'}
            title={t('Timing & triggers')}
            onClick={() => goRail('timing')}
          >
            <Clock size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'recipients'}
            title={t('Schedule')}
            onClick={() => goRail('recipients')}
          >
            <Calendar size={20} />
          </UX.RailButton>
          <UX.RailButton
            active={rail === 'notes'}
            title={t('Notes')}
            onClick={() => goRail('notes')}
          >
            <FilePen size={20} />
          </UX.RailButton>
        </UX.SectionRailContainer>
        <UX.RightColumn>
          <UX.TopBar>
            <div>
              <UX.TopBarCrumb>{expTpl.title}</UX.TopBarCrumb>
              <UX.TopBarTitle>{value.subject || t('Message')}</UX.TopBarTitle>
            </div>
            <UX.TopBarSpacer />
            <UX.TopBarRight>
              <UX.ActorChip
                color={assigneeActor?.color || UX.tokens.color.n300}
                dimmed={isLegacyAssigneeColor}
              >
                {assigneeLabel}
              </UX.ActorChip>
              {editButton}
              {onCloseDialog && (
                <IconButton
                  size="small"
                  aria-label={t('Close')}
                  onClick={onCloseDialog}
                >
                  <X size={18} />
                </IconButton>
              )}
            </UX.TopBarRight>
          </UX.TopBar>
          <UX.EditorBody>
            {isFullWidthRail ? (
              <NewUxViewerFullWidth>
                <NewUxFullWidthPanel>
                  <NewUxFullWidthPanelHead>
                    <UX.PanelHeader
                      icon={
                        rail === 'recipients' ? (
                          <Calendar size={17} />
                        ) : (
                          <FilePen size={17} />
                        )
                      }
                      title={rail === 'recipients' ? t('Schedule') : t('Notes')}
                    />
                  </NewUxFullWidthPanelHead>
                  <NewUxFullWidthPanelBody>
                    {rail === 'recipients' ? recipientsPanel : notesPanel}
                  </NewUxFullWidthPanelBody>
                </NewUxFullWidthPanel>
              </NewUxViewerFullWidth>
            ) : (
              <>
                <NewUxColumn collapsed={railCollapsed}>
                  {renderInspector()}
                </NewUxColumn>
                <UX.PreviewCanvas onBackgroundClick={() => setSelectedBlockId(null)}>
                  <div style={{ width: '100%', maxWidth: 640, margin: '0 auto' }}>
                    <UX.AudienceTabs
                      allLabelEveryone={t('All – sends to everyone')}
                allLabelBlocks={t('All blocks')}
                      everyone={{
                        id: getEveryoneAudience(value.audiences).id,
                        label: getAudienceName(getEveryoneAudience(value.audiences), true, t),
                        empty:
                          getEveryoneAudience(value.audiences).blocks.length ===
                          0
                      }}
                      segments={segments.map((a) => ({
                        id: a.id,
                        label: a.name || t('Segment'),
                        color: a.color || UX.tokens.color.blue500,
                        empty: a.blocks.length === 0
                      }))}
                      activeId={previewAudienceId}
                      onSelect={(id) => {
                        setSelectedBlockId(null);
                        setPreviewAudienceId(id);
                        if (id) {
                          setRailCollapsed(false);
                          setRail('audience');
                        }
                      }}
                    />
                    <div onClick={() => setSelectedBlockId(null)}>
                      <ExperienceMessagePreview
                        msg={value}
                        org={org}
                        expTpl={expTpl}
                        selectedBlock={selectedBlockId}
                        onSelectBlock={setSelectedBlockId}
                        validation={validation}
                        draggable={false}
                        viewMode={viewMode}
                        connectedTasks={connectedTasks}
                        tokens={tokens}
                        newUx
                        readOnly
                      />
                    </div>
                  </div>
                </UX.PreviewCanvas>
              </>
            )}
          </UX.EditorBody>
        </UX.RightColumn>
      </UX.EditorShell>
    </DragDropContext>
  );
};

const MessageSettingsOverview = ({
  value,
  onChange,
  expTpl,
  onDelete,
  setViewMode,
  tokens,
  customUserFields
}: {
  value: ExperienceTemplateMessage;
  onChange?: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  onDelete?: ((t: ExperienceTemplateMessage) => void) | undefined;
  expTpl: TaskBuilderContext;
  setViewMode?: (
    nextViewMode: Exclude<MessageBuilderViewMode, ViewModeAudiencesDetail>
  ) => void;
  tokens: TokenDefinition[];
  customUserFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();

  const anchorFields = useAnchorFields(customUserFields);

  const anchorUserField = getAnchorInterpolationVariable(
    expTpl.anchor,
    anchorFields
  );

  const hasInvalidInstanceInterpolations =
    value.batchMode === BatchMode.PER_COHORT &&
    hasInstanceSpecificInterpolations(value.subject, anchorUserField);

  return (
    <SideContainer>
      <SettingsBody>
        <SettingsSection>
          <FormRow>
            <CustomInputLabel
              label={t('Subject')}
              subLabel={capitalize(t('visible to employees'))}
              fullWidth
            >
              {onChange ? (
                <TextFieldWithReplacementTokens
                  value={value.subject}
                  fullWidth
                  variant="outlined"
                  onChange={(nextSubject) =>
                    onChange({
                      ...value,
                      subject: nextSubject
                    })
                  }
                  error={!value.subject || hasInvalidInstanceInterpolations}
                  helperText={
                    !value.subject
                      ? t('Please enter a subject')
                      : hasInvalidInstanceInterpolations
                      ? t(
                          'You cannot use this variable for messages sent once per cohort'
                        )
                      : undefined
                  }
                  autoFocus
                  tokens={
                    value.batchMode === BatchMode.PER_COHORT
                      ? tokens.filter(
                          (t) =>
                            t.group !== 'Participant' ||
                            (anchorUserField &&
                              t.token === `participant.${anchorUserField}`)
                        )
                      : tokens
                  }
                />
              ) : (
                <PlainTextWithReplacementPlaceholders
                  text={value.subject}
                  tokens={tokens}
                />
              )}
            </CustomInputLabel>
          </FormRow>
          {(onChange || value.adminName) && (
            <FormRow>
              <CustomInputLabel
                label={t('Admin Name')}
                subLabel={t('Only visible to administrators')}
                fullWidth
              >
                {onChange ? (
                  <TextFieldWithReplacementTokens
                    value={value.adminName || ''}
                    fullWidth
                    variant="outlined"
                    onChange={(nextAdminName) =>
                      onChange({
                        ...value,
                        adminName: nextAdminName
                      })
                    }
                    tokens={tokens}
                  />
                ) : (
                  <PlainTextWithReplacementPlaceholders
                    text={value.adminName || ''}
                    tokens={tokens}
                  />
                )}
              </CustomInputLabel>
            </FormRow>
          )}
          {onChange && (
            <AIMessageDraft
              context={{
                templateName: expTpl.title,
                recipientActor: value.actor,
              }}
              onDraftGenerated={(content) => {
                onChange((prev) => ({
                  ...prev,
                  subject: content.subject,
                }));
              }}
            />
          )}
        </SettingsSection>
        <TaskSettingsOverviewSectionAssignment
          title={t('Recipient and Sender')}
          assigneeNoun={t('Recipient')}
          actor={value.actor}
          supportingActors={expTpl.supportingActors}
          channels={value.channels}
          customSender={value.customSender}
          receivesCopy={value.receivesCopy}
          batchMode={value.batchMode}
          onEdit={
            setViewMode
              ? () => setViewMode(MESSAGE_BUILDER_VIEW_MODES.recipient)
              : undefined
          }
          divider
        />
        <MessageSettingsOverviewSectionSendDate
          title={t('Send Date')}
          value={value}
          initialUserTimezoneStartTime={getTimelineStartTimeFromTaskBuilderContext(
            expTpl
          )}
          onEdit={
            setViewMode
              ? () => setViewMode(MESSAGE_BUILDER_VIEW_MODES.sendDate)
              : undefined
          }
          allTasks={expTpl.tasks}
          divider
        />
        <TaskSettingsOverviewSectionAudiences
          title={t('Target Audiences')}
          audiences={value.audiences}
          onEdit={
            setViewMode
              ? () =>
                  setViewMode(
                    MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(null)
                  )
              : undefined
          }
          divider
        />
        <MessageSettingsOverviewSectionTaskList
          title={t('Task List')}
          value={value}
          onEdit={
            setViewMode
              ? () => setViewMode(MESSAGE_BUILDER_VIEW_MODES.taskList)
              : undefined
          }
          divider
        />
      </SettingsBody>
      {onDelete && (
        <SettingsFooter>
          <DeleteButton
            variant="outlined"
            color="secondary"
            startIcon={<Trash2 size={20} />}
            dialogTitle={t('Delete Message?')}
            buttonAction={t('Delete Message')}
            onDelete={async () => onDelete(value)}
          >
            {t('Delete Message')}
          </DeleteButton>
        </SettingsFooter>
      )}
    </SideContainer>
  );
};

const MessageSettings = ({
  value,
  onChange,
  onDelete,
  expTpl,
  options,
  viewMode,
  setViewMode,
  customUserFields,
  tokens,
  onGoToOtherItem
}: {
  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  onDelete?: ((t: ExperienceTemplateMessage) => void) | undefined;
  expTpl: TaskBuilderContext;
  options?: {
    disableDateChanges?: boolean;
    disableActorChanges?: boolean;
  };

  viewMode: MessageBuilderViewMode;
  setViewMode: (nextViewMode: MessageBuilderViewMode) => void;
  customUserFields: CustomUserField[];
  tokens: TokenDefinition[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const onBack = useCallback(
    () => setViewMode(MESSAGE_BUILDER_VIEW_MODES.settingsOverview),
    [setViewMode]
  );

  switch (viewMode.mode) {
    case 'SETTINGS_OVERVIEW':
      return (
        <MessageSettingsOverview
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onDelete={onDelete}
          setViewMode={setViewMode}
          tokens={tokens}
          customUserFields={customUserFields}
        />
      );
    case 'RECIPIENT':
      return (
        <TaskSettingsAssignment
          title={t('Recipient and Sender')}
          description={
            'Select who should receive this message and how it should be delivered.'
          }
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          type="MSG"
          tokens={tokens}
        />
      );
    case 'AUDIENCES_OVERVIEW':
      return (
        <MessageSettingsAudiencesOverview
          title={t('Target Audiences')}
          description={t(
            'Show or hide content to certain participants based on their profile.'
          )}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          previewAudienceId={viewMode.previewAudienceId}
          onPreview={(audienceId) =>
            setViewMode(
              MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(audienceId)
            )
          }
          goToDetails={(audience) =>
            setViewMode(
              MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(audience, false)
            )
          }
        />
      );
    case 'AUDIENCES_DETAIL':
      return (
        <MessageSettingsAudiencesDetails
          title={t('Edit Target Audience')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          model={viewMode.editModel}
          onUpdateModel={(nextModel) =>
            setViewMode(
              MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
                nextModel,
                viewMode.showHiddenBlocks
              )
            )
          }
          showHiddenBlocks={viewMode.showHiddenBlocks}
          onToggleHiddenBlocks={() =>
            setViewMode(
              MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
                viewMode.editModel,
                !viewMode.showHiddenBlocks
              )
            )
          }
          onBack={() => {
            return setViewMode(
              TASK_BUILDER_VIEW_MODES.audiencesOverview(viewMode.editModel.id)
            );
          }}
          customUserFields={customUserFields}
        />
      );
    case 'SEND_DATE':
      return (
        <MessageSettingsSendDateV2
          title={t('Send Date')}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
          onGoToOtherItem={onGoToOtherItem}
        />
      );
    case 'TASK_LIST':
      return (
        <MessageSettingsTaskList
          title={t('Task List')}
          description={''}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={onBack}
        />
      );
    default:
      return null;
  }
};

const MessageBlockSettings = ({
  b,
  onChangeBlock,
  validation,
  setValidation,
  expTpl,
  onBack,
  customFields,
  tokens,
  blocks,
  messageId,
  messageTitle,
  newUx = false
}: {
  b: MessageBlock;
  blocks: MessageBlock[];
  onChangeBlock: (nextB: TaskBlockTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  onBack: () => void;
  customFields: CustomUserField[];
  tokens: TokenDefinition[];
  messageId?: string;
  messageTitle?: string;
  newUx?: boolean;
}) => {
  const def = useBlockDefinition(b, EMPTY_ARR, NOOP);
  return (
    <SideContainer>
      <SideContainerHeader def={def} onBack={onBack} newUx={newUx} />
      {isContentTemplateBlock(b) && (
        <ContentBlockSettingsItem
          key={b.id}
          value={b}
          onChange={onChangeBlock}
          validation={validation}
          setValidation={setValidation}
          expTpl={expTpl}
          customFields={customFields}
          tokens={tokens}
          timelineItemId={messageId}
          timelineItemType="message"
          timelineItemTitle={messageTitle}
        />
      )}

      {isLayoutBlock(b) && (
        <LayoutBlockSettingsItem
          key={b.id}
          value={b}
          onChange={onChangeBlock}
          validation={validation}
          setValidation={setValidation}
          expTpl={expTpl}
          customFields={customFields}
          tokens={tokens}
          blocks={blocks}
        />
      )}
    </SideContainer>
  );
};

const MessageBlockSettingsReadOnly = ({
  b,
  validation,
  onBack
}: {
  b: MessageBlock;
  validation: TaskBuilderBlockValidationStateItem | null;
  onBack: () => void;
}) => {
  const def = useBlockDefinition(b, EMPTY_ARR, NOOP);
  return (
    <SideContainer>
      <SideContainerHeader def={def} onBack={onBack} />
      <ContentBlockSettingsItemReadOnly value={b} validation={validation} />
    </SideContainer>
  );
};

const MessagePreviewContainer = styled('div')((p) => ({
  paddingTop: p.theme.spacing(0)
}));

export const getTaskTemplatesWithSameActorAndWithinRange = (
  msg: ExperienceTemplateMessage,
  tpl: Doc<IExperienceTemplate>
) => {
  // this will need to take overrides into account for user based timelines
  const { timing } = msg;
  const { range } = msg.taskList;
  if (range === 0) {
    return EMPTY_ARR;
  }
  // Template-level preview only (this operates on the template's tasks, not a resolved
  // instance) — a trigger-based timing has no absolute date to measure "within N
  // days"/"all future" against, so trigger-condition matching only applies for the
  // "same time" option (range === 1 day); the other two options show nothing for a
  // trigger-based message, same as before this fix. Once an actual experience is
  // running, every item's start has already resolved to a real timestamp, and the
  // instance-level status logic used there handles it correctly without any of this.
  if (isEventDrivenTiming(timing)) {
    if (range !== 1 * ONE_DAY) {
      return EMPTY_ARR;
    }
    return tpl.data.tasks.filter(
      (t) =>
        t.actor === msg.actor &&
        t.batchMode === BatchMode.DEFAULT &&
        templateTimingsShareTriggerCondition(timing, t.timing)
    );
  }
  const allFutureTasksForActorWithDefaultBatchMode = tpl.data.tasks.filter(
    (t) =>
      t.actor === msg.actor &&
      t.timing.type === 'fixed' &&
      timing.start <= t.timing.start &&
      t.batchMode === BatchMode.DEFAULT
  );
  if (range === 'ALL') {
    return allFutureTasksForActorWithDefaultBatchMode;
  }
  return allFutureTasksForActorWithDefaultBatchMode.filter(
    (t) => t.timing.type === 'fixed' && timing.start + range > t.timing.start
  );
};

/**
 * NEW_UX message editor shell — mirrors TaskEditorNewUxShell for messages.
 * Content = Subject + Admin Name + content blocks; Delivery = Channels/Sender/Batch;
 * Audience = Recipient/CC + Segments; Timing = Send timing + Catch-up. Reuses the
 * message leaf editors + MessageBuilder's handlers (reskin only). Gated behind newUx.
 */
type MessageNewUxShellCtx = {
  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<SetStateAction<ExperienceTemplateMessage>>;
  onDelete?: (m: ExperienceTemplateMessage) => void;
  onDuplicate?: () => void;
  expTpl: TaskBuilderContext;
  org: IOrganization;
  tokens: TokenDefinition[];
  validation: TaskBuilderBlockValidationState;
  setValidation: React.Dispatch<SetStateAction<TaskBuilderBlockValidationState>>;
  viewMode: MessageBuilderViewMode;
  setViewMode: (v: MessageBuilderViewMode) => void;
  selectedBlock: MessageBlock | undefined;
  selectBlock: (id: string | null) => void;
  onChangeBlock: (b: MessageBlock) => void;
  onDeleteBlocks: (ids: string[]) => void;
  onDuplicateBlock: (id: string) => void;
  onAddBlocks: (
    blocks: MessageBlock[],
    index: number,
    restrictAudiences?: string[]
  ) => void;
  onPickBlocks?: (ids: string[]) => void;
  contentBlocks: ReturnType<typeof useContentBlocks>;
  connectedTasks: ITaskTemplate[];
  restrictAllBlockOperations: boolean;
  restrictBlockResorting: boolean;
  dragDropContext: React.MutableRefObject<{ currentlyVisibleBlocks: string[] }>;
  isAIEnabled: boolean;
  aiGeneratorState: 'idle' | 'generating' | 'inserting' | 'error';
  sendTestMessageButton: React.ReactNode;
  onGoToOtherItem?: (i: ITimelineItem) => void;
  customUserFields: CustomUserField[];
  newUxPrimaryAction?: React.ReactNode;
  /** Toggle deactivate/reactivate from the top-bar overflow menu (edit mode only). */
  newUxOnToggleDeactivate?: () => void;
  onCloseDialog?: () => void;
  notesPanel?: React.ReactNode;
};

const MessageEditorNewUxShell = ({ ctx }: { ctx: MessageNewUxShellCtx }) => {
  const { t } = useUnsafeTranslation();
  const [rail, setRail] = useState<NewUxRail>('content');
  const [railCollapsed, setRailCollapsed] = useState(false);
  // Deactivate/Reactivate is confirmed before it persists (parity with classic).
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  // Task list isn't a real member of value.blocks (see MessageSettingsTaskListNewUx),
  // so its settings panel is tracked separately rather than through selectBlock/selectedBlock.
  const [showTaskListSettings, setShowTaskListSettings] = useState(false);
  const {
    value,
    onChange,
    expTpl,
    org,
    tokens,
    viewMode,
    setViewMode,
    selectedBlock,
    selectBlock,
    contentBlocks,
    connectedTasks,
    onGoToOtherItem
  } = ctx;
  const anchorFields = useAnchorFields(ctx.customUserFields);

  const anchorUserField = getAnchorInterpolationVariable(
    expTpl.anchor,
    anchorFields
  );
  const hasInvalidInstanceInterpolations =
    value.batchMode === BatchMode.PER_COHORT &&
    hasInstanceSpecificInterpolations(value.subject, anchorUserField);
  const subjectTokens =
    value.batchMode === BatchMode.PER_COHORT
      ? tokens.filter(
          (tk) =>
            tk.group !== 'Participant' ||
            (anchorUserField && tk.token === `participant.${anchorUserField}`)
        )
      : tokens;

  // Open (and expand) a rail without ever collapsing — used by the preview's
  // "+ Add segment" tab, which should only ever open the panel.
  const openRail = (next: NewUxRail) => {
    setRailCollapsed(false);
    setRail(next);
    doSelectBlock(null);
    if (next === 'audience') {
      if (
        viewMode.mode !== 'AUDIENCES_OVERVIEW' &&
        viewMode.mode !== 'AUDIENCES_DETAIL'
      ) {
        setViewMode(MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(null));
      }
    } else if (viewMode.mode !== 'SETTINGS_OVERVIEW') {
      setViewMode(MESSAGE_BUILDER_VIEW_MODES.settingsOverview);
    }
  };

  const goRail = (next: NewUxRail) => {
    // Only the rail icons toggle: re-clicking the active (open) rail collapses it.
    if (next === rail && !railCollapsed) {
      setRailCollapsed(true);
      return;
    }
    openRail(next);
  };

  // Task list isn't a real block, so it isn't reachable through selectBlock/selectedBlock
  // — anywhere this shell would otherwise call selectBlock directly, route it through
  // here instead so opening/closing a real block's settings also closes the task list
  // settings panel (and vice versa), keeping only one settings panel open at a time.
  const doSelectBlock = (id: string | null) => {
    setShowTaskListSettings(false);
    selectBlock(id);
  };

  const addBlock = (item: { emptyBlock: () => MessageBlock | null }) => {
    if (ctx.restrictAllBlockOperations) return;
    const restrictAudiences =
      viewMode.mode === 'AUDIENCES_DETAIL' ? [] : undefined;
    const nb = item.emptyBlock();
    if (!nb) return;
    const insertionI = value.blocks.length;
    if (isLayoutBlock(nb as unknown as Parameters<typeof isLayoutBlock>[0])) {
      const text1 = contentBlocks
        .find((x) => x.key === 'TEXT')!
        .emptyBlock() as IContentTextTemplate;
      const text2 = contentBlocks
        .find((x) => x.key === 'TEXT')!
        .emptyBlock() as IContentTextTemplate;
      const layout = nb as unknown as {
        id: string;
        data: { columns: { blockId: string }[] };
      };
      layout.data.columns[0].blockId = text1.id;
      layout.data.columns[1].blockId = text2.id;
      text1.parentLayoutElement = layout.id;
      text2.parentLayoutElement = layout.id;
      ctx.onAddBlocks(
        [nb, text1 as unknown as MessageBlock, text2 as unknown as MessageBlock],
        insertionI,
        restrictAudiences
      );
    } else {
      ctx.onAddBlocks([nb], insertionI, restrictAudiences);
      doSelectBlock(nb.id);
    }
  };

  const renderInlineEditor = (blockId: string): React.ReactNode => {
    if (!selectedBlock || selectedBlock.id !== blockId) return null;
    const tb = value.blocks.find((b) => b.id === blockId);
    if (!tb || (tb.type !== ContentType.TEXT && tb.type !== ContentType.CALLOUT)) {
      return null;
    }
    return (
      <div
        style={{ pointerEvents: 'auto' }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <MarkdownEditorWithAI
          newUx
          placeholder="Write something…"
          templateId={expTpl.templateId}
          templateName={expTpl.title}
          timelineItemId={value.id}
          timelineItemType="message"
          timelineItemTitle={value.subject}
          fullWidth
          autoFocus
          tokens={tokens}
          value={tb.data.t.t}
          onChange={(v: string, wasAIGenerated?: boolean) =>
            ctx.onChangeBlock({
              ...tb,
              ...(wasAIGenerated ? { aiFeatureUsed: true } : {}),
              data: { ...tb.data, t: { ...tb.data.t, t: v } }
            } as MessageBlock)
          }
          textAlign={tb.data.t.textAlign || 'left'}
          onChangeTextAlign={(nextAlign) =>
            ctx.onChangeBlock({
              ...tb,
              data: { ...tb.data, t: { ...tb.data.t, textAlign: nextAlign } }
            } as MessageBlock)
          }
          calloutColor={tb.type === ContentType.CALLOUT ? tb.data.color : undefined}
          onChangeCalloutColor={
            tb.type === ContentType.CALLOUT
              ? (nextColor) =>
                  ctx.onChangeBlock({
                    ...tb,
                    data: { ...tb.data, color: nextColor }
                  } as MessageBlock)
              : undefined
          }
        />
      </div>
    );
  };

  // TEXT and CALLOUT blocks are edited inline in the canvas; every other selected
  // block opens its settings panel (see renderInspector), so Content just renders
  // the composer.
  const renderContent = () => {
    return (
      <NewUxContentScroll>
        <UX.PanelHeader
          icon={<Type size={17} />}
          title={t('Content')}
          hint={t('Compose the message — subject, admin name, and content blocks.')}
        />
        <UX.FieldLabel>{t('Subject line')}</UX.FieldLabel>
        <TextFieldWithReplacementTokens
          value={value.subject}
          fullWidth
          variant="outlined"
          onChange={(nextSubject) =>
            onChange({ ...value, subject: nextSubject })
          }
          error={!value.subject || hasInvalidInstanceInterpolations}
          helperText={
            !value.subject
              ? t('Please enter a subject')
              : hasInvalidInstanceInterpolations
              ? t('You cannot use this variable for messages sent once per cohort')
              : undefined
          }
          tokens={subjectTokens}
          compact
        />
        <div style={{ height: 16 }} />
        <UX.FieldLabel>{t('Admin Name')}</UX.FieldLabel>
        <TextFieldWithReplacementTokens
          value={value.adminName || ''}
          fullWidth
          variant="outlined"
          onChange={(nextAdminName) =>
            onChange({ ...value, adminName: nextAdminName })
          }
          tokens={tokens}
          compact
        />
        <UX.FieldHint>{t('Only visible to administrators')}</UX.FieldHint>

        <UX.SectionLabel>{t('Add block')}</UX.SectionLabel>
        <UX.BlockPickerGrid>
          {contentBlocks.map((b) => {
            const Icon = b.icon as React.ComponentType<{ size?: number }> | undefined;
            return (
              <UX.BlockButton
                key={b.key}
                icon={Icon ? <Icon size={16} /> : undefined}
                label={b.label}
                onClick={() => addBlock(b)}
              />
            );
          })}
          {/* Task list is a message-only field (taskList.range), not a real member of
              value.blocks — see MessageSettingsTaskListNewUx. */}
          {value.batchMode === BatchMode.DEFAULT && (
            <UX.BlockButton
              icon={<ListChecks size={16} />}
              label={t('Task list')}
              onClick={() => {
                if (!value.taskList.range) {
                  onChange({
                    ...value,
                    taskList: { ...value.taskList, range: 7 * ONE_DAY }
                  });
                }
                selectBlock(null);
                setShowTaskListSettings(true);
              }}
            />
          )}
        </UX.BlockPickerGrid>
      </NewUxContentScroll>
    );
  };

  const renderInspector = () => {
    // Task list isn't a real block (see doSelectBlock/showTaskListSettings above), so
    // it's checked separately, ahead of the real-block check below.
    if (showTaskListSettings) {
      return (
        <MessageSettingsTaskListNewUx
          value={value}
          onChange={onChange}
          onBack={() => setShowTaskListSettings(false)}
        />
      );
    }
    // A selected block opens its settings over ANY rail — clicking a block in the
    // preview always reveals its config — except TEXT/CALLOUT, which are edited
    // inline in the canvas instead (renderInlineEditor).
    if (
      selectedBlock &&
      selectedBlock.type !== ContentType.TEXT &&
      selectedBlock.type !== ContentType.CALLOUT
    ) {
      return (
        <MessageBlockSettings
          b={selectedBlock}
          onChangeBlock={(b: TaskBlockTemplate) =>
            ctx.onChangeBlock(b as MessageBlock)
          }
          blocks={value.blocks}
          validation={ctx.validation[selectedBlock.id] || null}
          setValidation={ctx.setValidation}
          expTpl={expTpl}
          onBack={() => doSelectBlock(null)}
          customFields={ctx.customUserFields}
          tokens={tokens}
          messageId={value.id}
          messageTitle={value.subject}
          newUx
        />
      );
    }
    if (rail === 'content') return renderContent();
    if (rail === 'delivery') {
      return (
        <TaskSettingsAssignment
          title={t('Delivery')}
          description={t('How and from where this message is delivered.')}
          value={value}
          onChange={onChange}
          expTpl={expTpl}
          onBack={() => undefined}
          type="MSG"
          tokens={tokens}
          newUx
          newUxSection="delivery"
        />
      );
    }
    if (rail === 'audience') {
      return (
        <div style={{ overflowY: 'auto', height: '100%', padding: 20 }}>
          <UX.PanelHeader
            icon={<UserCheck size={17} />}
            title={t('Audience & Completion')}
            hint={t('Who receives this message and which segments it targets.')}
          />
          <TaskSettingsAssignment
            title={t('Audience')}
            description={''}
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            onBack={() => undefined}
            type="MSG"
            tokens={tokens}
            newUx
            newUxSection="audience"
          />
          <div style={{ height: 24 }} />
          <div
            style={{
              height: 1,
              background: 'var(--border-subtle, #E5E7EB)',
              margin: '4px 0 16px'
            }}
          />
          <MessageSettingsAudiencesOverview
            title={t('Target Audiences')}
            description={t(
              'Show or hide content to certain participants based on their profile.'
            )}
            value={value}
            onChange={onChange}
            expTpl={expTpl}
            onBack={() => undefined}
            newUx
            embedded
            previewAudienceId={
              viewMode.mode === 'AUDIENCES_OVERVIEW'
                ? viewMode.previewAudienceId
                : null
            }
            onPreview={(audienceId) =>
              setViewMode(
                MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(audienceId)
              )
            }
            goToDetails={(audience) =>
              setViewMode(
                MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(audience, false)
              )
            }
            expandedAudienceId={
              viewMode.mode === 'AUDIENCES_DETAIL'
                ? viewMode.editModel.id
                : null
            }
            onToggleExpand={(id) => {
              if (id) {
                const aud = value.audiences.find((a) => a.id === id);
                if (aud) {
                  setViewMode(
                    MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(aud, false)
                  );
                }
              } else {
                setViewMode(
                  MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(null)
                );
              }
            }}
            inlineDetail={
              viewMode.mode === 'AUDIENCES_DETAIL' ? (
                <MessageSettingsAudiencesDetails
                  title={t('Edit Target Audience')}
                  description={''}
                  value={value}
                  onChange={onChange}
                  expTpl={expTpl}
                  model={viewMode.editModel}
                  onUpdateModel={(nextModel) =>
                    setViewMode(
                      MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
                        nextModel,
                        viewMode.showHiddenBlocks
                      )
                    )
                  }
                  showHiddenBlocks={viewMode.showHiddenBlocks}
                  onToggleHiddenBlocks={() =>
                    setViewMode(
                      MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
                        viewMode.editModel,
                        !viewMode.showHiddenBlocks
                      )
                    )
                  }
                  onBack={() =>
                    setViewMode(
                      MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(
                        viewMode.editModel.id
                      )
                    )
                  }
                  customUserFields={ctx.customUserFields}
                  newUx
                  inline
                />
              ) : undefined
            }
          />
        </div>
      );
    }
    // timing
    return (
      <MessageSettingsSendDateV2
        title={t('Timing')}
        description={''}
        value={value}
        onChange={onChange}
        expTpl={expTpl}
        onBack={() => undefined}
        onGoToOtherItem={onGoToOtherItem}
        newUx
      />
    );
  };

  const actorDescriptors = useActorDescriptorsFromTemplate(expTpl.supportingActors);
  const assigneeActor = actorDescriptors[value.actor];
  const assigneeLabel = assigneeActor?.label || t('Assignee');
  const isLegacyAssigneeColor =
    !!assigneeActor &&
    !ACTOR_COLORS_LIGHT.some((c) => c.hex.toLowerCase() === assigneeActor.color.toLowerCase());

  return (
    <UX.EditorShell>
      <UX.SectionRailContainer>
        <UX.RailButton active={rail === 'content'} title={t('Content')} onClick={() => goRail('content')}>
          <Type size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'delivery'} title={t('Delivery')} onClick={() => goRail('delivery')}>
          <SlidersHorizontal size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'audience'} title={t('Audience & Completion')} onClick={() => goRail('audience')}>
          <UserCheck size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'timing'} title={t('Timing & triggers')} onClick={() => goRail('timing')}>
          <Clock size={20} />
        </UX.RailButton>
        <UX.RailButton active={rail === 'notes'} title={t('Notes')} onClick={() => goRail('notes')}>
          <FilePen size={20} />
        </UX.RailButton>
      </UX.SectionRailContainer>
      <UX.RightColumn>
        <UX.TopBar>
          <div>
            <UX.TopBarCrumb>{expTpl.title}</UX.TopBarCrumb>
            <UX.TopBarTitle>
              {value.subject || t('Message')}
              {ctx.newUxOnToggleDeactivate && (
                <UX.ActivePill active={!value.deactivated} />
              )}
            </UX.TopBarTitle>
          </div>
          <UX.TopBarSpacer />
          <UX.TopBarRight>
            <UX.ActorChip
              color={assigneeActor?.color || UX.tokens.color.n300}
              dimmed={isLegacyAssigneeColor}
            >
              {assigneeLabel}
            </UX.ActorChip>
            {ctx.sendTestMessageButton}
            {ctx.newUxPrimaryAction}
            <UX.OverflowMenu
              items={[
                ...(ctx.onDuplicate
                  ? [
                      {
                        label: t('Duplicate'),
                        icon: <Copy size={15} />,
                        onClick: ctx.onDuplicate
                      }
                    ]
                  : []),
                ...(ctx.newUxOnToggleDeactivate
                  ? [
                      {
                        label: value.deactivated
                          ? t('Reactivate')
                          : t('Deactivate'),
                        icon: value.deactivated ? (
                          <PlayCircle size={15} />
                        ) : (
                          <PauseCircle size={15} />
                        ),
                        onClick: () => setDeactivateConfirmOpen(true)
                      }
                    ]
                  : []),
                ...(ctx.onDelete
                  ? [
                      {
                        label: t('Delete'),
                        icon: <Trash2 size={15} />,
                        onClick: () => ctx.onDelete?.(value),
                        danger: true
                      }
                    ]
                  : [])
              ]}
            />
            {ctx.onCloseDialog && (
              <IconButton size="small" aria-label={t('Close')} onClick={ctx.onCloseDialog}>
                <X size={18} />
              </IconButton>
            )}
          </UX.TopBarRight>
        </UX.TopBar>
        <UX.EditorBody>
          {rail === 'notes' ? (
            <NewUxViewerFullWidth>
              <NewUxFullWidthPanel>
                <NewUxFullWidthPanelHead>
                  <UX.PanelHeader icon={<FilePen size={17} />} title={t('Notes')} />
                </NewUxFullWidthPanelHead>
                <NewUxFullWidthPanelBody>{ctx.notesPanel}</NewUxFullWidthPanelBody>
              </NewUxFullWidthPanel>
            </NewUxViewerFullWidth>
          ) : (
            <>
          <NewUxColumn collapsed={railCollapsed}>{renderInspector()}</NewUxColumn>
          <UX.PreviewCanvas onBackgroundClick={() => doSelectBlock(null)}>
            <div style={{ width: '100%', maxWidth: 640, margin: '0 auto' }}>
              <UX.AudienceTabs
                allLabelEveryone={t('All – sends to everyone')}
                allLabelBlocks={t('All blocks')}
                everyone={{
                  id: getEveryoneAudience(value.audiences).id,
                  label: getAudienceName(getEveryoneAudience(value.audiences), true, t),
                  empty:
                    getEveryoneAudience(value.audiences).blocks.length === 0
                }}
                segments={value.audiences
                  .filter((a) => a.rule !== 'EVERYONE')
                  .map((a) => ({
                    id: a.id,
                    label: a.name || t('Segment'),
                    color: a.color || UX.tokens.color.blue500,
                    empty: a.blocks.length === 0
                  }))}
                activeId={
                  viewMode.mode === 'AUDIENCES_DETAIL'
                    ? viewMode.editModel.id
                    : viewMode.mode === 'AUDIENCES_OVERVIEW'
                    ? viewMode.previewAudienceId
                    : null
                }
                onSelect={(id) => {
                  // Switching the focused audience clears any block selection so the
                  // inspector doesn't linger on a block from the previous segment.
                  doSelectBlock(null);
                  if (id) {
                    // Focusing a segment from the preview jumps the inspector to the
                    // Audience rail so the nav icon + left panel follow the selection.
                    setRailCollapsed(false);
                    setRail('audience');
                    setViewMode(MESSAGE_BUILDER_VIEW_MODES.audiencesOverview(id));
                  } else {
                    setViewMode(MESSAGE_BUILDER_VIEW_MODES.settingsOverview);
                  }
                }}
                onAdd={() => {
                  // Open the Audience rail with the new-segment form already open.
                  setRailCollapsed(false);
                  setRail('audience');
                  doSelectBlock(null);
                  setViewMode(
                    MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
                      makeNewAudienceDraft(
                        value.audiences,
                        nextSegmentColorHex(value.audiences)
                      ),
                      false
                    )
                  );
                }}
                onDelete={(id) => {
                  onChange({
                    ...value,
                    audiences: clearEveryoneNameIfSole(
                      value.audiences.filter((a) => a.id !== id)
                    )
                  });
                  setViewMode(MESSAGE_BUILDER_VIEW_MODES.settingsOverview);
                }}
              />
              <div onClick={() => doSelectBlock(null)}>
                <ExperienceMessagePreview
                  msg={value}
                  org={org}
                  expTpl={expTpl}
                  selectedBlock={selectedBlock?.id ?? null}
                  onSelectBlock={
                    ctx.restrictAllBlockOperations ? undefined : doSelectBlock
                  }
                  onDeleteBlocks={
                    ctx.restrictAllBlockOperations
                      ? undefined
                      : ctx.onDeleteBlocks
                  }
                  onDuplicateBlock={ctx.onDuplicateBlock}
                  validation={ctx.validation}
                  draggable={!ctx.restrictBlockResorting}
                  viewMode={viewMode}
                  dragDropContext={ctx.dragDropContext}
                  onPickBlocks={ctx.onPickBlocks}
                  connectedTasks={connectedTasks}
                  tokens={tokens}
                  newUx
                  renderInlineEditor={renderInlineEditor}
                  onClickTaskList={() => {
                    selectBlock(null);
                    setShowTaskListSettings(true);
                  }}
                  assignAudiences={value.audiences}
                  onToggleBlockAudience={(blockId, audienceId) =>
                    onChange((v) => ({
                      ...v,
                      audiences: v.audiences.map((a) =>
                        a.id === audienceId
                          ? {
                              ...a,
                              blocks: a.blocks.includes(blockId)
                                ? a.blocks.filter((x) => x !== blockId)
                                : [...a.blocks, blockId]
                            }
                          : a
                      )
                    }))
                  }
                />
              </div>
            </div>
          </UX.PreviewCanvas>
            </>
          )}
        </UX.EditorBody>
      </UX.RightColumn>
      <DeactivateItemConfirmDialog
        open={deactivateConfirmOpen}
        deactivated={value.deactivated}
        onClose={() => setDeactivateConfirmOpen(false)}
        onConfirm={() => ctx.newUxOnToggleDeactivate?.()}
      />
    </UX.EditorShell>
  );
};

export const MessageBuilder = ({
  value,
  onChange,
  onDelete,
  options,
  validation,
  setValidation,
  expTpl,
  sendTestMessageButton,
  tokens,
  onGoToOtherItem,
  newUxPrimaryAction,
  onCloseDialog,
  newUxOnDuplicate,
  notesPanel,
  newUxOnToggleDeactivate
}: {
  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<SetStateAction<ExperienceTemplateMessage>>;
  onDelete?: (t: ExperienceTemplateMessage) => void;
  newUxOnDuplicate?: () => void;
  /** Toggle deactivate/reactivate from the NEW_UX overflow menu (edit mode only). */
  newUxOnToggleDeactivate?: () => void;
  options?: {
    disableDateChanges?: boolean;
    disableActorChanges?: boolean;
  };
  validation: TaskBuilderBlockValidationState;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: Doc<IExperienceTemplate>;
  sendTestMessageButton: React.ReactNode;
  tokens: TokenDefinition[];
  onGoToOtherItem?: (otherItem: ITimelineItem) => void;
  newUxPrimaryAction?: React.ReactNode;
  onCloseDialog?: () => void;
  notesPanel?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const { blocks } = value;
  const { org } = useCurrentUser();
  const newUxEnabled = useNewUx().enabled;

  const expTplMini = useTaskBuilderContext(expTpl.data);
  const connectedTasks = useMemo(
    () => getTaskTemplatesWithSameActorAndWithinRange(value, expTpl),
    [expTpl, value]
  );

  const [viewMode, setViewMode] = useState<MessageBuilderViewMode>(
    MESSAGE_BUILDER_VIEW_MODES.settingsOverview
  );

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId),
    [blocks, selectedBlockId]
  );

  useEffect(() => {
    const selectedBlockIdIsNotVisibleAnymore =
      selectedBlockId &&
      viewMode.mode === 'AUDIENCES_DETAIL' &&
      !viewMode.editModel.blocks.includes(selectedBlockId);
    // NEW_UX removes the "preview" concept: a focused audience stays fully
    // editable, so we no longer clear the block selection for it. Classic keeps
    // the old preview-is-read-only behaviour.
    const isPreviewMode =
      !newUxEnabled &&
      viewMode.mode === 'AUDIENCES_OVERVIEW' &&
      viewMode.previewAudienceId;
    if (selectedBlockIdIsNotVisibleAnymore || isPreviewMode) {
      setSelectedBlockId(null);
    }
  }, [selectedBlockId, viewMode, newUxEnabled]);

  const dragDropContext = useRef<{
    currentlyVisibleBlocks: string[];
  }>({
    currentlyVisibleBlocks: []
  });

  const contentBlocks = useContentBlocks({ extendedSelection: true });

  const {
    onDeleteBlocks,
    onChangeBlock,
    onChangeBlockOrder,
    onAddBlocks,
    onDuplicateBlock
  } = useMemo(() => {
    const tryToAddNewBlocksToViewModel = (nextBlockIds: string[]) => {
      if (viewMode.mode === 'AUDIENCES_DETAIL') {
        setViewMode(
          MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
            {
              ...viewMode.editModel,
              blocks: [...viewMode.editModel.blocks, ...nextBlockIds]
            },
            viewMode.showHiddenBlocks
          )
        );
      }
    };

    const tryToRemoveBlocksFromViewModel = (removedBlockIds: string[]) => {
      if (viewMode.mode === 'AUDIENCES_DETAIL') {
        setViewMode(
          MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
            {
              ...viewMode.editModel,
              blocks: viewMode.editModel.blocks.filter(
                (b) => !removedBlockIds.includes(b)
              )
            },
            viewMode.showHiddenBlocks
          )
        );
      }
    };

    const addBlocks = (
      v: ExperienceTemplateMessage,
      nextBlocks: MessageBlock[],
      insertIndex: number,
      onlyAddToTheseAudiences?: string[]
    ) => {
      const _nextBlocks: MessageBlock[] = [...v.blocks];
      const nextBlockIds = nextBlocks.map((nb) => nb.id);
      nextBlocks.forEach((nextBlock) =>
        _nextBlocks.splice(insertIndex, 0, nextBlock)
      );
      tryToAddNewBlocksToViewModel(nextBlockIds);
      return {
        ...v,
        blocks: _nextBlocks,
        audiences: v.audiences.map((a) => {
          if (
            onlyAddToTheseAudiences &&
            !onlyAddToTheseAudiences.includes(a.id)
          ) {
            return a;
          }
          return {
            ...a,
            blocks: [...a.blocks, ...nextBlockIds]
          };
        })
      };
    };
    return {
      onDeleteBlocks: (blockIds: string[]) => {
        tryToRemoveBlocksFromViewModel(blockIds);
        onChange((v) => ({
          ...v,
          blocks: v.blocks.filter((b) => !blockIds.includes(b.id)),
          audiences: v.audiences.map((a) => ({
            ...a,
            blocks: a.blocks.filter((b) => !blockIds.includes(b))
          }))
        }));
      },
      onChangeBlockOrder: (sortedBlockIds: string[]) =>
        onChange((v) => ({
          ...v,
          blocks: sortBy(v.blocks, (b) => sortedBlockIds.indexOf(b.id))
        })),

      onChangeBlock: (nextBlock: MessageBlock) =>
        onChange((v) => ({
          ...v,
          blocks: v.blocks.map((b) => (b.id === nextBlock.id ? nextBlock : b))
        })),
      onAddBlocks: (
        nextBlocks: (ContentTemplate | LayoutBlock)[],
        insertIndex: number,
        onlyAddToTheseAudiences?: string[]
      ) =>
        onChange((v) =>
          addBlocks(v, nextBlocks, insertIndex, onlyAddToTheseAudiences)
        ),
      onDuplicateBlock: (blockId: string) => {
        onChange((v) => {
          const i = v.blocks.findIndex((b) => b.id === blockId);
          if (i === -1) {
            return v;
          }
          // beware that this can cause problems if there are
          // timestamps values in a block (not happening atm)
          const duplicate = cloneDeep(v.blocks[i]);
          duplicate.id = shortid();
          const audiencesForBlock =
            viewMode.mode === 'AUDIENCES_DETAIL'
              ? [] // only added to the view model within addBlock
              : v.audiences
                  .filter((a) => a.blocks.includes(blockId))
                  .map((a) => a.id);
          return addBlocks(v, [duplicate], i + 1, audiencesForBlock);
        });
      }
    };
  }, [onChange, viewMode]);

  const selectBlockIdAndValidate = useCallback(
    (blockId: string | null) => {
      if (blockId === null && selectedBlock) {
        setValidation((s) =>
          selectedBlock ? validateBlock(s, selectedBlock, t, expTplMini) : s
        );
      }
      setSelectedBlockId(blockId);
    },
    [setValidation, selectedBlock, t, expTplMini]
  );

  const deselectBlock = useCallback(() => selectBlockIdAndValidate(null), [
    selectBlockIdAndValidate
  ]);

  // NEW_UX drops the read-only "preview" state — a focused segment tab is editable, so
  // block operations are never restricted there. Classic keeps preview read-only.
  const restrictAllBlockOperations = !!(
    !newUxEnabled &&
    viewMode.mode === 'AUDIENCES_OVERVIEW' &&
    viewMode.previewAudienceId
  );
  const restrictBlockResorting =
    restrictAllBlockOperations ||
    (viewMode.mode === 'AUDIENCES_DETAIL' && !viewMode.showHiddenBlocks);

  const onPickBlocks = useCallback(
    (blocks: string[]) => {
      if (viewMode.mode !== 'AUDIENCES_DETAIL') {
        return undefined;
      }
      const editModel = { ...viewMode.editModel, blocks };
      setViewMode(
        MESSAGE_BUILDER_VIEW_MODES.audiencesDetail(
          editModel,
          viewMode.showHiddenBlocks
        )
      );
      // NEW_UX: showing/hiding a block is a live change, not something that waits for the
      // segment's Save button (Save only persists name/rule/completion). Apply it to an
      // already-saved segment right away.
      if (newUxEnabled) {
        onChange((v) =>
          v.audiences.some((a) => a.id === editModel.id)
            ? {
                ...v,
                audiences: v.audiences.map((a) =>
                  a.id === editModel.id ? { ...a, blocks } : a
                )
              }
            : v
        );
      }
    },
    [viewMode, newUxEnabled, onChange]
  );

  // AI Multi-Block Generator hook
  const {
    aiDialogOpen,
    setAiDialogOpen,
    aiGeneratorState,
    aiGeneratorError,
    handleAIGenerate
  } = useBlockBuilderAI(value.blocks.length, onAddBlocks, viewMode);

  const isAIEnabled = useIsOrgAIEnabled(org.id);

  return (
    <DragDropContext
      onDragEnd={(r) => {
        if (r.destination?.droppableId === DROP_IDS.blockOrder) {
          if (r.source.droppableId === DROP_IDS.blockOrder) {
            onChangeBlockOrder(
              arrayMove(
                value.blocks.map((b) => b.id),
                r.source.index,
                r.destination.index
              )
            );
          }

          const insertionI = findTrueInsertionIndex(
            dragDropContext.current.currentlyVisibleBlocks,
            value.blocks.map((b) => b.id),
            r.destination.index
          );

          // While we're previewing an audience, a newly added block
          // is only applied to this particular audience.
          // However, we want a save step in between.
          // So we add it to NO audience, and just update the viewMode.editModel
          const restrictAudiences =
            viewMode.mode === 'AUDIENCES_DETAIL' ? [] : undefined;

          if (r.source.droppableId === DROP_IDS.blockSelectorContent) {
            const newContentBlock =
              contentBlocks
                .find((b) => b.key === r.draggableId)
                ?.emptyBlock() || null;
            if (newContentBlock && isContentTemplateBlock(newContentBlock)) {
              onAddBlocks([newContentBlock], insertionI, restrictAudiences);
              setSelectedBlockId(newContentBlock.id);
            } else if (newContentBlock && isLayoutBlock(newContentBlock)) {
              const text1 = contentBlocks
                .find((b) => b.key === 'TEXT')!
                .emptyBlock() as IContentTextTemplate;
              const text2 = contentBlocks
                .find((b) => b.key === 'TEXT')!
                .emptyBlock() as IContentTextTemplate;
              newContentBlock.data.columns[0].blockId = text1.id;
              newContentBlock.data.columns[1].blockId = text2.id;
              text1.parentLayoutElement = newContentBlock.id;
              text2.parentLayoutElement = newContentBlock.id;
              onAddBlocks(
                [newContentBlock, text1, text2],
                insertionI,
                restrictAudiences
              );
            }
          }
        }
      }}
    >
      {newUxEnabled ? (
        <MessageEditorNewUxShell
          ctx={{
            value,
            onChange,
            expTpl: expTplMini,
            org,
            tokens,
            validation,
            setValidation,
            viewMode,
            setViewMode,
            selectedBlock,
            selectBlock: selectBlockIdAndValidate,
            onChangeBlock,
            onDeleteBlocks,
            onDuplicateBlock,
            onAddBlocks,
            onPickBlocks,
            contentBlocks,
            connectedTasks,
            restrictAllBlockOperations,
            restrictBlockResorting,
            dragDropContext,
            isAIEnabled,
            aiGeneratorState,
            sendTestMessageButton,
            onGoToOtherItem,
            customUserFields: org.customUserFields,
            newUxPrimaryAction,
            newUxOnToggleDeactivate,
            onCloseDialog,
            onDelete,
            onDuplicate: newUxOnDuplicate,
            notesPanel
          }}
        />
      ) : (
      <Grid>
        <MessageSettings
          value={value}
          onChange={onChange}
          options={options}
          expTpl={expTplMini}
          onDelete={onDelete}
          viewMode={viewMode}
          setViewMode={setViewMode}
          customUserFields={org.customUserFields}
          tokens={tokens}
          onGoToOtherItem={onGoToOtherItem}
        />
        <PreviewContainer>
          <PreviewHeading>
            <Typography variant="h6">Preview</Typography>
            {sendTestMessageButton}
          </PreviewHeading>
          <PreviewBody>
            <MainContainer>
              {isAIEnabled && aiGeneratorState && (
                <AIGeneratorLoadingIndicator state={aiGeneratorState} />
              )}
              <MessagePreviewContainer onClick={deselectBlock}>
                <ExperienceMessagePreview
                  msg={value}
                  org={org}
                  expTpl={expTplMini}
                  selectedBlock={selectedBlockId}
                  onSelectBlock={
                    restrictAllBlockOperations
                      ? undefined
                      : selectBlockIdAndValidate
                  }
                  onDeleteBlocks={
                    restrictAllBlockOperations ? undefined : onDeleteBlocks
                  }
                  onDuplicateBlock={onDuplicateBlock}
                  validation={validation}
                  draggable={!restrictBlockResorting}
                  viewMode={viewMode}
                  dragDropContext={dragDropContext}
                  onPickBlocks={onPickBlocks}
                  connectedTasks={connectedTasks}
                  tokens={tokens}
                />
              </MessagePreviewContainer>
            </MainContainer>
          </PreviewBody>
        </PreviewContainer>
        {selectedBlock ? (
          <MessageBlockSettings
            b={selectedBlock}
            onChangeBlock={(b: TaskBlockTemplate) =>
              onChangeBlock(b as MessageBlock)
            }
            blocks={value.blocks}
            validation={validation[selectedBlock.id] || null}
            setValidation={setValidation}
            expTpl={expTplMini}
            onBack={deselectBlock}
            customFields={org.customUserFields}
            tokens={tokens}
            messageId={value.id}
            messageTitle={value.subject}
          />
        ) : (
          <BlockSelector
            contentBlocks={contentBlocks}
            subTaskBlocks={EMPTY_ARR}
            disabled={restrictAllBlockOperations}
            copyContent={undefined}
          />
        )}
      </Grid>
      )}
      {isAIEnabled && (
        <AIMultiBlockGeneratorDialog
          open={aiDialogOpen}
          onClose={() => setAiDialogOpen(false)}
          onGenerate={handleAIGenerate}
          state={aiGeneratorState}
          error={aiGeneratorError}
        />
      )}
    </DragDropContext>
  );
};

export const MessageBuilderReadOnly = ({
  value,
  validation,
  expTpl,
  sendTestMessageButton,
  tokens
}: {
  value: ExperienceTemplateMessage;
  validation: TaskBuilderBlockValidationState;
  expTpl: Doc<IExperienceTemplate>;
  sendTestMessageButton: React.ReactNode;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const { blocks } = value;
  const { org } = useCurrentUser();

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId),
    [blocks, selectedBlockId]
  );

  const deselectBlock = useCallback(() => setSelectedBlockId(null), [
    setSelectedBlockId
  ]);

  const expTplMini = useTaskBuilderContext(expTpl.data);
  const connectedTasks = useMemo(
    () => getTaskTemplatesWithSameActorAndWithinRange(value, expTpl),
    [expTpl, value]
  );

  // DragDropContext required because some inner components rely on it
  // It's all disabled though
  return (
    <DragDropContext onDragEnd={NOOP}>
      <Grid>
        <MessageSettingsOverview
          value={value}
          expTpl={expTplMini}
          tokens={tokens}
          customUserFields={org.customUserFields}
        />
        <PreviewContainer>
          <PreviewHeading>
            <Typography variant="h6">Preview</Typography>
            {sendTestMessageButton}
          </PreviewHeading>
          <PreviewBody>
            <MainContainer>
              <MessagePreviewContainer onClick={deselectBlock}>
                <ExperienceMessagePreview
                  msg={value}
                  org={org}
                  expTpl={expTplMini}
                  selectedBlock={selectedBlockId}
                  onSelectBlock={setSelectedBlockId}
                  validation={validation}
                  draggable={false}
                  viewMode={MESSAGE_BUILDER_VIEW_MODES.settingsOverview}
                  connectedTasks={connectedTasks}
                  tokens={tokens}
                />
              </MessagePreviewContainer>
            </MainContainer>
          </PreviewBody>
        </PreviewContainer>
        {selectedBlock ? (
          <MessageBlockSettingsReadOnly
            b={selectedBlock}
            validation={validation[selectedBlock.id] || null}
            onBack={deselectBlock}
          />
        ) : (
          <SideSettingsReadOnlyNoSelection>
            {t('Select a block to view details')}
          </SideSettingsReadOnlyNoSelection>
        )}
      </Grid>
    </DragDropContext>
  );
};
